import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { apiFetch } from "../lib/apiFetch";
import {
  clearOperatorToken,
  decodeJwtPayload,
  getMetricsBypassKeyFromLocation,
  getMetricsCodeFromLocation,
  getMetricsTokenFromLocation,
  getOperatorToken,
  MetricsTokenSource,
  normalizeTokenInput,
  jwtNumericDateToIso,
  setOperatorToken,
} from "../lib/auth";
import { withVetcanApiBase } from "../lib/apiBase";

type MetricsResponse = {
  uptimeSeconds: number;
  environment: string;
  lastUpdated: string;
  sanitized?: boolean;
  isClientViewer?: boolean;
  note?: string;
  callbackRequests?: Array<Record<string, unknown>>;
  callbackFollowUps?: Array<Record<string, unknown>>;
  auditTimeline?: Array<Record<string, unknown>>;
  recentAlertActivity?: Array<Record<string, unknown>>;
  eventCounts: Record<string, number>;
  ackTimeline: Array<{
    eventName: string;
    createdAt: string;
    payload: Record<string, any>;
  }>;
  sla: {
    callbacks: {
      averageSeconds: number;
      breachCount: number;
      buckets: { le2m: number; le5m: number; le10m: number; gt10m: number };
    };
    alerts: {
      acknowledge: {
        averageSeconds: number;
        breachCount: number;
        buckets: { le2m: number; le5m: number; le10m: number; gt10m: number };
      };
      resolve: {
        averageSeconds: number;
        breachCount: number;
        buckets: { le2m: number; le5m: number; le10m: number; gt10m: number };
      };
      fallbackUsage: { total: number; withFallback: number };
    };
  };
  activeAlerts: Array<{
    id?: string;
    alertType: string;
    eventName: string;
    count: number;
    threshold: number;
    windowSeconds: number;
    triggeredAt: string;
    environment: string;
    acknowledgedAt?: string;
    acknowledgedBy?: string;
  }>;
  alertOverview?: {
    totalActive: number;
    unacknowledgedActive: number;
    activeBySeverity: { info: number; warning: number; critical: number };
    resolvedToday: number;
    resolvedWindow: number;
    resolutionCounts: { automation: number; operator: number };
    slaCounts: { breached: number; within: number };
  };
};

type StatusResponse = {
  uptimeSeconds: number;
  environment: string;
  alertEngineInitialized: boolean;
  activeAlertCount: number;
  eventForwarderEnabled: boolean;
  sanitized?: boolean;
  isClientViewer?: boolean;
  note?: string;
  voiceEnabled?: boolean;
  voiceActivationSource?: string;
};

type IntegrationsStatusResponse = {
  n8nAlertConfigured: boolean;
  bookingConfigured: boolean;
  metricsConfigured: boolean;
  checkedAt?: string;
};

type PlatformExternalVisibilityItem = {
  key?: "crm_status_mirror" | "calendar_advisory_availability" | "staff_roster_snapshot" | string;
  label?: string;
  state?: "read_only_ready" | "scaffold_only" | string;
  tenantScopedSourceName?: string;
  sourceSystem?: string | null;
  sourceDefined?: boolean;
  advisoryOnly?: boolean;
  writeBackEnabled?: boolean;
  lastObservedAt?: string | null;
  freshnessWindowMinutes?: number | null;
  connectorManifest?: {
    key?: string;
    sourceName?: string;
    state?: "named_contract" | "scaffold_only" | string;
    mode?: "read_only_advisory" | string;
    ownerApprovalRequired?: boolean;
    ownerApprovalNote?: string;
    freshnessWindowMinutes?: number | null;
  } | null;
  calendarAdvisory?: {
    bookingAuthority?: string;
    callbackTimingCue?: string;
    staleStateNote?: string;
    timezone?: string | null;
    routingSource?: string | null;
  } | null;
  rosterAdvisory?: {
    escalationRoutingMode?: string;
    roleVisibility?: string[];
    channelVisibility?: string[];
    fallbackNote?: string;
  } | null;
  summary?: string;
  detail?: string;
  proofRoute?: string | null;
};

type PlatformExternalVisibilityResponse = {
  checkedAt?: string;
  currentWorkflowIntegrationsRemainSeparate?: boolean;
  summary?: {
    readOnlyReadyCount?: number;
    scaffoldOnlyCount?: number;
  };
  items?: PlatformExternalVisibilityItem[];
  sanitized?: boolean;
  isClientViewer?: boolean;
  note?: string;
};

type SalesOfferClarityOffer = {
  key?: string;
  displayName?: string;
  audience?: "new_patient" | "renewal" | "veteran" | "special" | string;
  publicPriceLabel?: string;
  publicTotalLabel?: string;
  paymentMode?: "full" | "deposit" | "dynamic" | string;
  bookingChannel?: "public_scheduler" | "staff_only" | "pending" | string;
  appointmentTypeId?: string | null;
  hydrationStatus?: "hydrated" | "pending_owner_confirmation" | "blocked" | string;
};

type SalesOfferClarityResponse = {
  checkedAt?: string;
  catalogTenantKey?: string;
  catalogClinicLabel?: string;
  status?: "visible" | "watch" | string;
  manifest?: {
    sourceDoc?: string;
    sourceJson?: string;
    ownerConfirmedUpdatedAt?: string;
    typeIdsVerifiedAt?: string;
    hydrationVersion?: string;
  };
  summary?: {
    activePublicOfferCount?: number;
    publicSchedulerCount?: number;
    hydratedOfferCount?: number;
    byAudience?: {
      new_patient?: number;
      renewal?: number;
      veteran?: number;
      special?: number;
    };
    byPaymentMode?: {
      full?: number;
      deposit?: number;
      dynamic?: number;
    };
  };
  adState?: {
    imageArtifactPath?: string;
    imageArtifactPresent?: boolean;
    imageArtifactEmpty?: boolean;
    manifestPath?: string;
    structuredStatePackagingReady?: boolean;
    activePrivateEntryCount?: number;
    privateEntryCount?: number;
    status?: string;
    note?: string;
  };
  offers?: SalesOfferClarityOffer[];
  operatorAction?: string;
  sanitized?: boolean;
  isClientViewer?: boolean;
  note?: string;
};

type LaunchControlNextAction = {
  category?: string;
  action?: string;
};

type LaunchControlReadinessResponse = {
  checkedAt?: string;
  overallReady?: boolean;
  blockerCount?: number;
  blockerCategories?: string[];
  nextActions?: LaunchControlNextAction[];
};

type Week1StabilizationProbe = {
  key?: string;
  cadence?: string;
  route?: string;
};

type Week1StabilizationLifecycleTypeCount = {
  lifecycleType?: string;
  count?: number;
};

type Week1StabilizationResponse = {
  checkedAt?: string;
  topProbes?: Week1StabilizationProbe[];
  unresolvedBlockers?: {
    count?: number;
    categories?: string[];
  };
  bookingExceptionPressure?: {
    awaitingConfirmationCount?: number;
    needsStaffCount?: number;
    total?: number;
    level?: string;
  };
  lifecyclePending?: {
    count?: number;
    level?: string;
    automationEnabled?: boolean;
    sampleLimit?: number;
    sampleTruncated?: boolean;
  };
  paymentFollowupExceptions?: {
    count?: number;
    staleCount?: number;
    level?: string;
  };
  liveOptimization?: {
    lifecycleUnderperformance?: {
      pendingCount?: number;
      overdueCount?: number;
      overdueRate?: number;
      level?: string;
      topAtRiskTypes?: Week1StabilizationLifecycleTypeCount[];
    };
  };
  actionCues?: string[];
};


type RevenueRecoveryResponse = {
  checkedAt?: string;
  snapshotDays?: number;
  abandonedBooking?: {
    status?: "visible" | "watch" | string;
    lifecycleRule?: string;
    sourceRoute?: string;
    overdueCount?: number;
    awaitingConfirmationCount?: number;
    needsStaffCount?: number;
    dedicatedReadModel?: boolean;
    operatorAction?: string;
  };
  reactivation?: {
    status?: "visible" | "watch" | string;
    cohortDefinition?: {
      dormantDays?: number;
      recentActivityDays?: number;
      ruleFamily?: string;
      channel?: string;
      ownerSafeOnly?: boolean;
      excludes?: string[];
    };
    cohortSummary?: {
      eligibleCount?: number;
      excludedOpenQueueCount?: number;
      excludedRecentActivityCount?: number;
      excludedRenewalCount?: number;
      excludedUnpaidCount?: number;
      excludedAbandonedCount?: number;
      samplePhoneLast4?: string[];
    };
    proof?: {
      dormantCustomerCohortReady?: boolean;
      separateRuleFamilyReady?: boolean;
      ownerApprovedCopyReady?: boolean;
      outcomeProofReady?: boolean;
    };
    operatorAction?: string;
  };
  sanitized?: boolean;
  isClientViewer?: boolean;
  note?: string;
};

type ModuleMatrixModuleEntry = {
  key?: string;
  label?: string;
  currentState?: string;
  defaultState?: string;
  requiresCredentials?: boolean;
  requiresOwnerApproval?: boolean;
  clientVisibleValue?: string;
  nextUpgradeAction?: string;
  blockedReasons?: string[];
  links?: {
    route?: string | null;
    doc?: string | null;
  };
};

type ModuleMatrixResponse = {
  checkedAt?: string;
  version?: string;
  tenantTemplateVersion?: string;
  summary?: {
    totalModules?: number;
    blockedCount?: number;
    pendingOwnerApprovalCount?: number;
    liveReadyCount?: number;
    nextUpgradeActions?: string[];
  };
  modules?: ModuleMatrixModuleEntry[];
};

type PlaybookActiveSummaryResponse = {
  checkedAt?: string;
  version?: string;
  approvedBy?: string;
  updatedAt?: string;
  blockCount?: number;
  intentCoverage?: string[];
  blockKeys?: string[];
  channels?: string[];
  prohibitedTriggerCount?: number;
  ownerOverrideEligibleCount?: number;
  ownerOverrideEligibleKeys?: string[];
};

type PaymentExceptionItem = {
  correlationId?: string;
  category?: string;
  reason?: string;
  ageMinutes?: number;
  stale?: boolean;
  state?: string;
  stage?: string;
  channel?: string;
  bookingProvider?: string;
  paymentProvider?: string;
  paymentRequired?: boolean;
  paymentLinkAvailable?: boolean;
  phoneLast4?: string;
  errorCode?: string | null;
  errorMessage?: string | null;
  eventCount?: number;
};

type PaymentExceptionsResponse = {
  limit?: number;
  snapshotDays?: number;
  category?: string | null;
  includeResolved?: boolean;
  thresholds?: {
    captureStaleMinutes?: number;
    unpaidAgingMinutes?: number;
    bookingWindowMinutes?: number;
  };
  items?: PaymentExceptionItem[];
};

type PaymentFollowupProofItem = {
  correlationId?: string;
  channel?: string;
  state?: string;
  paymentProvider?: string;
  paymentRequired?: boolean;
  paymentLinkAvailable?: boolean;
  paymentLinkDomain?: string | null;
  notifyOk?: boolean;
  notifyMode?: string;
  notifyDeduped?: boolean;
  smsOk?: boolean;
  smsSkipped?: boolean;
  phoneLast4?: string;
  errorCode?: string | null;
  errorMessage?: string | null;
};

type PaymentFollowupOpsResponse = {
  limit?: number;
  state?: string | null;
  channel?: string | null;
  items?: PaymentFollowupProofItem[];
};

type PaymentCaptureOutcomeItem = {
  correlationId?: string;
  channel?: string;
  stage?: string;
  state?: string;
  paymentProvider?: string;
  fallbackMode?: string | null;
  fallbackUsed?: boolean;
  notifyOk?: boolean;
  notifyMode?: string;
  notifyDeduped?: boolean;
  smsOk?: boolean;
  smsSkipped?: boolean;
  paymentReferenceId?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
};

type PaymentCaptureOutcomesResponse = {
  limit?: number;
  state?: string | null;
  channel?: string | null;
  stage?: string | null;
  items?: PaymentCaptureOutcomeItem[];
};

type PlaybookPreviewResponse = {
  matched: boolean;
  key?: string;
  intent?: string;
  reply?: string;
  voiceLines?: string[];
  escalateToStaff?: boolean;
  prohibited?: boolean;
  source?: string;
  version?: string;
};

type ClientMetricsValidateResponse = {
  ok?: boolean;
  reason?: string;
  issuedAt?: string;
  expiresAt?: string;
  scope?: string | string[];
  typ?: string;
};

type ClientMetricsExchangeResponse = {
  token?: string;
  issuedAt?: string;
  expiresAt?: string;
  reason?: string;
  error?: string;
};

type RecentEvent = {
  type: string;
  payload: Record<string, any>;
  createdAt: string;
};

type OperatorAuditEntry = {
  action: string;
  entityType: "callback" | "alert";
  entityId: string;
  operatorId?: string;
  operatorName?: string;
  role?: string;
  createdAt: string;
  environment: string;
};

type UnifiedAuditRow = {
  id: string;
  source: "operator" | "ai";
  action: string;
  actor: string;
  role: string;
  entityType: string;
  entityId: string;
  createdAt: string;
  handledAt: string | null;
  environment: string;
  detail: string | null;
};

type FilterMode = "all" | "pending" | "ai" | "staff";

type Callback = {
  id: string;
  name: string;
  phone: string;
  status: string;
  source?: string;
  createdAt: string;
  aiHandled?: boolean;
  aiOutcome?: string | null;
  staffFollowupRequired?: boolean;
  summary?: string | null;
  handledAt?: string | null;
  handledBy?: string | null;
};

type CallbackRequestQueueItem = {
  id?: string;
  createdAt: string;
  status?: string;
  name?: string;
  phone?: string;
  bestTime?: string | null;
  source?: string;
  requestType?: string | null;
  aiHandled?: boolean;
  aiOutcome?: string | null;
  staffFollowupRequired?: boolean;
  summary?: string | null;
  handledAt?: string | null;
  handledBy?: string | null;
  nonMedicalReason?: string | null;
  lastAttemptAt?: string | null;
  outboundAttemptCount?: number;
  outboundLastCallSid?: string | null;
};

type ActiveAlert = {
  id: string;
  alertType: string;
  eventName: string;
  count: number;
  threshold: number;
  firstTriggeredAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  externalIncident?: {
    sourceSystem?: string | null;
    referenceId?: string | null;
    url?: string | null;
    attachedAt?: string | null;
    attachedBy?: string | null;
    provenance?: string | null;
    sourceEventName?: string | null;
  } | null;
  delivery?: {
    status?: "none" | "pending" | "delivered" | "failed" | "canceled" | string;
    attemptCount?: number;
    lastAttemptAt?: string | null;
    lastError?: string | null;
    updatedAt?: string | null;
  };
};

type VoiceOpsMilestones = {
  call_started?: boolean;
  stage_gathered?: boolean;
  staff_handoff_attempted?: boolean;
  staff_handoff_disabled?: boolean;
  transfer_to_staff?: boolean;
  ai_fallback?: boolean;
  call_ended?: boolean;
};

type VoiceOpsCall = {
  callSid?: string;
  correlationId?: string;
  startedAt?: string;
  lastEventAt?: string;
  status?: "active" | "ai_handled" | "handed_off" | "failed" | "unknown" | string;
  milestones?: VoiceOpsMilestones;
};

type VoiceTimelineEvent = {
  at?: string;
  eventName?: string;
  severity?: string | null;
  summary?: string | null;
  meta?: Record<string, unknown> | null;
};

type EscalationBreakerSnapshot = {
  state?: string;
  failureCount?: number;
  openedAtMs?: number | null;
  openUntilMs?: number | null;
  remainingOpenMs?: number | null;
};

type EscalationCounters = {
  attempted?: number;
  delivered?: number;
  failed?: number;
  skippedBreaker?: number;
  skippedBackoff?: number;
  skippedNonePending?: number;
};

type EscalationsStatusResponse = {
  breaker?: EscalationBreakerSnapshot;
  backoff?: {
    state?: "ACTIVE" | "NONE" | string;
    untilMs?: number | null;
    remainingMs?: number | null;
    reason?: string | null;
  };
  config?: {
    retry?: {
      baseMs?: number;
      maxMs?: number;
      jitterMs?: number;
    };
    breaker?: {
      failureThreshold?: number;
      openMs?: number;
    };
  };
  counters?: EscalationCounters;
  backlog?: {
    pendingCount?: number;
    failedCount?: number;
    oldestPendingAgeSeconds?: number | null;
    oldestPendingAt?: string | null;
    oldestPendingCorrelationId?: string | null;
  };
  nowMs?: number;
};

type EscalationDeliveryAttempt = {
  deliveryId: string;
  status: string;
  attemptCount: number;
  lastError: string | null;
  activityAt: string;
  dedupeKey: string;
  eventName: string;
  correlationId: string | null;
  environment: string;
};

type EscalationCorrelationDelivery = {
  deliveryId?: string;
  status?: string;
  attemptCount?: number;
  lastError?: string | null;
  activityAt?: string;
  dedupeKey?: string;
  event?: {
    eventName?: string;
    environment?: string;
    correlationId?: string | null;
  } | null;
};

type EscalationCorrelationEvent = {
  eventName?: string;
  environment?: string;
  createdAt?: string;
};

type EscalationCorrelationDrilldownResponse = {
  nowMs?: number;
  correlationId?: string;
  breaker?: EscalationBreakerSnapshot;
  backoff?: {
    state?: "ACTIVE" | "NONE" | string;
    untilMs?: number | null;
    remainingMs?: number | null;
    reason?: string | null;
  };
  config?: EscalationsStatusResponse["config"];
  externalIncident?: {
    sourceSystem?: string | null;
    referenceId?: string | null;
    url?: string | null;
    attachedAt?: string | null;
    attachedBy?: string | null;
    provenance?: string | null;
    sourceEventName?: string | null;
  } | null;
  deliveries?: EscalationCorrelationDelivery[];
  events?: EscalationCorrelationEvent[];
};

type EscalationEligibilityProbeResponse = {
  nowMs?: number;
  breaker?: EscalationBreakerSnapshot;
  backoff?: {
    state?: "ACTIVE" | "NONE" | string;
    untilMs?: number | null;
    remainingMs?: number | null;
    reason?: string | null;
  };
  eligibility?: {
    pendingTotal?: number;
    pendingEligibleNow?: number;
    pendingInBackoff?: number;
    nextEligibleAt?: number | null;
    nextEligibleInMs?: number | null;
    sampleEligibleCorrelationIds?: string[];
    sampleInBackoffCorrelationIds?: string[];
  };
};

type OutboundSchedulingMetricsResponse = {
  snapshotDays?: number;
  scheduling?: {
    linksSent?: number;
    bookingsConfirmed?: number;
    deflectionRate?: number;
    staffSaves?: {
      count?: number;
      estimatedMinutes?: number;
    };
    timeToResolutionSeconds?: {
      avg?: number;
      p50?: number;
      p90?: number;
    };
  };
  voiceBookingRequests?: {
    total?: number;
    afterHoursCount?: number;
    afterHoursPercent?: number;
    byOutcome?: {
      pending?: number;
      confirmed?: number;
      cancelled?: number;
    };
  };
};

type SchedulingExceptionItem = {
  id: string;
  createdAt: string;
  status: string;
  summary: string;
  phoneLast4: string;
  source: string;
  ageMinutes?: number;
  ageBucket?: "0_15" | "15_60" | "60_240" | "240_plus" | string;
};

type SchedulingSweepRun = {
  createdAt?: string;
  correlationId?: string | null;
  action?: string | null;
  idempotent?: boolean;
  counters?: {
    attempted?: number;
    sent?: number;
    failed?: number;
    skippedCooldown?: number;
    updated?: number;
    skipped?: number;
  };
};

type OutboundVoiceSweepRun = {
  createdAt?: string;
  correlationId?: string | null;
  counters?: {
    attempted?: number;
    succeeded?: number;
    failed?: number;
    skippedBackoff?: number;
    skippedMax?: number;
    skippedAlreadyReached?: number;
    skipped?: number;
  };
  paging?: {
    hasMore?: boolean;
    cursorNext?: string | null;
    timeBudgetReached?: boolean;
  };
  perf?: {
    durationMs?: number;
    limit?: number;
    timeBudgetMs?: number;
    attemptTimeoutMs?: number;
  };
};

type OutboundVoiceSweepHealth = {
  status?: "ok" | "warn" | "error" | string;
  reason?: string;
};

const CLINIC_PROFILE_FIELDS = [
  { key: "displayName", label: "Clinic display name" },
  { key: "addressText", label: "Address text" },
  { key: "hoursText", label: "Hours text" },
  { key: "parkingText", label: "Parking text" },
  { key: "renewalsPhone", label: "Renewals phone" },
  { key: "supportPhone", label: "Support phone" },
] as const;

type ClinicProfileField = (typeof CLINIC_PROFILE_FIELDS)[number]["key"];

type ClinicProfileFormValues = Record<ClinicProfileField, string>;

type ClinicProfilePayload = ClinicProfileFormValues & {
  tenantKey: string;
  noMedicalAdviceNotice?: string;
};

const EMPTY_CLINIC_PROFILE_FORM: ClinicProfileFormValues = {
  displayName: "",
  addressText: "",
  hoursText: "",
  parkingText: "",
  renewalsPhone: "",
  supportPhone: "",
};

const tableHeaderStyle = {
  padding: "10px",
  borderBottom: "1px solid #d9d2c7",
};

const tableCellStyle = {
  padding: "12px 10px",
  borderBottom: "1px solid #efe7dd",
};

const callbackQueueHeaderStyle = {
  ...tableHeaderStyle,
  whiteSpace: "nowrap" as const,
};

const callbackQueueHintStyle = {
  margin: "6px 0 10px",
  color: "#5b544e",
  fontSize: "13px",
};

const callbackActionGroupStyle = {
  display: "flex",
  flexWrap: "wrap" as const,
  alignItems: "center",
  gap: "6px",
  maxWidth: "320px",
};

const callbackActionButtonStyle = {
  padding: "5px 10px",
  borderRadius: "8px",
  border: "1px solid #c7beb4",
  background: "#ffffff",
  color: "#2f2924",
  cursor: "pointer",
  fontSize: "12px",
  lineHeight: 1.2,
  whiteSpace: "nowrap" as const,
};

const callbackCallNowButtonStyle = {
  ...callbackActionButtonStyle,
  border: "1px solid #1f4f7a",
  background: "#1f4f7a",
  color: "#ffffff",
};

const callbackCompleteButtonStyle = {
  ...callbackActionButtonStyle,
  border: "1px solid #2c5d43",
  background: "#2c5d43",
  color: "#ffffff",
};

const callbackActionFeedbackStyle = {
  fontSize: "12px",
  color: "#5b544e",
  flexBasis: "100%",
  wordBreak: "break-word" as const,
};

const POLL_INTERVAL_MS = 30_000;
const RATE_LIMIT_BACKOFF_MS = 60_000;
const INTERNAL_METRICS_MESSAGE = "Access token required to view metrics.";
const CLIENT_VIEWER_MESSAGE = "Client metrics view (sanitized).";
const TOKEN_RATE_LIMIT_MESSAGE = "Rate limited, try again";
const TOKEN_GENERIC_ERROR_MESSAGE = "Unable to validate token. Try again.";
const COPY_CONFIRMATION_TIMEOUT_MS = 1_500;
const SCHEDULING_EXCEPTIONS_SNAPSHOT_DAYS = 7;
const SCHEDULING_BULK_ACTION_CAP = 50;
const TOKEN_SOURCE_CODE_MESSAGE = "Unlocked via code (recommended)";
const TOKEN_SOURCE_LINK_MESSAGE = "Token loaded from link";
const TOKEN_SOURCE_STORAGE_MESSAGE = "Token loaded from browser storage";
const BYPASS_SOURCE_LINK_MESSAGE = "Unlocked via bypass link (break-glass)";
const BYPASS_CLIENT_VIEW_LABEL = "Client view (bypass link) · Sanitized";
const EXCHANGE_CODE_GENERIC_ERROR_MESSAGE =
  "Code exchange failed. Request a new link.";
const EXCHANGE_CODE_REASON_MESSAGES: Record<string, string> = {
  expired_code: "Code expired. Request a new link.",
  invalid_code: "Code invalid. Request a new link.",
  malformed_code: "Code invalid. Request a new link.",
};

const TOKEN_REASON_MESSAGES: Record<string, string> = {
  invalid_signature: "Token signature invalid.",
  expired: "Token expired.",
  iat_in_future: "Token not valid yet (clock skew).",
  wrong_scope: "Token missing metrics:read scope.",
  wrong_typ: "Token type is invalid for metrics access.",
  malformed_token: "Token format is invalid.",
  missing_token: "Token is missing.",
  invalid_temporal: "Token time window is invalid.",
  invalid_token: "Token is invalid.",
  missing_secret: "Token verification unavailable. Contact support.",
};

type SchedulingExceptionsBucketFilter =
  | "all"
  | "0_15"
  | "15_60"
  | "60_240"
  | "240_plus";

const SCHEDULING_EXCEPTION_BUCKET_OPTIONS: Array<{
  value: SchedulingExceptionsBucketFilter;
  label: string;
}> = [
  { value: "all", label: "All ages" },
  { value: "0_15", label: "0-15 min" },
  { value: "15_60", label: "15-60 min" },
  { value: "60_240", label: "60-240 min" },
  { value: "240_plus", label: "240+ min" },
];

const isClinicProfilePayload = (value: unknown): value is ClinicProfilePayload => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.tenantKey !== "string" || !record.tenantKey.trim()) {
    return false;
  }
  return CLINIC_PROFILE_FIELDS.every(
    (field) => typeof record[field.key] === "string"
  );
};

const toClinicProfileFormValues = (
  payload: ClinicProfilePayload
): ClinicProfileFormValues => ({
  displayName: payload.displayName,
  addressText: payload.addressText,
  hoursText: payload.hoursText,
  parkingText: payload.parkingText,
  renewalsPhone: payload.renewalsPhone,
  supportPhone: payload.supportPhone,
});

const mapTokenReasonToMessage = (reason: unknown) => {
  if (typeof reason !== "string" || !reason.trim()) return null;
  return TOKEN_REASON_MESSAGES[reason] || null;
};

const fallbackString = (value: unknown, fallback: string) => {
  if (typeof value === "string" && value.trim()) return value;
  return fallback;
};

const formatTimestamp = (value: string | null | undefined, fallback: string) => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString();
};

const safeNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const formatPercentOneDecimal = (value: unknown) =>
  `${(safeNumber(value) * 100).toFixed(1)}%`;

const formatDurationLabel = (value: unknown) => {
  const totalSeconds = Math.max(0, Math.round(safeNumber(value)));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds}s`;
  if (seconds <= 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

const getEpochMs = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const truncateText = (value: string, max = 180) =>
  value.length > max ? `${value.slice(0, max - 1)}...` : value;

const formatCallbackQueueStatus = (status: string | null | undefined) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "pending";
  return normalized.replace(/_/g, " ");
};

const formatPhoneLast4 = (value: string | null | undefined) => {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 4) return "—";
  return `***-${digits.slice(-4)}`;
};

const formatSchedulingAgeBucketLabel = (
  bucket: SchedulingExceptionsBucketFilter | string | null | undefined
) => {
  const normalized = String(bucket || "").trim() as SchedulingExceptionsBucketFilter;
  const matched = SCHEDULING_EXCEPTION_BUCKET_OPTIONS.find(
    (option) => option.value === normalized
  );
  return matched?.label || "—";
};

const getMinAgeMinutesFromBucket = (
  bucket: SchedulingExceptionsBucketFilter
): number | null => {
  if (bucket === "15_60") return 15;
  if (bucket === "60_240") return 60;
  if (bucket === "240_plus") return 240;
  if (bucket === "0_15") return 0;
  return null;
};

const normalizeCallbackQueueStatus = (status: string | null | undefined) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return normalized || "pending";
};

type QueueStatusUpdateTarget = "acknowledged" | "in_progress" | "resolved";

type CallbackQueueAction = {
  key: string;
  label: string;
  status: QueueStatusUpdateTarget;
  successMessage: string;
};

const CALLBACK_QUEUE_ACTIONS: CallbackQueueAction[] = [
  {
    key: "acknowledge",
    label: "Acknowledge",
    status: "acknowledged",
    successMessage: "Callback request acknowledged.",
  },
  {
    key: "in_progress",
    label: "In progress",
    status: "in_progress",
    successMessage: "Callback request marked in progress.",
  },
  {
    key: "needs_staff",
    label: "Needs staff",
    status: "in_progress",
    successMessage: "Callback request flagged for staff follow-up.",
  },
  {
    key: "resolved",
    label: "Resolved",
    status: "resolved",
    successMessage: "Callback request resolved.",
  },
  {
    key: "canceled",
    label: "Canceled",
    status: "resolved",
    successMessage: "Callback request marked canceled.",
  },
];

const getQueueStatusActions = (
  status: string | null | undefined,
  requestType: string | null | undefined
) => {
  const normalized = normalizeCallbackQueueStatus(status);
  const canAcknowledge = normalized === "pending" || normalized === "needs_staff";
  const canProgress =
    normalized === "pending" ||
    normalized === "needs_staff" ||
    normalized === "acknowledged";
  const canResolve =
    normalized === "pending" ||
    normalized === "needs_staff" ||
    normalized === "acknowledged" ||
    normalized === "in_progress";

  const actions = CALLBACK_QUEUE_ACTIONS.filter((action) => {
    if (action.status === "acknowledged") return canAcknowledge;
    if (action.status === "in_progress") return canProgress;
    return canResolve;
  });

  const normalizedRequestType = String(requestType || "")
    .trim()
    .toLowerCase();
  if (normalizedRequestType === "scheduling" && canProgress) {
    actions.push({
      key: "confirmed",
      label: "Confirmed",
      status: "in_progress",
      successMessage: "Requested window confirmed; staff follow-up required.",
    });
  }

  return actions;
};

const VOICE_MILESTONE_LABELS: Array<{
  key: keyof VoiceOpsMilestones;
  label: string;
}> = [
  { key: "call_started", label: "Started" },
  { key: "stage_gathered", label: "Gathered" },
  { key: "staff_handoff_attempted", label: "Handoff Attempted" },
  { key: "staff_handoff_disabled", label: "Handoff Disabled" },
  { key: "transfer_to_staff", label: "Transferred" },
  { key: "ai_fallback", label: "AI Fallback" },
  { key: "call_ended", label: "Ended" },
];

const BYPASS_INTERNAL_READ_PREFIXES = [
  "/api/internal/metrics",
  "/api/internal/status",
  "/api/internal/integrations",
  "/api/internal/readiness/launch-control",
  "/api/internal/readiness/module-matrix",
  "/api/internal/ops/week1-stabilization",
  "/api/internal/playbooks/active",
  "/api/internal/events/recent",
  "/api/internal/operator-audit",
  "/api/internal/callback-requests",
  "/api/internal/alerts/active",
  "/api/internal/voice/recent",
  "/api/internal/voice/metrics/outbound",
  "/api/internal/escalations/status",
  "/api/internal/escalations/deliveries/recent",
  "/api/internal/escalations/eligibility/probe",
];

const formatVoiceStatus = (status: string | null | undefined) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "unknown";
  return normalized.replace(/_/g, " ");
};

const formatLifecycleLaneLabel = (value: string | null | undefined) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "Unknown";
  if (normalized === "booking_confirmed_reminder") return "Booking confirmed reminder";
  if (normalized === "same_day_reminder") return "Same-day reminder";
  if (normalized === "no_show_followup") return "No-show follow-up";
  return normalized.replace(/_/g, " ");
};

const REMINDER_ATTENDANCE_LIFECYCLE_TYPES = new Set([
  "booking_confirmed_reminder",
  "same_day_reminder",
  "no_show_followup",
]);

const formatReadinessStateLabel = (value: string | null | undefined) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "unknown";
  return normalized.replace(/_/g, " ");
};

const formatPaymentExceptionCategoryLabel = (value: string | null | undefined) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "Unknown";
  if (normalized === "unpaid_followup_required") return "Unpaid follow-up required";
  if (normalized === "capture_failed") return "Capture failed";
  if (normalized === "capture_started") return "Capture started";
  if (normalized === "pending_link") return "Pending link";
  if (normalized === "link_sent") return "Link sent";
  if (normalized === "staff_required") return "Staff required";
  if (normalized === "manually_resolved") return "Manually resolved";
  return normalized.replace(/_/g, " ");
};

const formatRoleLabel = (value: string | null | undefined) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "unknown";
  if (normalized === "client_viewer") return "client viewer";
  return normalized.replace(/_/g, " ");
};

function downloadTextFile(filename: string, content: string, mimeType: string) {
  if (typeof document === "undefined") return;

  const blob = new Blob([content], { type: mimeType });
  const objectUrl =
    typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
      ? URL.createObjectURL(blob)
      : null;
  const href =
    objectUrl ||
    `data:${mimeType};charset=utf-8,${encodeURIComponent(content)}`;
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();

  if (objectUrl && typeof URL.revokeObjectURL === "function") {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

function buildDashboardReportingMarkdown(snapshot: Record<string, any>) {
  const lines = [
    "# VetCan Owner/Operator Reporting Snapshot",
    "",
    `- Generated: ${snapshot.generatedAt || "n/a"}`,
    `- Environment: ${snapshot.environment || "unknown"}`,
    `- Build: ${snapshot.build || "dev"}`,
    `- Access role: ${snapshot.access?.role || "unknown"}`,
    "",
    "## A27 Revenue Lanes",
    `- Reminder and attendance: ${snapshot.reminderAttendance?.status || "n/a"} / pending ${snapshot.reminderAttendance?.sharedPendingCount ?? 0} / overdue ${snapshot.reminderAttendance?.overdueCount ?? 0}`,
    `- Payment recovery: ${snapshot.paymentRecovery?.status || "n/a"} / open ${snapshot.paymentRecovery?.openCount ?? 0} / stale ${snapshot.paymentRecovery?.staleCount ?? 0} / staff-required ${snapshot.paymentRecovery?.staffRequiredCount ?? 0}`,
    `- Renewal and retention: ${snapshot.renewalRetention?.status || "n/a"} / overdue ${snapshot.renewalRetention?.overdueCount ?? 0}`,
    `- Abandoned booking recovery: ${snapshot.abandonedBookingRecovery?.status || "n/a"} / overdue ${snapshot.abandonedBookingRecovery?.overdueCount ?? 0}`,
    `- Playbook governance: ${snapshot.playbookGovernance?.status || "n/a"} / owner pending ${snapshot.playbookGovernance?.ownerPendingCount ?? 0}`,
    "",
    "## A29 Commercial Surface",
    `- Sales and ad-state: ${snapshot.salesAdState?.status || "n/a"} / active public offers ${snapshot.salesAdState?.activePublicOfferCount ?? 0} / hydrated ${snapshot.salesAdState?.hydratedOfferCount ?? 0} / ad artifact ${snapshot.salesAdState?.adArtifactPresent ? snapshot.salesAdState?.adArtifactEmpty ? "present but empty" : "present" : "missing"}`,
    `- Sales source: ${snapshot.salesAdState?.catalogClinicLabel || "n/a"} / structured ad-state packaging ${snapshot.salesAdState?.structuredStatePackagingReady ? "ready" : "pending"}`,
    `- Scheduling integrity: ${snapshot.schedulingIntegrity?.status || "n/a"} / public scheduler ${snapshot.schedulingIntegrity?.publicSchedulerCount ?? 0} / hydrated ${snapshot.schedulingIntegrity?.hydratedOfferCount ?? 0} / authority ${snapshot.schedulingIntegrity?.bookingAuthority || "n/a"} / awaiting confirmation ${snapshot.schedulingIntegrity?.awaitingConfirmationCount ?? 0}`,
    `- Payment operability: ${snapshot.paymentOperability?.status || "n/a"} / followups ${snapshot.paymentOperability?.followupObservedCount ?? 0} / link available ${snapshot.paymentOperability?.followupLinkAvailableCount ?? 0} / capture outcomes ${snapshot.paymentOperability?.captureOutcomeCount ?? 0} / capture succeeded ${snapshot.paymentOperability?.captureSuccessCount ?? 0}`,
    "",
    "## A28 Enterprise Packaging",
    `- Governance lane: ${snapshot.governance?.status || "n/a"} / admin-only controls ${snapshot.governance?.adminOnlyControlCount ?? 0}`,
    `- Enterprise readiness: ${snapshot.enterpriseReadiness?.status || "n/a"} / platform ready ${snapshot.enterpriseReadiness?.platformReadyCount ?? 0} / scaffold-only ${snapshot.enterpriseReadiness?.platformScaffoldOnlyCount ?? 0}`,
    `- Voice posture: ${snapshot.enterpriseReadiness?.voicePosture || "n/a"}`,
    "",
    "## Boundaries",
    "- Ops-safe only.",
    "- No PHI, transcript, or raw payment data exported.",
    "- Preview-only voice quality proof remains separate from live routing authority.",
  ];

  return lines.join("\n");
}

const buildUnifiedAuditRows = ({
  operatorEntries,
  callbacks,
  environment,
}: {
  operatorEntries: OperatorAuditEntry[];
  callbacks: Callback[];
  environment: string;
}): UnifiedAuditRow[] => {
  const operatorRows = operatorEntries.map((entry, idx) => {
    const action = fallbackString(entry?.action, "Operator action");
    const actor = fallbackString(
      entry?.operatorName ?? entry?.operatorId,
      "Operator"
    );
    const role = fallbackString(entry?.role, "operator");
    const entityType = fallbackString(entry?.entityType, "unknown");
    const entityId = fallbackString(entry?.entityId, "unknown");
    const createdAt = fallbackString(entry?.createdAt, "");
    const env = fallbackString(entry?.environment, environment);

    return {
      id: `operator-${entityId}-${createdAt || idx}`,
      source: "operator" as const,
      action,
      actor,
      role,
      entityType,
      entityId,
      createdAt,
      handledAt: null,
      environment: env,
      detail: null,
    };
  });

  const aiRows = callbacks
    .filter((cb) => cb.aiHandled || cb.aiOutcome)
    .map((cb, idx) => {
      const action = cb.aiOutcome
        ? `AI outcome: ${cb.aiOutcome}`
        : "AI handled callback";
      const createdAt = fallbackString(cb.handledAt ?? cb.createdAt, "");
      const entityId = fallbackString(cb.id, "unknown");
      return {
        id: `ai-${entityId}-${createdAt || idx}`,
        source: "ai" as const,
        action,
        actor: "VetCan AI",
        role: "ai",
        entityType: "callback",
        entityId,
        createdAt,
        handledAt: cb.handledAt ?? null,
        environment,
        detail: cb.summary ?? null,
      };
    });

  return [...operatorRows, ...aiRows].sort((a, b) => {
    const aTime = getEpochMs(a.createdAt) ?? 0;
    const bTime = getEpochMs(b.createdAt) ?? 0;
    return bTime - aTime;
  });
};

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 style={{ fontSize: "20px", marginBottom: "12px" }}>
      {children}
    </h2>
  );
}

export default function MetricsDashboard() {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [integrations, setIntegrations] =
    useState<IntegrationsStatusResponse | null>(null);
  const [externalVisibility, setExternalVisibility] =
    useState<PlatformExternalVisibilityResponse | null>(null);
  const [externalVisibilityLoading, setExternalVisibilityLoading] =
    useState(true);
  const [externalVisibilityError, setExternalVisibilityError] = useState<
    string | null
  >(null);
  const [salesOfferClarity, setSalesOfferClarity] =
    useState<SalesOfferClarityResponse | null>(null);
  const [salesOfferClarityLoading, setSalesOfferClarityLoading] = useState(true);
  const [salesOfferClarityError, setSalesOfferClarityError] = useState<
    string | null
  >(null);
  const [launchControl, setLaunchControl] =
    useState<LaunchControlReadinessResponse | null>(null);
  const [launchControlLoading, setLaunchControlLoading] = useState(true);
  const [launchControlError, setLaunchControlError] = useState<string | null>(
    null
  );
  const [week1Stabilization, setWeek1Stabilization] =
    useState<Week1StabilizationResponse | null>(null);
  const [week1StabilizationLoading, setWeek1StabilizationLoading] =
    useState(true);
  const [week1StabilizationError, setWeek1StabilizationError] = useState<
    string | null
  >(null);
  const [revenueRecovery, setRevenueRecovery] =
    useState<RevenueRecoveryResponse | null>(null);
  const [revenueRecoveryLoading, setRevenueRecoveryLoading] = useState(true);
  const [revenueRecoveryError, setRevenueRecoveryError] = useState<string | null>(null);
  const [moduleMatrix, setModuleMatrix] = useState<ModuleMatrixResponse | null>(
    null
  );
  const [moduleMatrixLoading, setModuleMatrixLoading] = useState(true);
  const [moduleMatrixError, setModuleMatrixError] = useState<string | null>(null);
  const [playbookActiveSummary, setPlaybookActiveSummary] =
    useState<PlaybookActiveSummaryResponse | null>(null);
  const [playbookActiveSummaryLoading, setPlaybookActiveSummaryLoading] =
    useState(true);
  const [playbookActiveSummaryError, setPlaybookActiveSummaryError] = useState<
    string | null
  >(null);
  const [paymentExceptions, setPaymentExceptions] =
    useState<PaymentExceptionsResponse | null>(null);
  const [paymentExceptionsLoading, setPaymentExceptionsLoading] = useState(true);
  const [paymentExceptionsError, setPaymentExceptionsError] = useState<
    string | null
  >(null);
  const [paymentFollowups, setPaymentFollowups] =
    useState<PaymentFollowupOpsResponse | null>(null);
  const [paymentFollowupsLoading, setPaymentFollowupsLoading] = useState(true);
  const [paymentFollowupsError, setPaymentFollowupsError] = useState<string | null>(
    null
  );
  const [paymentCaptureOutcomes, setPaymentCaptureOutcomes] =
    useState<PaymentCaptureOutcomesResponse | null>(null);
  const [paymentCaptureOutcomesLoading, setPaymentCaptureOutcomesLoading] =
    useState(true);
  const [paymentCaptureOutcomesError, setPaymentCaptureOutcomesError] = useState<
    string | null
  >(null);
  const [playbookPreviewChannel, setPlaybookPreviewChannel] = useState<
    "chat" | "voice" | "outbound_voice"
  >("chat");
  const [playbookPreviewMessage, setPlaybookPreviewMessage] = useState("");
  const [playbookPreviewTopicHint, setPlaybookPreviewTopicHint] = useState("");
  const [playbookPreviewIntentHint, setPlaybookPreviewIntentHint] = useState("");
  const [playbookPreviewSubmitting, setPlaybookPreviewSubmitting] = useState(false);
  const [playbookPreviewResult, setPlaybookPreviewResult] =
    useState<PlaybookPreviewResponse | null>(null);
  const [playbookPreviewError, setPlaybookPreviewError] = useState<string | null>(
    null
  );
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [metricsUpdatedAt, setMetricsUpdatedAt] = useState<string | null>(null);

  const [callbacks, setCallbacks] = useState<Callback[]>([]);
  const [callbacksLoading, setCallbacksLoading] = useState(true);
  const [callbacksError, setCallbacksError] = useState<string | null>(null);
  const [callbacksNonJsonResponse, setCallbacksNonJsonResponse] =
    useState(false);
  const [callbacksUpdatedAt, setCallbacksUpdatedAt] = useState<Date | null>(null);
  const [callbackRequestQueue, setCallbackRequestQueue] = useState<
    CallbackRequestQueueItem[]
  >([]);
  const [callbackRequestQueueLoading, setCallbackRequestQueueLoading] =
    useState(true);
  const [callbackRequestActionKey, setCallbackRequestActionKey] = useState<
    string | null
  >(null);
  const [callbackRequestActionMessage, setCallbackRequestActionMessage] =
    useState<string | null>(null);
  const [callbackRequestActionError, setCallbackRequestActionError] = useState<
    string | null
  >(null);
  const [callbackCallNowFeedback, setCallbackCallNowFeedback] = useState<
    Record<string, string>
  >({});
  const [alertActionKey, setAlertActionKey] = useState<string | null>(null);
  const [alertResolveNotes, setAlertResolveNotes] = useState<
    Record<string, string>
  >({});
  const [voiceOpsCalls, setVoiceOpsCalls] = useState<VoiceOpsCall[]>([]);
  const [voiceOpsLoading, setVoiceOpsLoading] = useState(true);
  const [voiceOpsError, setVoiceOpsError] = useState<string | null>(null);
  const [voiceTimelineCallSid, setVoiceTimelineCallSid] = useState<string | null>(
    null
  );
  const [voiceTimelineEvents, setVoiceTimelineEvents] = useState<
    VoiceTimelineEvent[]
  >([]);
  const [voiceTimelineLoading, setVoiceTimelineLoading] = useState(false);
  const [voiceTimelineError, setVoiceTimelineError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [recentlyUpdatedId, setRecentlyUpdatedId] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<ActiveAlert[]>([]);
  const [alertsError, setAlertsError] = useState<string | null>(null);
  const [escalationsStatus, setEscalationsStatus] =
    useState<EscalationsStatusResponse | null>(null);
  const [escalationsLoading, setEscalationsLoading] = useState(true);
  const [escalationsError, setEscalationsError] = useState<string | null>(null);
  const [escalationDeliveries, setEscalationDeliveries] = useState<
    EscalationDeliveryAttempt[]
  >([]);
  const [escalationDeliveriesLoading, setEscalationDeliveriesLoading] =
    useState(true);
  const [escalationDeliveriesError, setEscalationDeliveriesError] =
    useState<string | null>(null);
  const [escalationEligibilityProbe, setEscalationEligibilityProbe] =
    useState<EscalationEligibilityProbeResponse | null>(null);
  const [outboundSchedulingMetrics, setOutboundSchedulingMetrics] =
    useState<OutboundSchedulingMetricsResponse | null>(null);
  const [outboundSchedulingLoading, setOutboundSchedulingLoading] = useState(true);
  const [outboundSchedulingError, setOutboundSchedulingError] = useState<string | null>(
    null
  );
  const [outboundSchedulingSnapshotDays, setOutboundSchedulingSnapshotDays] =
    useState<7 | 14 | 30>(7);
  const [schedulingExceptions, setSchedulingExceptions] = useState<
    SchedulingExceptionItem[]
  >([]);
  const [schedulingExceptionsLoading, setSchedulingExceptionsLoading] =
    useState(true);
  const [schedulingExceptionsError, setSchedulingExceptionsError] = useState<
    string | null
  >(null);
  const [schedulingSweepRuns, setSchedulingSweepRuns] = useState<
    SchedulingSweepRun[]
  >([]);
  const [schedulingSweepRunsLoading, setSchedulingSweepRunsLoading] =
    useState(true);
  const [schedulingSweepRunsError, setSchedulingSweepRunsError] = useState<
    string | null
  >(null);
  const [outboundVoiceSweepRuns, setOutboundVoiceSweepRuns] = useState<
    OutboundVoiceSweepRun[]
  >([]);
  const [outboundVoiceSweepRunsLoading, setOutboundVoiceSweepRunsLoading] =
    useState(true);
  const [outboundVoiceSweepRunsError, setOutboundVoiceSweepRunsError] = useState<
    string | null
  >(null);
  const [outboundVoiceSweepHealth, setOutboundVoiceSweepHealth] =
    useState<OutboundVoiceSweepHealth | null>(null);
  const [outboundVoiceSweepHealthLoading, setOutboundVoiceSweepHealthLoading] =
    useState(true);
  const [outboundVoiceSweepHealthError, setOutboundVoiceSweepHealthError] =
    useState<string | null>(null);
  const [schedulingExceptionsActionKey, setSchedulingExceptionsActionKey] =
    useState<string | null>(null);
  const [schedulingExceptionsToast, setSchedulingExceptionsToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [schedulingExceptionsBucketFilter, setSchedulingExceptionsBucketFilter] =
    useState<SchedulingExceptionsBucketFilter>("all");
  const [schedulingExceptionsMinAgeMinutes, setSchedulingExceptionsMinAgeMinutes] =
    useState<number>(60);
  const [schedulingExceptionsBulkAction, setSchedulingExceptionsBulkAction] =
    useState<"resend-bulk" | "force-staff-bulk" | null>(null);
  const [schedulingExceptionsExporting, setSchedulingExceptionsExporting] =
    useState(false);
  const [escalationCorrelationId, setEscalationCorrelationId] =
    useState<string | null>(null);
  const [escalationCorrelationExternalIncident, setEscalationCorrelationExternalIncident] =
    useState<EscalationCorrelationDrilldownResponse["externalIncident"]>(null);
  const [escalationCorrelationDeliveries, setEscalationCorrelationDeliveries] =
    useState<EscalationCorrelationDelivery[]>([]);
  const [escalationCorrelationEvents, setEscalationCorrelationEvents] =
    useState<EscalationCorrelationEvent[]>([]);
  const [escalationCorrelationLoading, setEscalationCorrelationLoading] =
    useState(false);
  const [escalationCorrelationError, setEscalationCorrelationError] =
    useState<string | null>(null);
  const [escalationCorrelationRetryingId, setEscalationCorrelationRetryingId] =
    useState<string | null>(null);
  const [escalationsResetting, setEscalationsResetting] = useState(false);
  const [escalationsBackoffResetting, setEscalationsBackoffResetting] =
    useState(false);
  const [operatorAuditEntries, setOperatorAuditEntries] = useState<OperatorAuditEntry[]>([]);
  const [operatorAuditError, setOperatorAuditError] = useState<string | null>(null);
  const [operatorAuditLoading, setOperatorAuditLoading] = useState(true);
  const [auditActionFilter, setAuditActionFilter] = useState<string>("all");
  const [auditWindow, setAuditWindow] = useState<"24h" | "7d" | "all">("24h");
  const [auditSourceFilter, setAuditSourceFilter] = useState<"all" | "operator" | "ai">("all");
  const [operatorTokenInput, setOperatorTokenInput] = useState("");
  const [showOperatorToken, setShowOperatorToken] = useState(false);
  const [operatorTokenSaving, setOperatorTokenSaving] = useState(false);
  const [operatorAccessError, setOperatorAccessError] = useState<string | null>(
    null
  );
  const [reportingExportMessage, setReportingExportMessage] = useState<string | null>(
    null
  );
  const [authMode, setAuthMode] = useState<"none" | "token" | "bypass">("none");
  const [metricsBypassKey, setMetricsBypassKey] = useState<string | null>(null);
  const [tokenSource, setTokenSource] = useState<MetricsTokenSource>("none");
  const [tokenBootstrapReady, setTokenBootstrapReady] = useState(false);
  const [operatorToken, setOperatorTokenState] = useState(
    () => getOperatorToken() ?? ""
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [clinicProfileTenantKey, setClinicProfileTenantKey] = useState<
    string | null
  >(null);
  const [clinicProfileForm, setClinicProfileForm] = useState<ClinicProfileFormValues>(
    EMPTY_CLINIC_PROFILE_FORM
  );
  const [clinicProfileLoading, setClinicProfileLoading] = useState(false);
  const [clinicProfileSaving, setClinicProfileSaving] = useState(false);
  const [clinicProfileMessage, setClinicProfileMessage] = useState<string | null>(
    null
  );
  const [clinicProfileError, setClinicProfileError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const hasOperatorToken = Boolean(operatorToken.trim());
  const isBypassMode = authMode === "bypass" && Boolean(metricsBypassKey);
  const hasAccessCredential = hasOperatorToken || isBypassMode;
  const canViewInternalMetrics = hasAccessCredential && !operatorAccessError;
  const showOperatorAccessPanel =
    (!hasOperatorToken && !isBypassMode) || Boolean(operatorAccessError);
  const refreshInFlightRef = useRef(false);
  const backoffUntilRef = useRef(0);
  const [copyTokenState, setCopyTokenState] = useState<"idle" | "copied">(
    "idle"
  );
  const normalizedOperatorTokenInput = normalizeTokenInput(operatorTokenInput);
  const decodedOperatorToken = useMemo(
    () => decodeJwtPayload(normalizedOperatorTokenInput),
    [normalizedOperatorTokenInput]
  );
  const decodedStoredOperatorToken = useMemo(
    () => decodeJwtPayload(normalizeTokenInput(operatorToken)),
    [operatorToken]
  );
  const decodedTokenPayload = decodedOperatorToken?.payload;
  const decodedTokenHeader = decodedOperatorToken?.header;
  const operatorRole =
    typeof decodedStoredOperatorToken?.payload?.role === "string"
      ? decodedStoredOperatorToken.payload.role.trim().toLowerCase()
      : "";
  const tokenExpiryIso = useMemo(
    () => jwtNumericDateToIso(decodedTokenPayload?.exp),
    [decodedTokenPayload?.exp]
  );
  const tokenIssuedAtIso = useMemo(
    () => jwtNumericDateToIso(decodedTokenPayload?.iat),
    [decodedTokenPayload?.iat]
  );
  const tokenExpiryDate = tokenExpiryIso ? new Date(tokenExpiryIso) : null;
  const tokenExpired =
    tokenExpiryDate && !Number.isNaN(tokenExpiryDate.getTime())
      ? tokenExpiryDate.getTime() <= Date.now()
      : null;
  const tokenScopeDisplay = useMemo(() => {
    const scope = decodedTokenPayload?.scope;
    if (typeof scope === "string" && scope.trim()) return scope.trim();
    if (Array.isArray(scope)) {
      const values = scope
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim())
        .filter(Boolean);
      return values.length ? values.join(", ") : "Unavailable";
    }
    return "Unavailable";
  }, [decodedTokenPayload?.scope]);
  const tokenTypeDisplay =
    typeof decodedTokenPayload?.typ === "string" && decodedTokenPayload.typ.trim()
      ? decodedTokenPayload.typ.trim()
      : "Unavailable";

  const safeJson = async <T,>(res: Response | undefined | null, fallback: T) => {
    if (!res || typeof res.json !== "function") return fallback;
    try {
      return (await res.json()) as T;
    } catch {
      return fallback;
    }
  };

  const isRateLimitedResponse = (res: Response | undefined | null) =>
    res?.status === 429;

  const isUnauthorizedResponse = (res: Response | undefined | null) =>
    res?.status === 401 || res?.status === 403;

  const hasMetricsReadScope = (scope: unknown) => {
    if (Array.isArray(scope)) {
      return scope
        .filter((item): item is string => typeof item === "string")
        .flatMap((item) => item.split(/[,\s]+/))
        .map((item) => item.trim())
        .filter(Boolean)
        .includes("metrics:read");
    }
    if (typeof scope === "string") {
      return scope
        .split(/[,\s]+/)
        .map((item) => item.trim())
        .filter(Boolean)
        .includes("metrics:read");
    }
    return false;
  };

  const inferTokenReasonLocally = (tokenInput: string) => {
    const token = normalizeTokenInput(tokenInput);
    if (!token) return "missing_token";
    const parts = token.split(".");
    if (parts.length !== 3 || parts.some((part) => !part.trim())) {
      return "malformed_token";
    }
    const decoded = decodeJwtPayload(token);
    const payload = decoded?.payload;
    if (!payload) return "malformed_token";
    if (payload.typ !== "client_metrics") return "wrong_typ";
    if (!hasMetricsReadScope(payload.scope)) return "wrong_scope";
    if (typeof payload.iat !== "number" || typeof payload.exp !== "number") {
      return "invalid_temporal";
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (payload.iat > nowSeconds + 60) return "iat_in_future";
    if (payload.exp <= nowSeconds) return "expired";
    return null;
  };

  const getReasonFromUnauthorizedResponse = async (
    res: Response | undefined | null
  ) => {
    if (!isUnauthorizedResponse(res)) return null;
    const body = await safeJson<{ reason?: unknown }>(res, {});
    if (typeof body.reason === "string" && body.reason.trim()) {
      return body.reason;
    }
    return null;
  };

  const requestTokenValidationReason = async (token: string) => {
    try {
      const response = await fetch(
        withVetcanApiBase("/api/internal/client-metrics/validate"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ token }),
        }
      );
      if (!response.ok) return null;
      const diagnostics = await safeJson<ClientMetricsValidateResponse>(
        response,
        {}
      );
      if (typeof diagnostics.reason === "string" && diagnostics.reason.trim()) {
        return diagnostics.reason;
      }
      return null;
    } catch {
      return null;
    }
  };

  const resolveTokenAccessErrorMessage = async ({
    token,
    unauthorizedResponses = [],
  }: {
    token: string;
    unauthorizedResponses?: Array<Response | undefined | null>;
  }) => {
    let responseReasonMessage: string | null = null;
    for (const response of unauthorizedResponses) {
      const reason = await getReasonFromUnauthorizedResponse(response);
      if (!responseReasonMessage) {
        responseReasonMessage = mapTokenReasonToMessage(reason);
      }
    }

    const normalizedToken = normalizeTokenInput(token);
    if (!normalizedToken) {
      return responseReasonMessage || INTERNAL_METRICS_MESSAGE;
    }

    const diagnosticsReason = await requestTokenValidationReason(normalizedToken);
    const diagnosticsMessage = mapTokenReasonToMessage(diagnosticsReason);
    if (diagnosticsMessage) return diagnosticsMessage;

    if (responseReasonMessage) return responseReasonMessage;

    const localReason = inferTokenReasonLocally(normalizedToken);
    const localMessage = mapTokenReasonToMessage(localReason);
    if (localMessage) return localMessage;

    return TOKEN_GENERIC_ERROR_MESSAGE;
  };

  const setRateLimitBackoff = () => {
    backoffUntilRef.current = Math.max(
      backoffUntilRef.current,
      Date.now() + RATE_LIMIT_BACKOFF_MS
    );
  };

  const apiFetchInternalRead = (input: string, init: RequestInit = {}) => {
    if (!isBypassMode || !metricsBypassKey) {
      return apiFetch(input, init);
    }
    const method = (init.method || "GET").toUpperCase();
    const isAllowlisted =
      method === "GET" &&
      BYPASS_INTERNAL_READ_PREFIXES.some((prefix) => input.startsWith(prefix));
    if (!isAllowlisted) {
      return apiFetch(input, init);
    }

    const headers = new Headers(init.headers || {});
    headers.set("x-metrics-bypass", metricsBypassKey);
    return apiFetch(input, {
      ...init,
      method,
      headers,
    });
  };

  const triggerManualRefresh = () => {
    backoffUntilRef.current = 0;
    setMetricsLoading(true);
    setExternalVisibilityLoading(true);
    setSalesOfferClarityLoading(true);
    setLaunchControlLoading(true);
    setWeek1StabilizationLoading(true);
    setRevenueRecoveryLoading(true);
    setModuleMatrixLoading(true);
    setPlaybookActiveSummaryLoading(true);
    setPaymentExceptionsLoading(true);
    setPaymentFollowupsLoading(true);
    setPaymentCaptureOutcomesLoading(true);
    setCallbacksLoading(true);
    setCallbackRequestQueueLoading(true);
    setVoiceOpsLoading(true);
    setEscalationsLoading(true);
    setEscalationDeliveriesLoading(true);
    setEscalationEligibilityProbe(null);
    setOutboundSchedulingLoading(true);
    setSchedulingExceptionsLoading(true);
    setOperatorAuditLoading(true);
    setRefreshKey((value) => value + 1);
  };

  const handleSaveOperatorToken = async () => {
    const token = normalizeTokenInput(operatorTokenInput);
    if (!token || operatorTokenSaving) return;

    setOperatorTokenSaving(true);
    try {
      const validationRes = await fetch(withVetcanApiBase("/api/internal/status"), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (validationRes.ok) {
        setOperatorToken(token);
        setOperatorTokenState(token);
        setAuthMode("token");
        setTokenSource("storage");
        setOperatorTokenInput("");
        setOperatorAccessError(null);
        triggerManualRefresh();
        return;
      }

      if (isUnauthorizedResponse(validationRes)) {
        const reasonMessage = await resolveTokenAccessErrorMessage({
          token,
          unauthorizedResponses: [validationRes],
        });
        setOperatorAccessError(reasonMessage);
        return;
      }
      if (validationRes.status === 429) {
        setOperatorAccessError(TOKEN_RATE_LIMIT_MESSAGE);
        return;
      }

      setOperatorAccessError(TOKEN_GENERIC_ERROR_MESSAGE);
    } catch {
      setOperatorAccessError(TOKEN_GENERIC_ERROR_MESSAGE);
    } finally {
      setOperatorTokenSaving(false);
    }
  };

  const handleClearOperatorToken = () => {
    clearOperatorToken();
    setOperatorTokenState("");
    if (isBypassMode) {
      setAuthMode("bypass");
    } else {
      setAuthMode("none");
    }
    setTokenSource("none");
    setOperatorTokenInput("");
    setCopyTokenState("idle");
    setShowOperatorToken(false);
    setOperatorTokenSaving(false);
    setOperatorAccessError(null);
    triggerManualRefresh();
  };

  const handleCopyToken = async () => {
    const token = normalizeTokenInput(operatorTokenInput);
    if (!token || !navigator?.clipboard?.writeText) return;

    try {
      await navigator.clipboard.writeText(token);
      setCopyTokenState("copied");
      setTimeout(() => {
        setCopyTokenState("idle");
      }, COPY_CONFIRMATION_TIMEOUT_MS);
    } catch {
      // Silent: copy action is optional convenience.
    }
  };

  const handleRunPlaybookPreview = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canViewInternalMetrics || !hasOperatorToken || isClientViewerMode) return;

    const message = playbookPreviewMessage.trim();
    const topicHint = playbookPreviewTopicHint.trim();
    const intentHint = playbookPreviewIntentHint.trim();
    if (!message && !topicHint && !intentHint) {
      setPlaybookPreviewResult(null);
      setPlaybookPreviewError("Enter a message or a hint to preview.");
      return;
    }

    setPlaybookPreviewSubmitting(true);
    setPlaybookPreviewError(null);

    try {
      const res = await apiFetch("/api/internal/playbooks/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel: playbookPreviewChannel,
          message,
          topicHint: topicHint || undefined,
          intentHint: intentHint || undefined,
        }),
      });

      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }

      if (!res || !res.ok) {
        const body = await safeJson<{ error?: string }>(res, {});
        if (body.error === "invalid_channel") {
          setPlaybookPreviewError("Channel must be chat, voice, or outbound_voice.");
        } else if (body.error === "preview_input_empty") {
          setPlaybookPreviewError("Enter a message or a hint to preview.");
        } else {
          setPlaybookPreviewError("Unable to run playbook preview.");
        }
        setPlaybookPreviewResult(null);
        return;
      }

      const payload = await safeJson<PlaybookPreviewResponse>(res, {
        matched: false,
      });
      if (typeof payload.matched !== "boolean") {
        setPlaybookPreviewResult(null);
        setPlaybookPreviewError("Preview response malformed.");
        return;
      }

      setPlaybookPreviewResult(payload);
      setPlaybookPreviewError(null);
    } catch {
      setPlaybookPreviewResult(null);
      setPlaybookPreviewError("Unable to run playbook preview.");
    } finally {
      setPlaybookPreviewSubmitting(false);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      setAuthMode(getOperatorToken() ? "token" : "none");
      setTokenBootstrapReady(true);
      return;
    }

    let isMounted = true;

    const removeCodeFromUrl = () => {
      try {
        const current = new URL(window.location.href);
        let changed = false;

        if (current.searchParams.has("code")) {
          current.searchParams.delete("code");
          changed = true;
        }

        const rawHash = current.hash.startsWith("#")
          ? current.hash.slice(1)
          : current.hash;

        if (rawHash) {
          if (rawHash.includes("?")) {
            const queryStart = rawHash.indexOf("?");
            const hashPath = rawHash.slice(0, queryStart);
            const hashQuery = rawHash.slice(queryStart + 1);
            const hashParams = new URLSearchParams(hashQuery);
            if (hashParams.has("code")) {
              hashParams.delete("code");
              const nextHashQuery = hashParams.toString();
              current.hash = nextHashQuery
                ? `#${hashPath}?${nextHashQuery}`
                : hashPath
                ? `#${hashPath}`
                : "";
              changed = true;
            }
          } else {
            const hashParams = new URLSearchParams(rawHash);
            if (hashParams.has("code")) {
              hashParams.delete("code");
              const nextHash = hashParams.toString();
              current.hash = nextHash ? `#${nextHash}` : "";
              changed = true;
            }
          }
        }

        if (changed) {
          const nextUrl = `${current.pathname}${current.search}${current.hash}`;
          window.history.replaceState(window.history.state, "", nextUrl || "/metrics");
        }
      } catch {
        // URL cleanup is best-effort and should never block auth bootstrap.
      }
    };

    const bootstrapToken = async () => {
      const search = window.location.search;
      const hash = window.location.hash;
      const exchangeCode = getMetricsCodeFromLocation(search, hash);
      const linkToken = getMetricsTokenFromLocation(search, hash);
      const bypassKey = getMetricsBypassKeyFromLocation(search, hash);
      const storedToken = getOperatorToken();

      setMetricsBypassKey(null);

      if (exchangeCode) {
        try {
          const exchangeRes = await fetch(
            withVetcanApiBase(
              `/api/internal/client-metrics/exchange?code=${encodeURIComponent(
                exchangeCode
              )}`
            ),
            {
              method: "GET",
            }
          );

          const exchangePayload = await safeJson<ClientMetricsExchangeResponse>(
            exchangeRes,
            {}
          );

          if (!isMounted) return;

          if (!exchangeRes.ok) {
            const reasonMessage =
              mapTokenReasonToMessage(exchangePayload.reason) ||
              EXCHANGE_CODE_REASON_MESSAGES[
                String(exchangePayload.reason || exchangePayload.error || "")
              ] ||
              EXCHANGE_CODE_GENERIC_ERROR_MESSAGE;
            clearOperatorToken();
            setOperatorTokenState("");
            setAuthMode("none");
            setTokenSource("none");
            setOperatorAccessError(reasonMessage);
            setTokenBootstrapReady(true);
            return;
          }

          const token = normalizeTokenInput(exchangePayload.token);
          if (!token) {
            clearOperatorToken();
            setOperatorTokenState("");
            setAuthMode("none");
            setTokenSource("none");
            setOperatorAccessError(EXCHANGE_CODE_GENERIC_ERROR_MESSAGE);
            setTokenBootstrapReady(true);
            return;
          }

          if (storedToken !== token) {
            setOperatorToken(token);
          }
          removeCodeFromUrl();
          setOperatorTokenState(token);
          setAuthMode("token");
          setTokenSource("code");
          setOperatorAccessError(null);
          setTokenBootstrapReady(true);
          return;
        } catch {
          if (!isMounted) return;
          clearOperatorToken();
          setOperatorTokenState("");
          setAuthMode("none");
          setTokenSource("none");
          setOperatorAccessError(EXCHANGE_CODE_GENERIC_ERROR_MESSAGE);
          setTokenBootstrapReady(true);
          return;
        }
      }

      if (linkToken) {
        if (!isMounted) return;
        if (linkToken !== storedToken) {
          setOperatorToken(linkToken);
        }
        setOperatorTokenState(linkToken);
        setAuthMode("token");
        setTokenSource("link");
        setOperatorAccessError(null);
        setTokenBootstrapReady(true);
        return;
      }

      if (bypassKey) {
        if (!isMounted) return;
        setMetricsBypassKey(bypassKey);
        setAuthMode("bypass");
        setOperatorTokenState("");
        setTokenSource("none");
        setOperatorAccessError(null);
        setTokenBootstrapReady(true);
        return;
      }

      if (!isMounted) return;

      if (storedToken) {
        setOperatorTokenState(storedToken);
        setAuthMode("token");
        setTokenSource("storage");
      } else {
        setOperatorTokenState("");
        setAuthMode("none");
        setTokenSource("none");
      }
      setTokenBootstrapReady(true);
    };

    void bootstrapToken();

    return () => {
      isMounted = false;
    };
  }, []);

  const readCallbackRequestQueue = async () => {
    const res = await apiFetchInternalRead("/api/internal/callback-requests?limit=50");
    if (isRateLimitedResponse(res)) {
      setRateLimitBackoff();
      throw new Error("Callback queue rate-limited");
    }
    if (!res || !res.ok) {
      throw new Error("Callback queue request failed");
    }
    const data = await safeJson<{ callbackRequests?: CallbackRequestQueueItem[] }>(
      res,
      { callbackRequests: [] }
    );
    return Array.isArray(data?.callbackRequests) ? data.callbackRequests : [];
  };

  useEffect(() => {
    if (!tokenBootstrapReady) return;

    let isMounted = true;
    let pollTimeout: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = (delayMs: number) => {
      if (!isMounted) return;
      if (pollTimeout) clearTimeout(pollTimeout);
      pollTimeout = setTimeout(() => {
        void refreshDashboard();
      }, delayMs);
    };

    const applyCallbacksResponse = async (
      callbacksRes: Response | undefined | null
    ) => {
      if (!callbacksRes || !callbacksRes.ok) {
        if (isRateLimitedResponse(callbacksRes)) setRateLimitBackoff();
        console.warn(`[callbacks] HTTP ${callbacksRes?.status ?? "unknown"}`);
        setCallbacks([]);
        setCallbacksError("Callbacks temporarily unavailable.");
        setCallbacksNonJsonResponse(false);
        setCallbacksUpdatedAt(new Date());
        return;
      }

      const contentType = (callbacksRes.headers?.get("content-type") || "").toLowerCase();
      if (!contentType.includes("application/json")) {
        console.warn(
          `[callbacks] non-JSON response content-type: ${contentType || "unknown"}`
        );
        setCallbacks([]);
        setCallbacksError("Callbacks temporarily unavailable.");
        setCallbacksNonJsonResponse(true);
        setCallbacksUpdatedAt(new Date());
        return;
      }

      const callbackData = await safeJson<Callback[]>(callbacksRes, []);
      if (!isMounted) return;
      const callbackList = Array.isArray(callbackData) ? callbackData : [];
      setCallbacks(callbackList);
      setCallbacksError(null);
      setCallbacksNonJsonResponse(false);
      if (callbackList.length > 0) {
        setRecentlyUpdatedId(callbackList[0].id);
        setTimeout(() => setRecentlyUpdatedId(null), 2000);
      }
      setCallbacksUpdatedAt(new Date());
    };

    const loadPublicData = async () => {
      const [publicMetricsResult, callbacksResult] = await Promise.allSettled([
        apiFetch("/api/metrics"),
        apiFetch("/api/callbacks"),
      ]);
      if (!isMounted) return;

      if (publicMetricsResult.status !== "fulfilled") {
        setMetrics(null);
        setMetricsUpdatedAt(null);
        setMetricsError("Unable to load metrics.");
      } else {
        const publicMetricsRes = publicMetricsResult.value;
        if (!publicMetricsRes || !publicMetricsRes.ok) {
          if (isRateLimitedResponse(publicMetricsRes)) setRateLimitBackoff();
          setMetrics(null);
          setMetricsUpdatedAt(null);
          setMetricsError("Unable to load metrics.");
        } else {
          const contentType = (
            publicMetricsRes.headers?.get("content-type") || ""
          ).toLowerCase();
          if (contentType && !contentType.includes("application/json")) {
            setMetrics(null);
            setMetricsUpdatedAt(null);
            setMetricsError("Unable to load metrics.");
          } else {
            const publicMetrics = await safeJson<MetricsResponse | null>(
              publicMetricsRes,
              null
            );
            if (!isMounted) return;
            if (!publicMetrics || typeof publicMetrics !== "object") {
              setMetrics(null);
              setMetricsUpdatedAt(null);
              setMetricsError("Unable to load metrics.");
            } else {
              setMetrics(publicMetrics);
              setMetricsUpdatedAt(new Date().toLocaleTimeString());
              setMetricsError(null);
            }
          }
        }
      }

      const callbacksRes =
        callbacksResult.status === "fulfilled" ? callbacksResult.value : null;
      await applyCallbacksResponse(callbacksRes);
      if (!isMounted) return;

      setStatus(null);
      setIntegrations(null);
      setExternalVisibility(null);
      setExternalVisibilityError(null);
      setSalesOfferClarity(null);
      setSalesOfferClarityError(null);
      setLaunchControl(null);
      setLaunchControlError(null);
      setWeek1Stabilization(null);
      setWeek1StabilizationError(null);
      setModuleMatrix(null);
      setModuleMatrixError(null);
      setPlaybookActiveSummary(null);
      setPlaybookActiveSummaryError(null);
      setPaymentExceptions(null);
      setPaymentExceptionsError(null);
      setRecentEvents([]);
      setCallbackRequestQueue([]);
      setVoiceOpsCalls([]);
      setVoiceOpsError(null);
      setAlerts([]);
      setAlertsError(null);
      setEscalationsStatus(null);
      setEscalationsError(null);
      setEscalationDeliveries([]);
      setEscalationDeliveriesError(null);
      setOutboundSchedulingMetrics(null);
      setOutboundSchedulingError(null);
      setSchedulingExceptions([]);
      setSchedulingExceptionsError(null);
      setSchedulingSweepRuns([]);
      setSchedulingSweepRunsError(null);
      setOutboundVoiceSweepRuns([]);
      setOutboundVoiceSweepRunsError(null);
      setOutboundVoiceSweepHealth(null);
      setOutboundVoiceSweepHealthError(null);
      setOperatorAuditEntries([]);
      setOperatorAuditError(null);
    };

    const refreshDashboard = async () => {
      if (!isMounted || refreshInFlightRef.current) return;

      const now = Date.now();
      if (backoffUntilRef.current > now) {
        scheduleNext(backoffUntilRef.current - now);
        return;
      }

      refreshInFlightRef.current = true;
      try {
        if (!canViewInternalMetrics) {
          await loadPublicData();
          return;
        }

        const [metricsRes, statusRes, integrationsRes] = await Promise.all([
          apiFetchInternalRead("/api/internal/metrics"),
          apiFetchInternalRead("/api/internal/status"),
          apiFetchInternalRead("/api/internal/integrations"),
        ]);

        if (!isMounted) return;

        if ([metricsRes, statusRes, integrationsRes].some(isUnauthorizedResponse)) {
          const tokenErrorMessage = await resolveTokenAccessErrorMessage({
            token: operatorToken,
            unauthorizedResponses: [metricsRes, statusRes, integrationsRes],
          });
          setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
          await loadPublicData();
          return;
        }

        setOperatorAccessError(null);

        let metricsData: MetricsResponse | null = null;
        if (!metricsRes || !metricsRes.ok) {
          if (isRateLimitedResponse(metricsRes)) {
            setRateLimitBackoff();
          } else {
            console.warn(`[metrics] HTTP ${metricsRes?.status ?? "unknown"}`);
          }
          setMetricsError("Unable to load metrics.");
        } else {
          metricsData = await safeJson<MetricsResponse>(
            metricsRes,
            {} as MetricsResponse
          );
          if (!isMounted) return;
          setMetrics(metricsData || null);
          setMetricsUpdatedAt(new Date().toLocaleTimeString());
          setMetricsError(null);
        }

        let statusData: StatusResponse | null = null;
        if (!statusRes || !statusRes.ok) {
          if (isRateLimitedResponse(statusRes)) setRateLimitBackoff();
          setStatus(null);
        } else {
          statusData = await safeJson<StatusResponse>(
            statusRes,
            {} as StatusResponse
          );
          if (!isMounted) return;
          setStatus(statusData || null);
        }

        if (!integrationsRes || !integrationsRes.ok) {
          if (isRateLimitedResponse(integrationsRes)) setRateLimitBackoff();
          setIntegrations(null);
        } else {
          const integrationsData = await safeJson<IntegrationsStatusResponse>(
            integrationsRes,
            {} as IntegrationsStatusResponse
          );
          if (!isMounted) return;
          const hasExpectedShape =
            typeof integrationsData?.n8nAlertConfigured === "boolean" &&
            typeof integrationsData?.bookingConfigured === "boolean" &&
            typeof integrationsData?.metricsConfigured === "boolean";
          setIntegrations(hasExpectedShape ? integrationsData : null);
        }

        const clientViewerMode = Boolean(
          metricsData?.isClientViewer || statusData?.isClientViewer
        );

        if (clientViewerMode) {
          const [callbacksRes, callbackQueueRes] = await Promise.all([
            apiFetch("/api/callbacks"),
            apiFetchInternalRead("/api/internal/callback-requests?limit=50"),
          ]);
          if (!isMounted) return;

          if (isUnauthorizedResponse(callbackQueueRes)) {
            const tokenErrorMessage = await resolveTokenAccessErrorMessage({
              token: operatorToken,
              unauthorizedResponses: [callbackQueueRes],
            });
            setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
            await loadPublicData();
            return;
          }

          await applyCallbacksResponse(callbacksRes);
          if (!isMounted) return;

          if (!callbackQueueRes || !callbackQueueRes.ok) {
            if (isRateLimitedResponse(callbackQueueRes)) setRateLimitBackoff();
            setCallbackRequestQueue([]);
          } else {
            const callbackQueueData = await safeJson<{
              callbackRequests?: CallbackRequestQueueItem[];
            }>(callbackQueueRes, { callbackRequests: [] });
            if (!isMounted) return;
            setCallbackRequestQueue(
              Array.isArray(callbackQueueData.callbackRequests)
                ? callbackQueueData.callbackRequests
                : []
            );
          }

          setRecentEvents([]);
          setExternalVisibility(null);
          setExternalVisibilityError(null);
          setSalesOfferClarity(null);
          setSalesOfferClarityError(null);
          setLaunchControl(null);
          setLaunchControlError(null);
          setWeek1Stabilization(null);
          setWeek1StabilizationError(null);
          setRevenueRecovery(null);
          setRevenueRecoveryError(null);
          setModuleMatrix(null);
          setModuleMatrixError(null);
          setPlaybookActiveSummary(null);
          setPlaybookActiveSummaryError(null);
          setPaymentExceptions(null);
          setPaymentExceptionsError(null);
          setPaymentFollowups(null);
          setPaymentFollowupsError(null);
          setPaymentCaptureOutcomes(null);
          setPaymentCaptureOutcomesError(null);
          setVoiceOpsCalls([]);
          setVoiceOpsError(null);
          setAlerts([]);
          setAlertsError(null);
          setEscalationsStatus(null);
          setEscalationsError(null);
          setEscalationDeliveries([]);
          setEscalationDeliveriesError(null);
          setEscalationEligibilityProbe(null);
          setOutboundSchedulingMetrics(null);
          setOutboundSchedulingError(null);
          setSchedulingExceptions([]);
          setSchedulingExceptionsError(null);
          setSchedulingSweepRuns([]);
          setSchedulingSweepRunsError(null);
          setOutboundVoiceSweepRuns([]);
          setOutboundVoiceSweepRunsError(null);
          setOutboundVoiceSweepHealth(null);
          setOutboundVoiceSweepHealthError(null);
          setOperatorAuditEntries([]);
          setOperatorAuditError(null);
          return;
        }

        const canFetchSchedulingExceptions =
          hasOperatorToken &&
          (operatorRole === "admin" || operatorRole === "operator");
        const canFetchPaymentExceptions =
          hasOperatorToken &&
          (operatorRole === "admin" || operatorRole === "operator");
        const schedulingExceptionsQuery = new URLSearchParams({
          snapshotDays: String(SCHEDULING_EXCEPTIONS_SNAPSHOT_DAYS),
          limit: "200",
        });
        if (schedulingExceptionsBucketFilter !== "all") {
          schedulingExceptionsQuery.set("bucket", schedulingExceptionsBucketFilter);
        }
        const schedulingExceptionsPromise = canFetchSchedulingExceptions
          ? apiFetchInternalRead(
              `/api/internal/voice/ops/scheduling/awaiting-confirmation?${schedulingExceptionsQuery.toString()}`
            )
          : Promise.resolve(null as Response | null);
        const schedulingSweepRunsPromise = canFetchSchedulingExceptions
          ? apiFetchInternalRead(
              "/api/internal/voice/ops/scheduling/awaiting-confirmation/sweep-runs?limit=20"
            )
          : Promise.resolve(null as Response | null);
        const outboundVoiceSweepRunsPromise = canFetchSchedulingExceptions
          ? apiFetchInternalRead("/api/internal/voice/ops/outbound/sweep-runs?limit=20")
          : Promise.resolve(null as Response | null);
        const outboundVoiceSweepHealthPromise = canFetchSchedulingExceptions
          ? apiFetchInternalRead(
              "/api/internal/voice/ops/outbound/sweep-health?snapshotMinutes=60"
            )
          : Promise.resolve(null as Response | null);
        const paymentExceptionsPromise = canFetchPaymentExceptions
          ? apiFetchInternalRead(
              "/api/internal/voice/ops/payments/exceptions?snapshotDays=7&limit=100"
            )
          : Promise.resolve(null as Response | null);
        const paymentFollowupsPromise = canFetchPaymentExceptions
          ? apiFetchInternalRead("/api/internal/voice/ops/payments/followups?limit=100")
          : Promise.resolve(null as Response | null);
        const paymentCaptureOutcomesPromise = canFetchPaymentExceptions
          ? apiFetchInternalRead(
              "/api/internal/voice/ops/payments/capture-outcomes?limit=100"
            )
          : Promise.resolve(null as Response | null);

        const [
          recentEventsRes,
          callbacksRes,
          callbackQueueRes,
          externalVisibilityRes,
          salesOfferClarityRes,
          alertsRes,
          escalationsStatusRes,
          escalationDeliveriesRes,
          escalationEligibilityProbeRes,
          outboundSchedulingMetricsRes,
          schedulingExceptionsRes,
          schedulingSweepRunsRes,
          outboundVoiceSweepRunsRes,
          outboundVoiceSweepHealthRes,
          operatorAuditRes,
          voiceOpsRecentRes,
          launchControlRes,
          week1StabilizationRes,
          revenueRecoveryRes,
          moduleMatrixRes,
          playbookActiveSummaryRes,
          paymentExceptionsRes,
          paymentFollowupsRes,
          paymentCaptureOutcomesRes,
        ] = await Promise.all([
          apiFetchInternalRead("/api/internal/events/recent?limit=50"),
          apiFetch("/api/callbacks"),
          apiFetchInternalRead("/api/internal/callback-requests?limit=50"),
          apiFetchInternalRead("/api/internal/metrics/external-visibility"),
          apiFetchInternalRead("/api/internal/metrics/sales-offer-clarity"),
          apiFetchInternalRead("/api/internal/alerts/active"),
          apiFetchInternalRead("/api/internal/escalations/status"),
          apiFetchInternalRead("/api/internal/escalations/deliveries/recent?limit=10"),
          apiFetchInternalRead("/api/internal/escalations/eligibility/probe"),
          apiFetchInternalRead(
            `/api/internal/voice/metrics/outbound?snapshotDays=${outboundSchedulingSnapshotDays}`
          ),
          schedulingExceptionsPromise,
          schedulingSweepRunsPromise,
          outboundVoiceSweepRunsPromise,
          outboundVoiceSweepHealthPromise,
          apiFetchInternalRead("/api/internal/operator-audit?limit=100"),
          apiFetchInternalRead("/api/internal/voice/recent?limit=25"),
          apiFetchInternalRead("/api/internal/readiness/launch-control"),
          apiFetchInternalRead("/api/internal/ops/week1-stabilization"),
          apiFetchInternalRead("/api/internal/metrics/revenue-recovery"),
          apiFetchInternalRead("/api/internal/readiness/module-matrix"),
          apiFetchInternalRead("/api/internal/playbooks/active"),
          paymentExceptionsPromise,
          paymentFollowupsPromise,
          paymentCaptureOutcomesPromise,
        ]);

        if (!isMounted) return;

        const internalResponses = [
          recentEventsRes,
          callbackQueueRes,
          externalVisibilityRes,
          salesOfferClarityRes,
          alertsRes,
          escalationsStatusRes,
          escalationDeliveriesRes,
          escalationEligibilityProbeRes,
          outboundSchedulingMetricsRes,
          ...(schedulingExceptionsRes ? [schedulingExceptionsRes] : []),
          ...(schedulingSweepRunsRes ? [schedulingSweepRunsRes] : []),
          ...(outboundVoiceSweepRunsRes ? [outboundVoiceSweepRunsRes] : []),
          ...(outboundVoiceSweepHealthRes ? [outboundVoiceSweepHealthRes] : []),
          operatorAuditRes,
          voiceOpsRecentRes,
          launchControlRes,
          week1StabilizationRes,
          revenueRecoveryRes,
          moduleMatrixRes,
          playbookActiveSummaryRes,
          ...(paymentExceptionsRes ? [paymentExceptionsRes] : []),
          ...(paymentFollowupsRes ? [paymentFollowupsRes] : []),
          ...(paymentCaptureOutcomesRes ? [paymentCaptureOutcomesRes] : []),
        ];
        if (internalResponses.some(isUnauthorizedResponse)) {
          const tokenErrorMessage = await resolveTokenAccessErrorMessage({
            token: operatorToken,
            unauthorizedResponses: internalResponses,
          });
          setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
          await loadPublicData();
          return;
        }

        if (!recentEventsRes || !recentEventsRes.ok) {
          if (isRateLimitedResponse(recentEventsRes)) setRateLimitBackoff();
          setRecentEvents([]);
        } else {
          const recentEventsData = await safeJson<{ events: RecentEvent[] }>(
            recentEventsRes,
            { events: [] }
          );
          if (!isMounted) return;
          setRecentEvents(
            Array.isArray(recentEventsData.events) ? recentEventsData.events : []
          );
        }

        await applyCallbacksResponse(callbacksRes);
        if (!isMounted) return;

        if (!callbackQueueRes || !callbackQueueRes.ok) {
          if (isRateLimitedResponse(callbackQueueRes)) setRateLimitBackoff();
          setCallbackRequestQueue([]);
        } else {
          const callbackQueueData = await safeJson<{
            callbackRequests?: CallbackRequestQueueItem[];
          }>(callbackQueueRes, { callbackRequests: [] });
          if (!isMounted) return;
          setCallbackRequestQueue(
            Array.isArray(callbackQueueData.callbackRequests)
              ? callbackQueueData.callbackRequests
              : []
          );
        }

        if (!externalVisibilityRes || !externalVisibilityRes.ok) {
          if (isRateLimitedResponse(externalVisibilityRes)) setRateLimitBackoff();
          setExternalVisibility(null);
          setExternalVisibilityError("Unable to load external visibility seams.");
        } else {
          const externalVisibilityData =
            await safeJson<PlatformExternalVisibilityResponse>(
              externalVisibilityRes,
              {}
            );
          if (!isMounted) return;
          const items = Array.isArray(externalVisibilityData?.items)
            ? externalVisibilityData.items
            : [];
          setExternalVisibility({
            ...externalVisibilityData,
            items,
          });
          setExternalVisibilityError(null);
        }

        if (!salesOfferClarityRes || !salesOfferClarityRes.ok) {
          if (isRateLimitedResponse(salesOfferClarityRes)) setRateLimitBackoff();
          setSalesOfferClarity(null);
          setSalesOfferClarityError("Unable to load sales and ad-state clarity.");
        } else {
          const salesOfferClarityData = await safeJson<SalesOfferClarityResponse>(
            salesOfferClarityRes,
            {}
          );
          if (!isMounted) return;
          const offers = Array.isArray(salesOfferClarityData?.offers)
            ? salesOfferClarityData.offers
            : [];
          setSalesOfferClarity({
            ...salesOfferClarityData,
            offers,
          });
          setSalesOfferClarityError(null);
        }

        if (!alertsRes || !alertsRes.ok) {
          if (isRateLimitedResponse(alertsRes)) setRateLimitBackoff();
          setAlerts([]);
          setAlertsError("Unable to load alerts.");
        } else {
          const alertsData = await safeJson<ActiveAlert[]>(alertsRes, []);
          if (!isMounted) return;
          setAlerts(Array.isArray(alertsData) ? alertsData : []);
          setAlertsError(null);
        }

        if (!escalationsStatusRes || !escalationsStatusRes.ok) {
          if (isRateLimitedResponse(escalationsStatusRes)) setRateLimitBackoff();
          setEscalationsStatus(null);
          setEscalationsError("Unable to load escalation health.");
          setEscalationEligibilityProbe(null);
        } else {
          const escalationsData = await safeJson<EscalationsStatusResponse>(
            escalationsStatusRes,
            {}
          );
          if (!isMounted) return;
          setEscalationsStatus(escalationsData || null);
          setEscalationsError(null);
        }

        if (!escalationDeliveriesRes || !escalationDeliveriesRes.ok) {
          if (isRateLimitedResponse(escalationDeliveriesRes)) {
            setRateLimitBackoff();
          }
          setEscalationDeliveries([]);
          setEscalationDeliveriesError("Unable to load recent deliveries.");
        } else {
          const deliveriesData = await safeJson<EscalationDeliveryAttempt[]>(
            escalationDeliveriesRes,
            []
          );
          if (!isMounted) return;
          if (!Array.isArray(deliveriesData)) {
            setEscalationDeliveries([]);
            setEscalationDeliveriesError("Recent deliveries data malformed.");
          } else {
            setEscalationDeliveries(deliveriesData);
            setEscalationDeliveriesError(null);
          }
        }

        if (
          !escalationEligibilityProbeRes ||
          !escalationEligibilityProbeRes.ok
        ) {
          if (isRateLimitedResponse(escalationEligibilityProbeRes)) {
            setRateLimitBackoff();
          }
          setEscalationEligibilityProbe(null);
        } else {
          const probeData = await safeJson<EscalationEligibilityProbeResponse>(
            escalationEligibilityProbeRes,
            {}
          );
          if (!isMounted) return;
          setEscalationEligibilityProbe(probeData || null);
        }

        if (!operatorAuditRes || !operatorAuditRes.ok) {
          if (isRateLimitedResponse(operatorAuditRes)) setRateLimitBackoff();
          setOperatorAuditEntries([]);
          setOperatorAuditError("Unable to load operator audit.");
        } else {
          const operatorAuditData = await safeJson<{
            entries?: OperatorAuditEntry[];
          }>(operatorAuditRes, { entries: [] });
          if (!isMounted) return;
          if (!Array.isArray(operatorAuditData.entries)) {
            setOperatorAuditEntries([]);
            setOperatorAuditError("Operator audit data malformed.");
          } else {
            setOperatorAuditEntries(operatorAuditData.entries);
            setOperatorAuditError(null);
          }
        }

        if (!launchControlRes || !launchControlRes.ok) {
          if (isRateLimitedResponse(launchControlRes)) setRateLimitBackoff();
          setLaunchControl(null);
          setLaunchControlError("Launch control unavailable.");
        } else {
          const launchControlData = await safeJson<LaunchControlReadinessResponse>(
            launchControlRes,
            {}
          );
          if (!isMounted) return;
          const hasExpectedShape =
            typeof launchControlData?.overallReady === "boolean" &&
            typeof launchControlData?.blockerCount === "number" &&
            Array.isArray(launchControlData?.nextActions);
          if (!hasExpectedShape) {
            setLaunchControl(null);
            setLaunchControlError("Launch control data malformed.");
          } else {
            setLaunchControl(launchControlData);
            setLaunchControlError(null);
          }
        }

        if (!week1StabilizationRes || !week1StabilizationRes.ok) {
          if (isRateLimitedResponse(week1StabilizationRes)) setRateLimitBackoff();
          setWeek1Stabilization(null);
          setWeek1StabilizationError("Week-1 stabilization unavailable.");
        } else {
          const week1Data = await safeJson<Week1StabilizationResponse>(
            week1StabilizationRes,
            {}
          );
          if (!isMounted) return;
          const hasExpectedShape =
            Array.isArray(week1Data?.topProbes) &&
            Array.isArray(week1Data?.actionCues) &&
            typeof week1Data?.unresolvedBlockers?.count === "number";
          if (!hasExpectedShape) {
            setWeek1Stabilization(null);
            setWeek1StabilizationError("Week-1 stabilization data malformed.");
          } else {
            setWeek1Stabilization(week1Data);
            setWeek1StabilizationError(null);
          }
        }
        
        if (!revenueRecoveryRes || !revenueRecoveryRes.ok) {
          if (isRateLimitedResponse(revenueRecoveryRes)) setRateLimitBackoff();
          setRevenueRecovery(null);
          setRevenueRecoveryError("Revenue recovery proof unavailable.");
        } else {
          const revenueRecoveryData = await safeJson<RevenueRecoveryResponse>(
            revenueRecoveryRes,
            {}
          );
          if (!isMounted) return;
          const hasExpectedShape =
            typeof revenueRecoveryData?.abandonedBooking?.overdueCount === "number" &&
            typeof revenueRecoveryData?.reactivation?.cohortSummary?.eligibleCount === "number";
          if (!hasExpectedShape) {
            setRevenueRecovery(null);
            setRevenueRecoveryError("Revenue recovery proof data malformed.");
          } else {
            setRevenueRecovery(revenueRecoveryData);
            setRevenueRecoveryError(null);
          }
        }
        setRevenueRecoveryLoading(false);

        if (!moduleMatrixRes || !moduleMatrixRes.ok) {
          if (isRateLimitedResponse(moduleMatrixRes)) setRateLimitBackoff();
          setModuleMatrix(null);
          setModuleMatrixError("Module matrix unavailable.");
        } else {
          const moduleMatrixData = await safeJson<ModuleMatrixResponse>(moduleMatrixRes, {});
          if (!isMounted) return;
          if (!Array.isArray(moduleMatrixData?.modules)) {
            setModuleMatrix(null);
            setModuleMatrixError("Module matrix data malformed.");
          } else {
            setModuleMatrix(moduleMatrixData);
            setModuleMatrixError(null);
          }
        }

        if (!playbookActiveSummaryRes || !playbookActiveSummaryRes.ok) {
          if (isRateLimitedResponse(playbookActiveSummaryRes)) setRateLimitBackoff();
          setPlaybookActiveSummary(null);
          setPlaybookActiveSummaryError("Active playbook summary unavailable.");
        } else {
          const playbookActiveData = await safeJson<PlaybookActiveSummaryResponse>(
            playbookActiveSummaryRes,
            {}
          );
          if (!isMounted) return;
          if (!Array.isArray(playbookActiveData?.blockKeys)) {
            setPlaybookActiveSummary(null);
            setPlaybookActiveSummaryError("Active playbook summary malformed.");
          } else {
            setPlaybookActiveSummary(playbookActiveData);
            setPlaybookActiveSummaryError(null);
          }
        }

        if (!canFetchPaymentExceptions) {
          setPaymentExceptions(null);
          setPaymentExceptionsError(null);
        } else if (!paymentExceptionsRes || !paymentExceptionsRes.ok) {
          if (isRateLimitedResponse(paymentExceptionsRes)) setRateLimitBackoff();
          setPaymentExceptions(null);
          setPaymentExceptionsError("Unable to load payment recovery exceptions.");
        } else {
          const paymentExceptionsData = await safeJson<PaymentExceptionsResponse>(
            paymentExceptionsRes,
            {}
          );
          if (!isMounted) return;
          if (!Array.isArray(paymentExceptionsData?.items)) {
            setPaymentExceptions(null);
            setPaymentExceptionsError("Payment recovery data malformed.");
          } else {
            setPaymentExceptions(paymentExceptionsData);
            setPaymentExceptionsError(null);
          }
        }

        if (!canFetchPaymentExceptions) {
          setPaymentFollowups(null);
          setPaymentFollowupsError(null);
        } else if (!paymentFollowupsRes || !paymentFollowupsRes.ok) {
          if (isRateLimitedResponse(paymentFollowupsRes)) setRateLimitBackoff();
          setPaymentFollowups(null);
          setPaymentFollowupsError("Unable to load payment follow-up proof.");
        } else {
          const paymentFollowupsData = await safeJson<PaymentFollowupOpsResponse>(
            paymentFollowupsRes,
            {}
          );
          if (!isMounted) return;
          if (!Array.isArray(paymentFollowupsData?.items)) {
            setPaymentFollowups(null);
            setPaymentFollowupsError("Payment follow-up proof data malformed.");
          } else {
            setPaymentFollowups(paymentFollowupsData);
            setPaymentFollowupsError(null);
          }
        }

        if (!canFetchPaymentExceptions) {
          setPaymentCaptureOutcomes(null);
          setPaymentCaptureOutcomesError(null);
        } else if (!paymentCaptureOutcomesRes || !paymentCaptureOutcomesRes.ok) {
          if (isRateLimitedResponse(paymentCaptureOutcomesRes)) setRateLimitBackoff();
          setPaymentCaptureOutcomes(null);
          setPaymentCaptureOutcomesError("Unable to load payment capture proof.");
        } else {
          const paymentCaptureOutcomesData =
            await safeJson<PaymentCaptureOutcomesResponse>(
              paymentCaptureOutcomesRes,
              {}
            );
          if (!isMounted) return;
          if (!Array.isArray(paymentCaptureOutcomesData?.items)) {
            setPaymentCaptureOutcomes(null);
            setPaymentCaptureOutcomesError("Payment capture proof data malformed.");
          } else {
            setPaymentCaptureOutcomes(paymentCaptureOutcomesData);
            setPaymentCaptureOutcomesError(null);
          }
        }

        if (!voiceOpsRecentRes || !voiceOpsRecentRes.ok) {
          if (isRateLimitedResponse(voiceOpsRecentRes)) setRateLimitBackoff();
          setVoiceOpsCalls([]);
          setVoiceOpsError("Unable to load voice ops.");
        } else {
          const voiceOpsData = await safeJson<{ calls?: VoiceOpsCall[] }>(
            voiceOpsRecentRes,
            { calls: [] }
          );
          if (!isMounted) return;
          if (!Array.isArray(voiceOpsData.calls)) {
            setVoiceOpsCalls([]);
            setVoiceOpsError("Voice ops data malformed.");
          } else {
            setVoiceOpsCalls(voiceOpsData.calls);
            setVoiceOpsError(null);
          }
        }

        if (!outboundSchedulingMetricsRes || !outboundSchedulingMetricsRes.ok) {
          if (isRateLimitedResponse(outboundSchedulingMetricsRes)) setRateLimitBackoff();
          setOutboundSchedulingMetrics(null);
          setOutboundSchedulingError("Unable to load outbound scheduling metrics.");
        } else {
          const outboundSchedulingData = await safeJson<OutboundSchedulingMetricsResponse>(
            outboundSchedulingMetricsRes,
            {}
          );
          if (!isMounted) return;
          setOutboundSchedulingMetrics(outboundSchedulingData || null);
          setOutboundSchedulingError(null);
        }

        if (!canFetchSchedulingExceptions) {
          setSchedulingExceptions([]);
          setSchedulingExceptionsError(null);
          setSchedulingSweepRuns([]);
          setSchedulingSweepRunsError(null);
          setOutboundVoiceSweepRuns([]);
          setOutboundVoiceSweepRunsError(null);
          setOutboundVoiceSweepHealth(null);
          setOutboundVoiceSweepHealthError(null);
        } else if (!schedulingExceptionsRes || !schedulingExceptionsRes.ok) {
          if (isRateLimitedResponse(schedulingExceptionsRes)) setRateLimitBackoff();
          setSchedulingExceptions([]);
          setSchedulingExceptionsError("Unable to load scheduling exceptions.");
        } else {
          const schedulingExceptionsData = await safeJson<{
            items?: SchedulingExceptionItem[];
          }>(schedulingExceptionsRes, { items: [] });
          if (!isMounted) return;
          setSchedulingExceptions(
            Array.isArray(schedulingExceptionsData.items)
              ? schedulingExceptionsData.items
              : []
          );
          setSchedulingExceptionsError(null);
        }

        if (!canFetchSchedulingExceptions) {
          setSchedulingSweepRuns([]);
          setSchedulingSweepRunsError(null);
        } else if (!schedulingSweepRunsRes || !schedulingSweepRunsRes.ok) {
          if (isRateLimitedResponse(schedulingSweepRunsRes)) setRateLimitBackoff();
          setSchedulingSweepRuns([]);
          setSchedulingSweepRunsError("Unable to load sweep runs.");
        } else {
          const schedulingSweepRunsData = await safeJson<{
            runs?: SchedulingSweepRun[];
          }>(schedulingSweepRunsRes, { runs: [] });
          if (!isMounted) return;
          setSchedulingSweepRuns(
            Array.isArray(schedulingSweepRunsData.runs)
              ? schedulingSweepRunsData.runs
              : []
          );
          setSchedulingSweepRunsError(null);
        }

        if (!canFetchSchedulingExceptions) {
          setOutboundVoiceSweepRuns([]);
          setOutboundVoiceSweepRunsError(null);
          setOutboundVoiceSweepHealth(null);
          setOutboundVoiceSweepHealthError(null);
        } else if (!outboundVoiceSweepRunsRes || !outboundVoiceSweepRunsRes.ok) {
          if (isRateLimitedResponse(outboundVoiceSweepRunsRes)) {
            setRateLimitBackoff();
          }
          setOutboundVoiceSweepRuns([]);
          setOutboundVoiceSweepRunsError("Unable to load outbound voice sweep runs.");
        } else {
          const outboundVoiceSweepRunsData = await safeJson<{
            runs?: OutboundVoiceSweepRun[];
          }>(outboundVoiceSweepRunsRes, { runs: [] });
          if (!isMounted) return;
          setOutboundVoiceSweepRuns(
            Array.isArray(outboundVoiceSweepRunsData.runs)
              ? outboundVoiceSweepRunsData.runs
              : []
          );
          setOutboundVoiceSweepRunsError(null);
        }

        if (!canFetchSchedulingExceptions) {
          setOutboundVoiceSweepHealth(null);
          setOutboundVoiceSweepHealthError(null);
        } else if (!outboundVoiceSweepHealthRes || !outboundVoiceSweepHealthRes.ok) {
          if (isRateLimitedResponse(outboundVoiceSweepHealthRes)) {
            setRateLimitBackoff();
          }
          setOutboundVoiceSweepHealth(null);
          setOutboundVoiceSweepHealthError("Unable to load outbound voice sweep health.");
        } else {
          const outboundVoiceSweepHealthData = await safeJson<OutboundVoiceSweepHealth>(
            outboundVoiceSweepHealthRes,
            {}
          );
          if (!isMounted) return;
          setOutboundVoiceSweepHealth(outboundVoiceSweepHealthData || null);
          setOutboundVoiceSweepHealthError(null);
        }
      } catch (err) {
        if (!isMounted) return;
        setMetricsError("Unable to load metrics.");
        console.error("Failed to refresh dashboard", err);
      } finally {
        refreshInFlightRef.current = false;
        if (!isMounted) return;

        setMetricsLoading(false);
        setExternalVisibilityLoading(false);
        setSalesOfferClarityLoading(false);
        setLaunchControlLoading(false);
        setWeek1StabilizationLoading(false);
        setModuleMatrixLoading(false);
        setPlaybookActiveSummaryLoading(false);
        setPaymentExceptionsLoading(false);
        setPaymentFollowupsLoading(false);
        setPaymentCaptureOutcomesLoading(false);
        setCallbacksLoading(false);
        setCallbackRequestQueueLoading(false);
        setVoiceOpsLoading(false);
        setEscalationsLoading(false);
        setEscalationDeliveriesLoading(false);
        setOutboundSchedulingLoading(false);
        setSchedulingExceptionsLoading(false);
        setSchedulingSweepRunsLoading(false);
        setOutboundVoiceSweepRunsLoading(false);
        setOutboundVoiceSweepHealthLoading(false);
        setOperatorAuditLoading(false);

        const backoffDelay = Math.max(0, backoffUntilRef.current - Date.now());
        scheduleNext(backoffDelay > 0 ? backoffDelay : POLL_INTERVAL_MS);
      }
    };

    void refreshDashboard();

    return () => {
      isMounted = false;
      if (pollTimeout) clearTimeout(pollTimeout);
    };
  }, [
    canViewInternalMetrics,
    hasOperatorToken,
    operatorRole,
    operatorToken,
    outboundSchedulingSnapshotDays,
    refreshKey,
    schedulingExceptionsBucketFilter,
    tokenBootstrapReady,
  ]);

  const applyCallbackRequestAction = async (
    callbackId: string | undefined,
    status: QueueStatusUpdateTarget,
    options?: {
      actionKey?: string;
      successMessage?: string;
    }
  ) => {
    if (!canViewInternalMetrics || !callbackId) return;
    const actionKey = options?.actionKey || `${callbackId}:${status}`;
    setCallbackRequestActionKey(actionKey);
    setCallbackRequestActionMessage(null);
    setCallbackRequestActionError(null);
    try {
      const res = await apiFetch(
        `/api/internal/callback-requests/${encodeURIComponent(callbackId)}/status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );
      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }
      if (res?.status === 409) {
        const conflictBody = await safeJson<{
          error?: string;
          handledBy?: string;
          status?: string;
        }>(res, {});
        if (conflictBody.error === "assigned_to_other") {
          console.warn(
            `[callback-requests] assigned to ${conflictBody.handledBy || "another operator"}`
          );
        }
      }
      if (!res || (!res.ok && res.status !== 409)) {
        throw new Error(`Callback status update failed: ${status}`);
      }
      setCallbackRequestActionMessage(
        options?.successMessage ||
          `Callback request marked ${formatCallbackQueueStatus(status)}.`
      );
    } catch (err) {
      console.error(`Failed to update callback request status: ${status}`, err);
      setCallbackRequestActionError("Unable to update callback request status.");
    } finally {
      try {
        const queue = await readCallbackRequestQueue();
        setCallbackRequestQueue(queue);
      } catch {
        setCallbackRequestQueue([]);
      }
      setCallbackRequestActionKey(null);
    }
  };

  const handleMarkCallbackCompleted = async (callbackId: string | undefined) => {
    if (!canViewInternalMetrics || !callbackId || isClientViewerMode) return;
    const actionKey = `${callbackId}:mark_completed`;
    setCallbackRequestActionKey(actionKey);
    try {
      const res = await apiFetch(
        `/api/internal/callbacks/${encodeURIComponent(callbackId)}/mark-staff-handled`,
        {
          method: "POST",
        }
      );
      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }
      if (!res || (!res.ok && res.status !== 409)) {
        throw new Error("Callback mark completed failed");
      }
    } catch (err) {
      console.error("Failed to mark callback request completed", err);
    } finally {
      try {
        const queue = await readCallbackRequestQueue();
        setCallbackRequestQueue(queue);
      } catch {
        setCallbackRequestQueue([]);
      }
      setCallbackRequestActionKey(null);
    }
  };

  const handleCallNow = async (callbackId: string | undefined) => {
    if (!canViewInternalMetrics || !callbackId || isClientViewerMode) return;
    const actionKey = `${callbackId}:call_now`;
    setCallbackRequestActionKey(actionKey);
    try {
      const res = await apiFetch(`/api/callbacks/${encodeURIComponent(callbackId)}/ai-call`, {
        method: "POST",
      });

      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }

      if (!res) {
        setCallbackCallNowFeedback((current) => ({
          ...current,
          [callbackId]: "Call request failed.",
        }));
        return;
      }

      if (res.ok) {
        const body = await safeJson<{ callSid?: string | null; idempotent?: boolean }>(
          res,
          {}
        );
        const callSid = typeof body.callSid === "string" ? body.callSid.trim() : "";
        const message = callSid
          ? `Call started: ${callSid}`
          : body.idempotent
            ? "Call already in progress."
            : "Call started.";
        setCallbackCallNowFeedback((current) => ({
          ...current,
          [callbackId]: message,
        }));
      } else if (res.status === 409) {
        const body = await safeJson<{ error?: string; retryAt?: string }>(res, {});
        let message = "Call request rejected.";
        if (body.error === "retry_backoff") {
          message = body.retryAt
            ? `Retry available at ${formatTimestamp(body.retryAt, body.retryAt)}`
            : "Retry backoff active.";
        } else if (body.error === "too_many_attempts") {
          message = "Max attempts reached.";
        } else if (body.error === "already_reached") {
          message = "Already reached.";
        } else if (typeof body.error === "string" && body.error.trim()) {
          message = body.error;
        }
        setCallbackCallNowFeedback((current) => ({
          ...current,
          [callbackId]: message,
        }));
      } else if (res.status === 500 || res.status === 502) {
        setCallbackCallNowFeedback((current) => ({
          ...current,
          [callbackId]: "Call failed. Try again.",
        }));
      } else {
        setCallbackCallNowFeedback((current) => ({
          ...current,
          [callbackId]: "Call request failed.",
        }));
      }
    } catch (err) {
      console.error("Failed to start outbound callback", err);
      setCallbackCallNowFeedback((current) => ({
        ...current,
        [callbackId]: "Call request failed.",
      }));
    } finally {
      try {
        const queue = await readCallbackRequestQueue();
        setCallbackRequestQueue(queue);
      } catch {
        setCallbackRequestQueue([]);
      }
      setCallbackRequestActionKey(null);
    }
  };

  const handleAlertAction = async ({
    alertId,
    action,
  }: {
    alertId: string | undefined;
    action: "acknowledge" | "resolve";
  }) => {
    const normalizedId = String(alertId || "").trim();
    if (!canViewInternalMetrics || !normalizedId || isClientViewerMode) return;

    const actionKey = `${normalizedId}:${action}`;
    setAlertActionKey(actionKey);
    try {
      const resolveNote = alertResolveNotes[normalizedId]?.trim();
      const shouldSendNote = action === "resolve" && Boolean(resolveNote);
      const res = await apiFetch(
        `/api/internal/alerts/${encodeURIComponent(normalizedId)}/${action}`,
        {
          method: "POST",
          headers: shouldSendNote ? { "Content-Type": "application/json" } : undefined,
          body: shouldSendNote ? JSON.stringify({ note: resolveNote }) : undefined,
        }
      );

      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }

      if (!res || (!res.ok && res.status !== 409)) {
        throw new Error(`Alert ${action} failed`);
      }

      if (action === "resolve") {
        setAlertResolveNotes((prev) => ({ ...prev, [normalizedId]: "" }));
      }
      triggerManualRefresh();
    } catch (err) {
      console.error(`Failed to ${action} alert`, err);
    } finally {
      setAlertActionKey(null);
    }
  };

  const handleAlertDeliveryRetry = async (alertId: string | undefined) => {
    const normalizedId = String(alertId || "").trim();
    if (!canViewInternalMetrics || !normalizedId || isClientViewerMode) return;

    const actionKey = `${normalizedId}:delivery_retry`;
    setAlertActionKey(actionKey);
    try {
      const res = await apiFetch(
        `/api/internal/alerts/${encodeURIComponent(normalizedId)}/delivery/retry`,
        { method: "POST" }
      );

      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }

      if (!res || (!res.ok && res.status !== 409)) {
        throw new Error("Alert delivery retry failed");
      }

      if (res.status === 409) {
        console.warn("[alerts] delivery retry not eligible");
      }

      triggerManualRefresh();
    } catch (err) {
      console.error("Failed to retry alert delivery", err);
      triggerManualRefresh();
    } finally {
      setAlertActionKey(null);
    }
  };

  const handleResetEscalationBreaker = async () => {
    if (!canViewInternalMetrics || isClientViewerMode || operatorRole !== "admin") {
      return;
    }
    setEscalationsResetting(true);
    try {
      const res = await apiFetch("/api/internal/escalations/breaker/reset", {
        method: "POST",
      });
      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }
      if (!res || !res.ok) {
        throw new Error("Escalation breaker reset failed");
      }
      setEscalationsError(null);
      triggerManualRefresh();
    } catch (err) {
      setEscalationsError("Unable to reset escalation breaker.");
      console.error("Failed to reset escalation breaker", err);
    } finally {
      setEscalationsResetting(false);
    }
  };

  const handleResetEscalationBackoff = async () => {
    if (!canViewInternalMetrics || isClientViewerMode || operatorRole !== "admin") {
      return;
    }
    setEscalationsBackoffResetting(true);
    try {
      const res = await apiFetch("/api/internal/escalations/backoff/reset", {
        method: "POST",
      });
      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }
      if (!res || !res.ok) {
        throw new Error("Escalation backoff reset failed");
      }
      setEscalationsError(null);
      triggerManualRefresh();
    } catch (err) {
      setEscalationsError("Unable to reset escalation backoff.");
      console.error("Failed to reset escalation backoff", err);
    } finally {
      setEscalationsBackoffResetting(false);
    }
  };

  const handleOpenVoiceTimeline = async (callSid: string | undefined) => {
    const normalizedCallSid = (callSid || "").trim();
    if (!canViewInternalMetrics || !normalizedCallSid) return;

    setVoiceTimelineCallSid(normalizedCallSid);
    setVoiceTimelineLoading(true);
    setVoiceTimelineError(null);
    setVoiceTimelineEvents([]);

    try {
      const res = await apiFetch(
        `/api/internal/voice/${encodeURIComponent(normalizedCallSid)}/timeline`
      );
      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        const message = tokenErrorMessage || INTERNAL_METRICS_MESSAGE;
        setOperatorAccessError(message);
        setVoiceTimelineError(message);
        return;
      }
      if (!res || !res.ok) {
        throw new Error("Voice timeline request failed");
      }

      const data = await safeJson<{ events?: VoiceTimelineEvent[] }>(res, {
        events: [],
      });
      setVoiceTimelineEvents(Array.isArray(data.events) ? data.events : []);
    } catch {
      setVoiceTimelineEvents([]);
      setVoiceTimelineError("Unable to load voice timeline.");
    } finally {
      setVoiceTimelineLoading(false);
    }
  };

  const handleOpenEscalationCorrelation = async (
    correlationIdValue: string | null | undefined
  ) => {
    const normalizedCorrelationId = String(correlationIdValue || "").trim();
    if (
      !canViewInternalMetrics ||
      isClientViewerMode ||
      !normalizedCorrelationId
    ) {
      return;
    }

    setEscalationCorrelationId(normalizedCorrelationId);
    setEscalationCorrelationLoading(true);
    setEscalationCorrelationError(null);
    setEscalationCorrelationExternalIncident(null);
    setEscalationCorrelationDeliveries([]);
    setEscalationCorrelationEvents([]);

    try {
      const res = await apiFetch(
        `/api/internal/escalations/correlation/${encodeURIComponent(
          normalizedCorrelationId
        )}`
      );

      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        const message = tokenErrorMessage || INTERNAL_METRICS_MESSAGE;
        setOperatorAccessError(message);
        setEscalationCorrelationError(message);
        return;
      }

      if (!res || !res.ok) {
        throw new Error("Escalation correlation drilldown request failed");
      }

      const data = await safeJson<EscalationCorrelationDrilldownResponse>(res, {});
      setEscalationCorrelationExternalIncident(data.externalIncident || null);
      setEscalationCorrelationDeliveries(
        Array.isArray(data.deliveries) ? data.deliveries : []
      );
      setEscalationCorrelationEvents(Array.isArray(data.events) ? data.events : []);
    } catch {
      setEscalationCorrelationExternalIncident(null);
      setEscalationCorrelationDeliveries([]);
      setEscalationCorrelationEvents([]);
      setEscalationCorrelationError("Unable to load correlation drilldown.");
    } finally {
      setEscalationCorrelationLoading(false);
    }
  };

  const handleCloseEscalationCorrelation = () => {
    setEscalationCorrelationId(null);
    setEscalationCorrelationExternalIncident(null);
    setEscalationCorrelationDeliveries([]);
    setEscalationCorrelationEvents([]);
    setEscalationCorrelationLoading(false);
    setEscalationCorrelationError(null);
    setEscalationCorrelationRetryingId(null);
  };

  const handleRetryEscalationDelivery = async (
    deliveryIdValue: string | undefined
  ) => {
    const normalizedDeliveryId = String(deliveryIdValue || "").trim();
    const canRetry =
      canViewInternalMetrics &&
      !isClientViewerMode &&
      (operatorRole === "admin" || operatorRole === "operator");
    if (!normalizedDeliveryId || !canRetry) return;

    setEscalationCorrelationRetryingId(normalizedDeliveryId);
    let shouldRefresh = false;

    try {
      const res = await apiFetch(
        `/api/internal/escalations/deliveries/${encodeURIComponent(
          normalizedDeliveryId
        )}/retry`,
        { method: "POST" }
      );

      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        const message = tokenErrorMessage || INTERNAL_METRICS_MESSAGE;
        setOperatorAccessError(message);
        setEscalationCorrelationError(message);
        return;
      }

      if (res?.status === 409) {
        console.warn("Escalation delivery retry is not eligible", {
          deliveryId: normalizedDeliveryId,
        });
        shouldRefresh = true;
        return;
      }

      if (!res || !res.ok) {
        throw new Error("Escalation delivery retry failed");
      }

      shouldRefresh = true;
    } catch {
      setEscalationCorrelationError("Unable to retry escalation delivery.");
    } finally {
      if (shouldRefresh && escalationCorrelationId) {
        await handleOpenEscalationCorrelation(escalationCorrelationId);
      }
      setEscalationCorrelationRetryingId(null);
    }
  };

  const handleSchedulingExceptionAction = async (
    callbackIdValue: string | undefined,
    action: "resend-link" | "mark-needs-staff"
  ) => {
    const callbackId = String(callbackIdValue || "").trim();
    if (!canViewInternalMetrics || !callbackId || isClientViewerMode || !hasOperatorToken) {
      return;
    }

    const actionKey = `${callbackId}:${action}`;
    setSchedulingExceptionsActionKey(actionKey);
    setSchedulingExceptionsToast(null);
    try {
      const res = await apiFetch(
        `/api/internal/voice/ops/scheduling/${encodeURIComponent(callbackId)}/${action}`,
        {
          method: "POST",
        }
      );
      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }
      if (!res || !res.ok) {
        const errorBody = await safeJson<{ error?: string }>(res, {});
        throw new Error(errorBody.error || "scheduling_exception_action_failed");
      }

      setSchedulingExceptionsToast({
        type: "success",
        message:
          action === "resend-link"
            ? "Scheduling link resent."
            : "Staff follow-up forced.",
      });
      triggerManualRefresh();
    } catch {
      setSchedulingExceptionsToast({
        type: "error",
        message:
          action === "resend-link"
            ? "Unable to resend scheduling link."
            : "Unable to mark staff follow-up.",
      });
    } finally {
      setSchedulingExceptionsActionKey(null);
    }
  };

  const handleSchedulingExceptionsBulkAction = async (
    action: "resend-bulk" | "force-staff-bulk"
  ) => {
    if (!canManageSchedulingExceptions || schedulingExceptionsBulkAction) return;

    const normalizedMinAgeMinutes = Math.max(
      0,
      Math.floor(Number(schedulingExceptionsMinAgeMinutes) || 0)
    );

    const requestBody =
      schedulingExceptionsBucketFilter !== "all"
        ? {
            ids: schedulingExceptions
              .map((item) => item.id)
              .filter(Boolean)
              .slice(0, SCHEDULING_BULK_ACTION_CAP),
          }
        : {
            minAgeMinutes: normalizedMinAgeMinutes,
            limit: SCHEDULING_BULK_ACTION_CAP,
          };

    if (Array.isArray((requestBody as { ids?: string[] }).ids) && !(requestBody as { ids?: string[] }).ids?.length) {
      setSchedulingExceptionsToast({
        type: "error",
        message: "No matching scheduling exceptions for the selected bucket.",
      });
      return;
    }

    const endpoint =
      action === "resend-bulk"
        ? "/api/internal/voice/ops/scheduling/awaiting-confirmation/resend-bulk"
        : "/api/internal/voice/ops/scheduling/awaiting-confirmation/force-staff-bulk";

    setSchedulingExceptionsBulkAction(action);
    setSchedulingExceptionsToast(null);
    try {
      const res = await apiFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        setOperatorAccessError(tokenErrorMessage || INTERNAL_METRICS_MESSAGE);
        return;
      }
      if (!res || !res.ok) {
        const errorBody = await safeJson<{ error?: string }>(res, {});
        throw new Error(errorBody.error || "scheduling_exception_bulk_action_failed");
      }

      const payload = await safeJson<{
        attempted?: number;
        sent?: number;
        failed?: number;
        skippedCooldown?: number;
        updated?: number;
        skipped?: number;
      }>(res, {});
      if (action === "resend-bulk") {
        const skippedCooldown = safeNumber(payload.skippedCooldown);
        setSchedulingExceptionsToast({
          type: "success",
          message:
            skippedCooldown > 0
              ? `Bulk resend complete. Attempted ${safeNumber(
                  payload.attempted
                )}, sent ${safeNumber(payload.sent)}, failed ${safeNumber(
                  payload.failed
                )}, skipped cooldown ${skippedCooldown}.`
              : `Bulk resend complete. Attempted ${safeNumber(
                  payload.attempted
                )}, sent ${safeNumber(payload.sent)}, failed ${safeNumber(
                  payload.failed
                )}.`,
        });
      } else {
        setSchedulingExceptionsToast({
          type: "success",
          message: `Bulk staff follow-up complete. Attempted ${safeNumber(
            payload.attempted
          )}, updated ${safeNumber(payload.updated)}, skipped ${safeNumber(
            payload.skipped
          )}.`,
        });
      }
      triggerManualRefresh();
    } catch {
      setSchedulingExceptionsToast({
        type: "error",
        message:
          action === "resend-bulk"
            ? "Unable to bulk resend scheduling links."
            : "Unable to bulk force staff follow-up.",
      });
    } finally {
      setSchedulingExceptionsBulkAction(null);
    }
  };

  const handleExportSchedulingExceptionsCsv = () => {
    if (!canManageSchedulingExceptions || schedulingExceptionsExporting) return;

    setSchedulingExceptionsExporting(true);
    setSchedulingExceptionsToast(null);
    try {
      const params = new URLSearchParams({
        format: "csv",
        snapshotDays: String(SCHEDULING_EXCEPTIONS_SNAPSHOT_DAYS),
      });
      if (schedulingExceptionsBucketFilter !== "all") {
        params.set("bucket", schedulingExceptionsBucketFilter);
      }
      const exportPath = `/api/internal/voice/ops/scheduling/awaiting-confirmation/export?${params.toString()}`;
      if (typeof window !== "undefined" && typeof window.open === "function") {
        window.open(withVetcanApiBase(exportPath), "_blank", "noopener,noreferrer");
      }
      setSchedulingExceptionsToast({
        type: "success",
        message: "CSV export opened.",
      });
    } catch {
      setSchedulingExceptionsToast({
        type: "error",
        message: "Unable to export scheduling exceptions.",
      });
    } finally {
      setSchedulingExceptionsExporting(false);
    }
  };

  const handleCloseVoiceTimeline = () => {
    setVoiceTimelineCallSid(null);
    setVoiceTimelineEvents([]);
    setVoiceTimelineLoading(false);
    setVoiceTimelineError(null);
  };

  const handleCopyCallSid = async (callSid: string | undefined) => {
    const normalizedCallSid = (callSid || "").trim();
    if (!normalizedCallSid) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalizedCallSid);
      }
    } catch {
      // Clipboard support is best-effort in browser contexts.
    }
  };

  const handleClinicProfileFieldChange = (
    field: ClinicProfileField,
    value: string
  ) => {
    setClinicProfileForm((current) => ({
      ...current,
      [field]: value,
    }));
    setClinicProfileMessage(null);
  };

  const loadClinicProfile = async (isActive: () => boolean = () => true) => {
    setClinicProfileLoading(true);
    setClinicProfileError(null);
    setClinicProfileMessage(null);
    try {
      const res = await apiFetch("/api/clinic-profile");
      if (!isActive()) return;
      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        const message = tokenErrorMessage || INTERNAL_METRICS_MESSAGE;
        if (!isActive()) return;
        setOperatorAccessError(message);
        setClinicProfileError(message);
        return;
      }
      if (!res || !res.ok) {
        throw new Error("clinic_profile_load_failed");
      }

      const payload = await safeJson<unknown>(res, null);
      if (!isActive()) return;
      if (!isClinicProfilePayload(payload)) {
        throw new Error("clinic_profile_payload_invalid");
      }

      setClinicProfileTenantKey(payload.tenantKey);
      setClinicProfileForm(toClinicProfileFormValues(payload));
      setClinicProfileError(null);
    } catch {
      if (!isActive()) return;
      setClinicProfileError("Unable to load clinic settings.");
    } finally {
      if (isActive()) {
        setClinicProfileLoading(false);
      }
    }
  };

  const handleSaveClinicProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManageClinicSettings || clinicProfileSaving) return;

    const updates = {} as ClinicProfileFormValues;
    let hasBlankValue = false;
    for (const field of CLINIC_PROFILE_FIELDS) {
      const trimmedValue = clinicProfileForm[field.key].trim();
      updates[field.key] = trimmedValue;
      if (!trimmedValue) hasBlankValue = true;
    }

    if (hasBlankValue) {
      setClinicProfileMessage(null);
      setClinicProfileError("All clinic settings fields are required.");
      return;
    }

    setClinicProfileSaving(true);
    setClinicProfileError(null);
    setClinicProfileMessage(null);

    try {
      const res = await apiFetch("/api/internal/clinic-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });

      if (isUnauthorizedResponse(res)) {
        const tokenErrorMessage = await resolveTokenAccessErrorMessage({
          token: operatorToken,
          unauthorizedResponses: [res],
        });
        const message = tokenErrorMessage || INTERNAL_METRICS_MESSAGE;
        setOperatorAccessError(message);
        setClinicProfileError(message);
        return;
      }

      if (!res || !res.ok) {
        setClinicProfileError("Unable to save clinic settings.");
        return;
      }

      const payload = await safeJson<{ profile?: unknown }>(res, {});
      if (!isClinicProfilePayload(payload.profile)) {
        setClinicProfileError("Clinic settings saved, but response was invalid.");
        return;
      }

      setClinicProfileTenantKey(payload.profile.tenantKey);
      setClinicProfileForm(toClinicProfileFormValues(payload.profile));
      setClinicProfileMessage("Clinic settings saved.");
    } catch {
      setClinicProfileError("Unable to save clinic settings.");
    } finally {
      setClinicProfileSaving(false);
    }
  };

  const uptime = metrics?.uptimeSeconds ?? 0;
  const hours = Math.floor(uptime / 3600);
  const minutes = Math.floor((uptime % 3600) / 60);
  const seconds = Math.floor(uptime % 60);
  const uptimeLabel = `${hours}h ${minutes}m ${seconds}s`;
  const callbackRows = Array.isArray(callbacks) ? callbacks : [];
  const callbackRequestRows = Array.isArray(callbackRequestQueue)
    ? callbackRequestQueue
    : [];
  const voiceOpsRows = Array.isArray(voiceOpsCalls) ? voiceOpsCalls : [];
  const alertRows = Array.isArray(alerts) ? alerts : [];
  const recentEventRows = Array.isArray(recentEvents) ? recentEvents : [];
  const metricsActiveAlerts = Array.isArray(metrics?.activeAlerts)
    ? metrics.activeAlerts
    : [];
  const activeAlertCount =
    metrics?.alertOverview?.totalActive ?? metricsActiveAlerts.length;
  const ackTimeline = Array.isArray(metrics?.ackTimeline) ? metrics.ackTimeline : [];
  const sla = metrics?.sla;
  const alertOverview = metrics?.alertOverview;
  const acknowledgedActive = alertOverview
    ? Math.max(alertOverview.totalActive - alertOverview.unacknowledgedActive, 0)
    : 0;

  const eventEntries = Object.entries(metrics?.eventCounts || {}).sort(
    (a, b) => b[1] - a[1]
  );

  const alertEvents = recentEventRows.filter(event =>
    ["alert_triggered", "alert_resolved", "alert_acknowledged"].includes(
      event.type
    )
  );
  const voiceEscalations = recentEventRows.filter(
    (event) => event.type === "voice_escalation_requested"
  );

  const resolvedAlertKeys = new Set(
    recentEventRows
      .filter(event => event.type === "alert_resolved")
      .map(event => `${event.payload?.alertType || ""}:${event.payload?.eventName || ""}`)
  );
  const acknowledgedAlertKeys = new Set(
    recentEventRows
      .filter(event => event.type === "alert_acknowledged")
      .map(event => `${event.payload?.alertType || ""}:${event.payload?.eventName || ""}`)
  );

  function getAlertStatus(
    alertType: string,
    eventName: string,
    acknowledgedAt?: string
  ) {
    const key = `${alertType}:${eventName}`;
    if (acknowledgedAt && resolvedAlertKeys.has(key)) return "Resolved";
    if (acknowledgedAt) return "Acknowledged";
    if (resolvedAlertKeys.has(key)) return "Resolved";
    return "Active";
  }

  function getResolvedLabel(
    alertType: string,
    eventName: string,
    acknowledgedAt?: string
  ) {
    const key = `${alertType}:${eventName}`;
    if (!resolvedAlertKeys.has(key)) return null;
    return acknowledgedAt ? "Resolved (after acknowledgment)" : "Auto-resolved";
  }

  const pendingCount = callbackRows.filter(c => c.status === "pending").length;

  const filteredCallbacks = callbackRows
    .filter(cb =>
      `${cb.name} ${cb.phone}`.toLowerCase().includes(query.toLowerCase())
    )
    .filter(cb => {
      switch (filterMode) {
        case "pending":
          return cb.status === "pending";
        case "ai":
          return cb.aiHandled === true;
        case "staff":
          return cb.staffFollowupRequired === true;
        default:
          return true;
      }
    });

  const aiAuditEntries = callbackRows
    .filter((cb) => cb.aiHandled || cb.aiOutcome);

  const unifiedAuditEntries = useMemo(() => {
    return buildUnifiedAuditRows({
      operatorEntries: operatorAuditEntries,
      callbacks: aiAuditEntries,
      environment: metrics?.environment || "unknown",
    });
  }, [operatorAuditEntries, aiAuditEntries, metrics?.environment]);
  const isClientViewerMode = Boolean(metrics?.isClientViewer || status?.isClientViewer);
  const canResetEscalationBreaker =
    canViewInternalMetrics && !isClientViewerMode && operatorRole === "admin";
  const canResetEscalationBackoff =
    canViewInternalMetrics && !isClientViewerMode && operatorRole === "admin";
  const canRetryEscalationDeliveries =
    canViewInternalMetrics &&
    !isClientViewerMode &&
    (operatorRole === "admin" || operatorRole === "operator");
  const canManageSchedulingExceptions =
    canViewInternalMetrics &&
    !isClientViewerMode &&
    hasOperatorToken &&
    (operatorRole === "admin" || operatorRole === "operator");
  const canManageClinicSettings =
    canViewInternalMetrics &&
    !isClientViewerMode &&
    hasOperatorToken &&
    (operatorRole === "admin" || operatorRole === "operator");

  useEffect(() => {
    if (!advancedOpen || !canManageClinicSettings) return;
    let active = true;
    void loadClinicProfile(() => active);
    return () => {
      active = false;
    };
  }, [advancedOpen, canManageClinicSettings, refreshKey]);

  const outboundSchedulingSnapshotOptions: Array<7 | 14 | 30> = [7, 14, 30];
  const outboundSchedulingLinksSent = safeNumber(
    outboundSchedulingMetrics?.scheduling?.linksSent
  );
  const outboundSchedulingBookingsConfirmed = safeNumber(
    outboundSchedulingMetrics?.scheduling?.bookingsConfirmed
  );
  const outboundSchedulingDeflectionRate = formatPercentOneDecimal(
    outboundSchedulingMetrics?.scheduling?.deflectionRate
  );
  const outboundSchedulingEstimatedMinutes = safeNumber(
    outboundSchedulingMetrics?.scheduling?.staffSaves?.estimatedMinutes
  );
  const outboundSchedulingAvgResolution = formatDurationLabel(
    outboundSchedulingMetrics?.scheduling?.timeToResolutionSeconds?.avg
  );
  const outboundSchedulingP50Resolution = formatDurationLabel(
    outboundSchedulingMetrics?.scheduling?.timeToResolutionSeconds?.p50
  );
  const outboundSchedulingP90Resolution = formatDurationLabel(
    outboundSchedulingMetrics?.scheduling?.timeToResolutionSeconds?.p90
  );
  const voiceBookingRequestsTotal = safeNumber(
    outboundSchedulingMetrics?.voiceBookingRequests?.total
  );
  const voiceBookingAfterHoursCount = safeNumber(
    outboundSchedulingMetrics?.voiceBookingRequests?.afterHoursCount
  );
  const voiceBookingAfterHoursPercent = formatPercentOneDecimal(
    outboundSchedulingMetrics?.voiceBookingRequests?.afterHoursPercent
  );
  const voiceBookingOutcomePending = safeNumber(
    outboundSchedulingMetrics?.voiceBookingRequests?.byOutcome?.pending
  );
  const voiceBookingOutcomeConfirmed = safeNumber(
    outboundSchedulingMetrics?.voiceBookingRequests?.byOutcome?.confirmed
  );
  const voiceBookingOutcomeCancelled = safeNumber(
    outboundSchedulingMetrics?.voiceBookingRequests?.byOutcome?.cancelled
  );
  const awaitingConfirmationCount = canManageSchedulingExceptions
    ? schedulingExceptions.length
    : 0;
  const latestResendBulkSweepRun = useMemo(
    () => schedulingSweepRuns.find((run) => run?.action === "resend_bulk") || null,
    [schedulingSweepRuns]
  );
  const latestForceStaffSweepRun = useMemo(
    () =>
      schedulingSweepRuns.find((run) => run?.action === "force_staff_bulk") || null,
    [schedulingSweepRuns]
  );
  const latestOutboundVoiceSweepRun = useMemo(
    () => outboundVoiceSweepRuns[0] || null,
    [outboundVoiceSweepRuns]
  );
  const outboundVoiceSweepHealthStatus = String(
    outboundVoiceSweepHealth?.status || "unknown"
  ).toLowerCase();
  const outboundVoiceSweepHealthReason =
    typeof outboundVoiceSweepHealth?.reason === "string" &&
    outboundVoiceSweepHealth.reason.trim()
      ? outboundVoiceSweepHealth.reason.trim()
      : "n/a";

  const filteredAudit = useMemo(() => {
    return unifiedAuditEntries.filter((entry) => {
      if (auditSourceFilter !== "all" && entry.source !== auditSourceFilter) {
        return false;
      }
      if (auditActionFilter !== "all" && entry.action !== auditActionFilter) {
        return false;
      }
      if (auditWindow === "all") return true;
      const now = Date.now();
      if (entry.source === "ai" && !entry.handledAt) {
        return false;
      }
      const entryTime = getEpochMs(entry.handledAt ?? entry.createdAt);
      if (entryTime === null) return false;
      const windowMs =
        auditWindow === "24h" ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
      return entryTime >= now - windowMs;
    });
  }, [unifiedAuditEntries, auditSourceFilter, auditActionFilter, auditWindow]);

  const auditActions = Array.from(
    new Set(unifiedAuditEntries.map(entry => entry.action))
  );
  const embedMode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embed") === "1";
  const environmentLabel =
    typeof metrics?.environment === "string" && metrics.environment.trim()
      ? metrics.environment
      : typeof status?.environment === "string" && status.environment.trim()
      ? status.environment
      : "unknown";
  const buildStamp =
    (import.meta.env.VITE_BUILD_SHA as string | undefined)?.trim() ||
    (import.meta.env.VITE_BUILD_TIME as string | undefined)?.trim() ||
    "dev";
  const embedSrc =
    typeof window !== "undefined"
      ? new URL("/metrics?embed=1", window.location.origin).toString()
      : "/metrics?embed=1";
  const embedSnippet =
    `<iframe src="${embedSrc}" style="width:100%;height:900px;border:0;border-radius:12px;" loading="lazy"></iframe>`;
  const tokenSourceMessage =
    tokenSource === "code"
      ? TOKEN_SOURCE_CODE_MESSAGE
      : tokenSource === "link"
      ? TOKEN_SOURCE_LINK_MESSAGE
      : tokenSource === "storage"
      ? TOKEN_SOURCE_STORAGE_MESSAGE
      : null;
  const bypassSourceMessage = isBypassMode ? BYPASS_SOURCE_LINK_MESSAGE : null;
  const integrationsCheckedAt =
    typeof integrations?.checkedAt === "string" && integrations.checkedAt.trim()
      ? new Date(integrations.checkedAt)
      : null;
  const integrationsCheckedAtLabel =
    integrationsCheckedAt && !Number.isNaN(integrationsCheckedAt.getTime())
      ? integrationsCheckedAt.toLocaleTimeString()
      : null;
  const externalVisibilityCheckedAt =
    typeof externalVisibility?.checkedAt === "string" &&
    externalVisibility.checkedAt.trim()
      ? new Date(externalVisibility.checkedAt)
      : null;
  const externalVisibilityCheckedAtLabel =
    externalVisibilityCheckedAt &&
    !Number.isNaN(externalVisibilityCheckedAt.getTime())
      ? externalVisibilityCheckedAt.toLocaleTimeString()
      : null;
  const integrationCards = integrations
    ? [
        {
          label: "n8n alerts",
          configured: integrations.n8nAlertConfigured,
          guidance:
            "If missing: set N8N_ALERT_WEBHOOK_URL in Render so alert notifications can send.",
        },
        {
          label: "n8n booking notifications",
          configured: integrations.bookingConfigured,
          guidance:
            "If missing: set BOOKING_NOTIFY_MODE=email+n8n and BOOKING_NOTIFY_N8N_WEBHOOK_URL.",
        },
        {
          label: "metrics webhook",
          configured: integrations.metricsConfigured,
          guidance:
            "If missing: set N8N_METRICS_WEBHOOK_URL so reliability snapshots reach the Metrics sheet.",
        },
      ]
    : [];
  const externalVisibilityCards = Array.isArray(externalVisibility?.items)
    ? externalVisibility.items.map((item) => ({
        key: String(item?.key || item?.label || "external_visibility"),
        label: String(item?.label || "External visibility seam").trim() || "External visibility seam",
        state: String(item?.state || "scaffold_only").trim().toLowerCase(),
        tenantScopedSourceName:
          typeof item?.tenantScopedSourceName === "string" &&
          item.tenantScopedSourceName.trim()
            ? item.tenantScopedSourceName.trim()
            : null,
        sourceSystem:
          typeof item?.sourceSystem === "string" && item.sourceSystem.trim()
            ? item.sourceSystem.trim()
            : null,
        connectorManifestKey:
          typeof item?.connectorManifest?.key === "string" &&
          item.connectorManifest.key.trim()
            ? item.connectorManifest.key.trim()
            : null,
        connectorManifestSourceName:
          typeof item?.connectorManifest?.sourceName === "string" &&
          item.connectorManifest.sourceName.trim()
            ? item.connectorManifest.sourceName.trim()
            : null,
        connectorManifestOwnerApprovalNote:
          typeof item?.connectorManifest?.ownerApprovalNote === "string" &&
          item.connectorManifest.ownerApprovalNote.trim()
            ? item.connectorManifest.ownerApprovalNote.trim()
            : null,
        freshnessWindowMinutes:
          typeof item?.freshnessWindowMinutes === "number"
            ? item.freshnessWindowMinutes
            : typeof item?.connectorManifest?.freshnessWindowMinutes === "number"
            ? item.connectorManifest.freshnessWindowMinutes
            : null,
        bookingAuthority:
          typeof item?.calendarAdvisory?.bookingAuthority === "string" &&
          item.calendarAdvisory.bookingAuthority.trim()
            ? item.calendarAdvisory.bookingAuthority.trim()
            : null,
        callbackTimingCue:
          typeof item?.calendarAdvisory?.callbackTimingCue === "string" &&
          item.calendarAdvisory.callbackTimingCue.trim()
            ? item.calendarAdvisory.callbackTimingCue.trim()
            : null,
        calendarStaleStateNote:
          typeof item?.calendarAdvisory?.staleStateNote === "string" &&
          item.calendarAdvisory.staleStateNote.trim()
            ? item.calendarAdvisory.staleStateNote.trim()
            : null,
        rosterFallbackNote:
          typeof item?.rosterAdvisory?.fallbackNote === "string" &&
          item.rosterAdvisory.fallbackNote.trim()
            ? item.rosterAdvisory.fallbackNote.trim()
            : null,
        rosterRoleVisibility: Array.isArray(item?.rosterAdvisory?.roleVisibility)
          ? item.rosterAdvisory.roleVisibility
              .map((value) => String(value || "").trim())
              .filter(Boolean)
          : [],
        rosterChannelVisibility: Array.isArray(item?.rosterAdvisory?.channelVisibility)
          ? item.rosterAdvisory.channelVisibility
              .map((value) => String(value || "").trim())
              .filter(Boolean)
          : [],
        summary:
          typeof item?.summary === "string" && item.summary.trim()
            ? item.summary.trim()
            : "No summary available.",
        detail:
          typeof item?.detail === "string" && item.detail.trim()
            ? item.detail.trim()
            : "No additional detail available.",
        lastObservedAt:
          typeof item?.lastObservedAt === "string" && item.lastObservedAt.trim()
            ? item.lastObservedAt.trim()
            : null,
        ready: String(item?.state || "").trim().toLowerCase() === "read_only_ready",
      }))
    : [];
  const salesOfferCheckedAt =
    typeof salesOfferClarity?.checkedAt === "string" &&
    salesOfferClarity.checkedAt.trim()
      ? new Date(salesOfferClarity.checkedAt)
      : null;
  const salesOfferCheckedAtLabel =
    salesOfferCheckedAt && !Number.isNaN(salesOfferCheckedAt.getTime())
      ? salesOfferCheckedAt.toLocaleTimeString()
      : null;
  const salesOfferCards = Array.isArray(salesOfferClarity?.offers)
    ? salesOfferClarity.offers
        .map((offer) => ({
          key: String(offer?.key || "").trim(),
          displayName: String(offer?.displayName || "").trim(),
          audience: String(offer?.audience || "").trim().toLowerCase(),
          publicTotalLabel: String(offer?.publicTotalLabel || "").trim(),
          paymentMode: String(offer?.paymentMode || "").trim().toLowerCase(),
          hydrationStatus: String(offer?.hydrationStatus || "").trim().toLowerCase(),
        }))
        .filter((offer) => offer.key && offer.displayName)
    : [];
  const launchControlTopActions = Array.isArray(launchControl?.nextActions)
    ? launchControl.nextActions
        .map((item) => String(item?.action || "").trim())
        .filter(Boolean)
        .slice(0, 3)
    : [];
  const week1TopProbes = Array.isArray(week1Stabilization?.topProbes)
    ? week1Stabilization.topProbes
        .map((probe) => ({
          key: String(probe?.key || "").trim(),
          cadence: String(probe?.cadence || "").trim(),
        }))
        .filter((probe) => probe.key)
        .slice(0, 3)
    : [];
  const week1TopActions = Array.isArray(week1Stabilization?.actionCues)
    ? week1Stabilization.actionCues
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .slice(0, 3)
    : [];
  const week1BlockerCount = safeNumber(week1Stabilization?.unresolvedBlockers?.count);
  const week1ProbeStatusLabel =
    week1BlockerCount > 0 ? "Watch" : week1TopProbes.length > 0 ? "Healthy" : "No probes";
  const week1LifecycleTopRiskTypes = Array.isArray(
    week1Stabilization?.liveOptimization?.lifecycleUnderperformance?.topAtRiskTypes
  )
    ? week1Stabilization.liveOptimization.lifecycleUnderperformance.topAtRiskTypes
        .map((item) => ({
          lifecycleType: String(item?.lifecycleType || "").trim().toLowerCase(),
          count: safeNumber(item?.count),
        }))
        .filter((item) => item.lifecycleType && item.count > 0)
    : [];
  const reminderAttendanceTopRiskTypes = Array.isArray(
    week1Stabilization?.liveOptimization?.lifecycleUnderperformance?.topAtRiskTypes
  )
    ? week1Stabilization.liveOptimization.lifecycleUnderperformance.topAtRiskTypes
        .map((item) => ({
          lifecycleType: String(item?.lifecycleType || "").trim().toLowerCase(),
          count: safeNumber(item?.count),
        }))
        .filter(
          (item) =>
            REMINDER_ATTENDANCE_LIFECYCLE_TYPES.has(item.lifecycleType) && item.count > 0
        )
    : [];
  const reminderAttendanceSharedPendingCount = safeNumber(
    week1Stabilization?.lifecyclePending?.count
  );
  const reminderAttendanceOverdueCount = safeNumber(
    reminderAttendanceTopRiskTypes.reduce((total, item) => total + item.count, 0)
  );
  const reminderAttendanceAutomationEnabled = Boolean(
    week1Stabilization?.lifecyclePending?.automationEnabled
  );
  const reminderAttendanceStatusLabel = !reminderAttendanceAutomationEnabled
    ? "Disabled"
    : reminderAttendanceOverdueCount > 0
    ? "Watch"
    : reminderAttendanceSharedPendingCount > 0
    ? "Visible"
    : "Ready";
  const reminderAttendanceStatusColor = !reminderAttendanceAutomationEnabled
    ? "#a23e3e"
    : reminderAttendanceOverdueCount > 0
    ? "#b26a00"
    : "#2c5d43";
  const reminderAttendanceAction = !reminderAttendanceAutomationEnabled
    ? "Lifecycle automation is disabled. Reminder and no-show delivery remains proof-only until lifecycle readiness is enabled."
    : reminderAttendanceOverdueCount > 0
    ? "Review overdue reminder and no-show items, then confirm timing windows before increasing reminder reliance."
    : "Reminder and attendance discipline is visible through current lifecycle probes with no live delivery mutation implied.";
  const renewalAtRiskCount = week1LifecycleTopRiskTypes
    .filter((item) => item.lifecycleType === "renewal_nudge")
    .reduce((total, item) => total + item.count, 0);
  const abandonedBookingAtRiskCount = week1LifecycleTopRiskTypes
    .filter((item) => item.lifecycleType === "abandoned_booking_followup")
    .reduce((total, item) => total + item.count, 0);
  const bookingExceptionAwaitingConfirmationCount = safeNumber(
    week1Stabilization?.bookingExceptionPressure?.awaitingConfirmationCount
  );
  const bookingExceptionNeedsStaffCount = safeNumber(
    week1Stabilization?.bookingExceptionPressure?.needsStaffCount
  );
  const abandonedRecoveryProof = revenueRecovery?.abandonedBooking || null;
  const reactivationProof = revenueRecovery?.reactivation || null;
  const abandonedRecoveryStatusLabel =
    abandonedRecoveryProof?.status === "visible" ? "Visible" : "Watch";
  const abandonedRecoveryOverdueCount = safeNumber(abandonedRecoveryProof?.overdueCount);
  const abandonedRecoveryAwaitingCount = safeNumber(
    abandonedRecoveryProof?.awaitingConfirmationCount
  );
  const abandonedRecoveryNeedsStaffCount = safeNumber(
    abandonedRecoveryProof?.needsStaffCount
  );
  const reactivationStatusLabel =
    reactivationProof?.status === "visible" ? "Visible" : "Watch";
  const reactivationEligibleCount = safeNumber(reactivationProof?.cohortSummary?.eligibleCount);
  const reactivationExcludedOpenQueueCount = safeNumber(
    reactivationProof?.cohortSummary?.excludedOpenQueueCount
  );
  const reactivationExcludedRecentActivityCount = safeNumber(
    reactivationProof?.cohortSummary?.excludedRecentActivityCount
  );
  const reactivationExcludedRenewalCount = safeNumber(
    reactivationProof?.cohortSummary?.excludedRenewalCount
  );
  const reactivationExcludedUnpaidCount = safeNumber(
    reactivationProof?.cohortSummary?.excludedUnpaidCount
  );
  const reactivationExcludedAbandonedCount = safeNumber(
    reactivationProof?.cohortSummary?.excludedAbandonedCount
  );
  const reactivationSamplePhonesLabel = Array.isArray(
    reactivationProof?.cohortSummary?.samplePhoneLast4
  )
    ? (reactivationProof?.cohortSummary?.samplePhoneLast4 || []).join(", ") || "n/a"
    : "n/a";
  const moduleEntries = Array.isArray(moduleMatrix?.modules) ? moduleMatrix.modules : [];
  const schedulingModule = moduleEntries.find((item) => item?.key === "scheduling") || null;
  const lifecycleAutomationModule =
    moduleEntries.find((item) => item?.key === "lifecycle_automation") || null;
  const paymentFollowupModule =
    moduleEntries.find((item) => item?.key === "payment_followup") || null;
  const voicePaymentCaptureModule =
    moduleEntries.find((item) => item?.key === "voice_payment_capture") || null;
  const playbooksModule = moduleEntries.find((item) => item?.key === "playbooks") || null;
  const calendarAdvisoryCard =
    externalVisibilityCards.find(
      (item) => String(item?.key || "").trim().toLowerCase() === "calendar_advisory_availability"
    ) || null;
  const playbookBlockKeys = Array.isArray(playbookActiveSummary?.blockKeys)
    ? playbookActiveSummary.blockKeys
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];
  const ownerOverrideEligibleKeys = Array.isArray(
    playbookActiveSummary?.ownerOverrideEligibleKeys
  )
    ? playbookActiveSummary.ownerOverrideEligibleKeys
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];
  const commercialOverrideKeys = ownerOverrideEligibleKeys.filter((key) =>
    ["renewal_pitch", "new_patient_pitch", "objection_cost", "objection_timing"].includes(
      key
    )
  );
  const renewalPitchPresent = playbookBlockKeys.includes("renewal_pitch");
  const paymentExceptionItems = Array.isArray(paymentExceptions?.items)
    ? paymentExceptions.items
        .map((item) => ({
          category: String(item?.category || "").trim().toLowerCase(),
          stale: Boolean(item?.stale),
          state: String(item?.state || "").trim().toLowerCase(),
        }))
        .filter((item) => item.category)
    : [];
  const paymentFollowupItems = Array.isArray(paymentFollowups?.items)
    ? paymentFollowups.items
        .map((item) => ({
          correlationId: String(item?.correlationId || "").trim(),
          channel: String(item?.channel || "").trim().toLowerCase(),
          state: String(item?.state || "").trim().toLowerCase(),
          paymentProvider: String(item?.paymentProvider || "").trim().toLowerCase(),
          paymentLinkAvailable: Boolean(item?.paymentLinkAvailable),
          notifyOk: Boolean(item?.notifyOk),
          notifyMode: String(item?.notifyMode || "").trim().toLowerCase(),
          smsOk: Boolean(item?.smsOk),
          smsSkipped: Boolean(item?.smsSkipped),
        }))
        .filter((item) => item.correlationId)
    : [];
  const paymentCaptureOutcomeItems = Array.isArray(paymentCaptureOutcomes?.items)
    ? paymentCaptureOutcomes.items
        .map((item) => ({
          correlationId: String(item?.correlationId || "").trim(),
          channel: String(item?.channel || "").trim().toLowerCase(),
          stage: String(item?.stage || "").trim().toLowerCase(),
          state: String(item?.state || "").trim().toLowerCase(),
          paymentProvider: String(item?.paymentProvider || "").trim().toLowerCase(),
          fallbackMode:
            typeof item?.fallbackMode === "string"
              ? item.fallbackMode.trim().toLowerCase()
              : "",
          fallbackUsed: Boolean(item?.fallbackUsed),
          notifyOk: Boolean(item?.notifyOk),
          notifyMode: String(item?.notifyMode || "").trim().toLowerCase(),
          smsOk: Boolean(item?.smsOk),
          smsSkipped: Boolean(item?.smsSkipped),
        }))
        .filter((item) => item.correlationId)
    : [];
  const paymentExceptionTotalCount = paymentExceptionItems.length;
  const paymentExceptionStaleCount = paymentExceptionItems.filter(
    (item) => item.stale
  ).length;
  const paymentStaffRequiredCount = paymentExceptionItems.filter(
    (item) => item.category === "staff_required" || item.state === "staff_required"
  ).length;
  const paymentCategoryCounts = paymentExceptionItems.reduce<Record<string, number>>(
    (acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + 1;
      return acc;
    },
    {}
  );
  const paymentHostedLinkActiveCount =
    safeNumber(paymentCategoryCounts.pending_link) + safeNumber(paymentCategoryCounts.link_sent);
  const paymentCaptureStartedCount = safeNumber(paymentCategoryCounts.capture_started);
  const paymentCaptureFailedCount = safeNumber(paymentCategoryCounts.capture_failed);
  const paymentFollowupObservedCount = paymentFollowupItems.length;
  const paymentFollowupLinkAvailableCount = paymentFollowupItems.filter(
    (item) => item.paymentLinkAvailable
  ).length;
  const paymentFollowupNotifyOkCount = paymentFollowupItems.filter(
    (item) => item.notifyOk
  ).length;
  const paymentFollowupSmsDeliveredCount = paymentFollowupItems.filter(
    (item) => item.smsOk && !item.smsSkipped
  ).length;
  const paymentFollowupProviderLabels = [
    ...new Set(paymentFollowupItems.map((item) => item.paymentProvider).filter(Boolean)),
  ];
  const paymentCaptureOutcomeCount = paymentCaptureOutcomeItems.length;
  const paymentCaptureSuccessCount = paymentCaptureOutcomeItems.filter(
    (item) => item.stage === "booking_payment_capture_succeeded"
  ).length;
  const paymentCaptureFailureCount = paymentCaptureOutcomeItems.filter(
    (item) => item.stage === "booking_payment_capture_failed"
  ).length;
  const paymentCaptureFallbackCount = paymentCaptureOutcomeItems.filter(
    (item) => item.stage === "booking_payment_capture_fallback"
  ).length;
  const paymentCaptureHostedLinkFallbackCount = paymentCaptureOutcomeItems.filter(
    (item) =>
      item.stage === "booking_payment_capture_fallback" &&
      item.fallbackMode === "hosted_link"
  ).length;
  const paymentCaptureStaffFallbackCount = paymentCaptureOutcomeItems.filter(
    (item) =>
      item.stage === "booking_payment_capture_fallback" &&
      item.fallbackMode === "staff_required"
  ).length;
  const paymentCaptureProviderLabels = [
    ...new Set(paymentCaptureOutcomeItems.map((item) => item.paymentProvider).filter(Boolean)),
  ];
  const paymentRecoveryStatusLabel =
    paymentStaffRequiredCount > 0
      ? "Watch"
      : paymentExceptionTotalCount > 0
      ? "Visible"
      : "Ready";
  const paymentRecoveryStatusColor =
    paymentStaffRequiredCount > 0
      ? "#a23e3e"
      : paymentExceptionTotalCount > 0
      ? "#b26a00"
      : "#2c5d43";
  const paymentRecoveryAction =
    paymentStaffRequiredCount > 0
      ? "Escalate staff-required recovery items first and keep hosted-link follow-up operator-visible without widening capture authority."
      : paymentCaptureFailedCount > 0
      ? "Review capture-failed exceptions and move recovery back to hosted-link or staff-required flows without exposing sensitive payment detail."
      : paymentHostedLinkActiveCount > 0 || paymentCaptureStartedCount > 0
      ? "Monitor hosted-link and capture-in-progress states as proof-only recovery signals; booking and payment authority remain unchanged."
      : paymentFollowupModule?.nextUpgradeAction ||
        "Payment recovery remains payment-safe and proof-only from this lane.";
  const normalizedPaymentFollowupState = String(
    paymentFollowupModule?.currentState || ""
  )
    .trim()
    .toLowerCase();
  const normalizedVoicePaymentCaptureState = String(
    voicePaymentCaptureModule?.currentState || ""
  )
    .trim()
    .toLowerCase();
  const paymentOperabilityStatusLabel =
    normalizedPaymentFollowupState === "blocked"
      ? "Blocked"
      : paymentFollowupObservedCount > 0 || paymentCaptureOutcomeCount > 0
      ? normalizedVoicePaymentCaptureState === "blocked" ||
        normalizedVoicePaymentCaptureState === "disabled"
        ? "Partial"
        : "Visible"
      : normalizedPaymentFollowupState === "enabled" ||
        normalizedVoicePaymentCaptureState === "enabled" ||
        normalizedVoicePaymentCaptureState === "credential_ready" ||
        normalizedVoicePaymentCaptureState === "live_ready"
      ? "Ready"
      : "Watch";
  const paymentOperabilityStatusColor =
    paymentOperabilityStatusLabel === "Visible"
      ? "#2c5d43"
      : paymentOperabilityStatusLabel === "Ready"
      ? "#2c5d43"
      : paymentOperabilityStatusLabel === "Partial" ||
        paymentOperabilityStatusLabel === "Watch"
      ? "#b26a00"
      : "#a23e3e";
  const paymentOperabilityAction =
    normalizedPaymentFollowupState === "blocked"
      ? paymentFollowupModule?.nextUpgradeAction ||
        "Resolve payment follow-up blockers before treating recovery as operable."
      : normalizedVoicePaymentCaptureState === "blocked"
      ? voicePaymentCaptureModule?.nextUpgradeAction ||
        "Keep voice capture configuration gated until PCI and connector readiness are resolved."
      : paymentCaptureOutcomeCount === 0
      ? "Hosted-link follow-up proof is visible, but no capture-outcome proof is currently observed. Keep live processor claims bounded."
      : "Keep follow-up and capture outcomes operator-visible without treating dashboard proof as raw processor-ledger authority.";
  const paymentFollowupProvidersLabel =
    paymentFollowupProviderLabels.join(", ") || "none observed";
  const paymentCaptureProvidersLabel =
    paymentCaptureProviderLabels.join(", ") || "none observed";
  const renewalRetentionStatusLabel =
    renewalPitchPresent &&
    lifecycleAutomationModule?.currentState &&
    playbooksModule?.currentState
      ? renewalAtRiskCount > 0
        ? "Watch"
        : "Visible"
      : "Watch";
  const playbookGovernanceStatusLabel =
    String(playbooksModule?.currentState || "").trim().toLowerCase() === "pending_owner_input"
      ? "Pending owner input"
      : String(playbooksModule?.currentState || "").trim().toLowerCase() === "blocked"
      ? "Blocked"
      : "Visible";
  const playbookGovernanceStatusColor =
    String(playbooksModule?.currentState || "").trim().toLowerCase() === "blocked"
      ? "#a23e3e"
      : String(playbooksModule?.currentState || "").trim().toLowerCase() ===
        "pending_owner_input"
      ? "#b26a00"
      : "#2c5d43";
  const revenueVisibleLaneCount = 5;
  const revenueModuleBlockedCount = [
    lifecycleAutomationModule,
    paymentFollowupModule,
    playbooksModule,
  ].filter((item) => String(item?.currentState || "").trim().toLowerCase() === "blocked")
    .length;
  const revenueOwnerPendingCount = [lifecycleAutomationModule, playbooksModule].filter(
    (item) => String(item?.currentState || "").trim().toLowerCase() === "pending_owner_input"
  ).length;
  const platformReadyCount = safeNumber(externalVisibility?.summary?.readOnlyReadyCount);
  const platformScaffoldOnlyCount = safeNumber(externalVisibility?.summary?.scaffoldOnlyCount);
  const governanceRoleLabel = isClientViewerMode
    ? "client viewer"
    : hasOperatorToken
    ? formatRoleLabel(operatorRole || "operator")
    : "locked";
  const governanceOwnerPendingCount = safeNumber(
    moduleMatrix?.summary?.pendingOwnerApprovalCount
  );
  const governanceAdminOnlyControlCount = [
    canResetEscalationBreaker,
    canResetEscalationBackoff,
  ].filter(Boolean).length;
  const governanceOperatorControlCount = [
    canRetryEscalationDeliveries,
    canManageSchedulingExceptions,
    canManageClinicSettings,
  ].filter(Boolean).length;
  const governanceStatusLabel = isClientViewerMode
    ? "Viewer only"
    : governanceOwnerPendingCount > 0
    ? "Pending owner input"
    : hasOperatorToken
    ? "Bounded"
    : "Locked";
  const governanceStatusColor = isClientViewerMode
    ? "#7b726a"
    : governanceOwnerPendingCount > 0
    ? "#b26a00"
    : hasOperatorToken
    ? "#2c5d43"
    : "#a23e3e";
  const enterpriseReadinessStatusLabel =
    !canViewInternalMetrics || isClientViewerMode
      ? "Bounded"
      : !launchControl?.overallReady ||
        revenueModuleBlockedCount > 0 ||
        governanceOwnerPendingCount > 0
      ? "Watch"
      : "Visible";
  const enterpriseReadinessStatusColor =
    enterpriseReadinessStatusLabel === "Watch"
      ? "#b26a00"
      : enterpriseReadinessStatusLabel === "Visible"
      ? "#2c5d43"
      : "#7b726a";
  const enterpriseVoicePosture = status?.voiceEnabled
    ? `Clinic voice runtime enabled via ${status?.voiceActivationSource || "unknown source"}`
    : "Clinic voice runtime disabled";
  const salesActivePublicOfferCount = safeNumber(
    salesOfferClarity?.summary?.activePublicOfferCount
  );
  const salesPublicSchedulerCount = safeNumber(
    salesOfferClarity?.summary?.publicSchedulerCount
  );
  const salesHydratedOfferCount = safeNumber(
    salesOfferClarity?.summary?.hydratedOfferCount
  );
  const salesByAudience = {
    newPatient: safeNumber(salesOfferClarity?.summary?.byAudience?.new_patient),
    renewal: safeNumber(salesOfferClarity?.summary?.byAudience?.renewal),
    veteran: safeNumber(salesOfferClarity?.summary?.byAudience?.veteran),
    special: safeNumber(salesOfferClarity?.summary?.byAudience?.special),
  };
  const salesByPaymentMode = {
    full: safeNumber(salesOfferClarity?.summary?.byPaymentMode?.full),
    deposit: safeNumber(salesOfferClarity?.summary?.byPaymentMode?.deposit),
    dynamic: safeNumber(salesOfferClarity?.summary?.byPaymentMode?.dynamic),
  };
  const salesAdArtifactPresent = Boolean(salesOfferClarity?.adState?.imageArtifactPresent);
  const salesAdArtifactEmpty = Boolean(salesOfferClarity?.adState?.imageArtifactEmpty);
  const salesStructuredAdStateReady = Boolean(
    salesOfferClarity?.adState?.structuredStatePackagingReady
  );
  const salesActivePrivateEntryCount = safeNumber(
    salesOfferClarity?.adState?.activePrivateEntryCount
  );
  const salesPrivateEntryCount = safeNumber(
    salesOfferClarity?.adState?.privateEntryCount
  );
  const salesCatalogClinicLabel =
    typeof salesOfferClarity?.catalogClinicLabel === "string" &&
    salesOfferClarity.catalogClinicLabel.trim()
      ? salesOfferClarity.catalogClinicLabel.trim()
      : "Unknown clinic";
  const salesAdStateStatusLabel =
    salesActivePublicOfferCount <= 0
      ? "Missing"
      : salesAdArtifactPresent && salesStructuredAdStateReady
      ? "Ready"
      : salesAdArtifactPresent
      ? "Visible"
      : "Watch";
  const salesAdStateStatusColor =
    salesAdStateStatusLabel === "Ready"
      ? "#2c5d43"
      : salesAdStateStatusLabel === "Visible"
      ? "#2c5d43"
      : salesAdStateStatusLabel === "Watch"
      ? "#b26a00"
      : "#a23e3e";
  const salesAdStateOperatorAction =
    typeof salesOfferClarity?.operatorAction === "string" &&
    salesOfferClarity.operatorAction.trim()
      ? salesOfferClarity.operatorAction.trim()
      : "Keep ad-state review manual until a structured ad-state manifest is checked in.";
  const schedulingAuthorityLabel = calendarAdvisoryCard?.bookingAuthority || "acuity";
  const schedulingIntegrityStatusLabel =
    salesPublicSchedulerCount > 0 &&
    salesHydratedOfferCount > 0 &&
    schedulingAuthorityLabel.toLowerCase() === "acuity"
      ? "Visible"
      : salesPublicSchedulerCount > 0
      ? "Watch"
      : "Missing";
  const schedulingIntegrityStatusColor =
    schedulingIntegrityStatusLabel === "Visible"
      ? "#2c5d43"
      : schedulingIntegrityStatusLabel === "Watch"
      ? "#b26a00"
      : "#a23e3e";
  const schedulingIntegrityAction =
    schedulingModule?.nextUpgradeAction ||
    "Keep direct-booking truth anchored to Acuity and treat callback/confirmation lanes as operator follow-up only.";
  const dashboardReportingSnapshot = useMemo(
    () => ({
      generatedAt: new Date().toISOString(),
      environment: environmentLabel,
      build: buildStamp,
      access: {
        role: governanceRoleLabel,
        clientViewer: isClientViewerMode,
      },
      reminderAttendance: {
        status: reminderAttendanceStatusLabel,
        sharedPendingCount: reminderAttendanceSharedPendingCount,
        overdueCount: reminderAttendanceOverdueCount,
        automationEnabled: reminderAttendanceAutomationEnabled,
      },
      paymentRecovery: {
        status: paymentRecoveryStatusLabel,
        openCount: paymentExceptionTotalCount,
        staleCount: paymentExceptionStaleCount,
        staffRequiredCount: paymentStaffRequiredCount,
        hostedLinkActiveCount: paymentHostedLinkActiveCount,
        captureStartedCount: paymentCaptureStartedCount,
        captureFailedCount: paymentCaptureFailedCount,
      },
      renewalRetention: {
        status: renewalRetentionStatusLabel,
        overdueCount: renewalAtRiskCount,
        renewalPitchPresent,
      },
      abandonedBookingRecovery: {
        status: abandonedRecoveryStatusLabel,
        overdueCount: abandonedBookingAtRiskCount,
        awaitingConfirmationCount: bookingExceptionAwaitingConfirmationCount,
        needsStaffCount: bookingExceptionNeedsStaffCount,
      },
      playbookGovernance: {
        status: playbookGovernanceStatusLabel,
        ownerPendingCount: governanceOwnerPendingCount,
        overrideEligibleCount: safeNumber(playbookActiveSummary?.ownerOverrideEligibleCount),
      },
      salesAdState: {
        status: salesAdStateStatusLabel,
        activePublicOfferCount: salesActivePublicOfferCount,
        hydratedOfferCount: salesHydratedOfferCount,
        adArtifactPresent: salesAdArtifactPresent,
        adArtifactEmpty: salesAdArtifactEmpty,
        structuredStatePackagingReady: salesStructuredAdStateReady,
        catalogClinicLabel: salesCatalogClinicLabel,
      },
      schedulingIntegrity: {
        status: schedulingIntegrityStatusLabel,
        publicSchedulerCount: salesPublicSchedulerCount,
        hydratedOfferCount: salesHydratedOfferCount,
        bookingAuthority: schedulingAuthorityLabel,
        schedulingModuleState: schedulingModule?.currentState || null,
        awaitingConfirmationCount: bookingExceptionAwaitingConfirmationCount,
        needsStaffCount: bookingExceptionNeedsStaffCount,
      },
      paymentOperability: {
        status: paymentOperabilityStatusLabel,
        followupObservedCount: paymentFollowupObservedCount,
        followupLinkAvailableCount: paymentFollowupLinkAvailableCount,
        followupNotifyOkCount: paymentFollowupNotifyOkCount,
        captureOutcomeCount: paymentCaptureOutcomeCount,
        captureSuccessCount: paymentCaptureSuccessCount,
        captureFailureCount: paymentCaptureFailureCount,
        captureFallbackCount: paymentCaptureFallbackCount,
        paymentFollowupModuleState: paymentFollowupModule?.currentState || null,
        voicePaymentCaptureModuleState: voicePaymentCaptureModule?.currentState || null,
      },
      governance: {
        status: governanceStatusLabel,
        role: governanceRoleLabel,
        adminOnlyControlCount: governanceAdminOnlyControlCount,
        operatorControlCount: governanceOperatorControlCount,
      },
      enterpriseReadiness: {
        status: enterpriseReadinessStatusLabel,
        platformReadyCount,
        platformScaffoldOnlyCount,
        launchControlReady: Boolean(launchControl?.overallReady),
        revenueModuleBlockedCount,
        voicePosture: enterpriseVoicePosture,
      },
    }),
    [
      abandonedBookingAtRiskCount,
      abandonedRecoveryStatusLabel,
      bookingExceptionAwaitingConfirmationCount,
      bookingExceptionNeedsStaffCount,
      buildStamp,
      enterpriseReadinessStatusLabel,
      enterpriseVoicePosture,
      environmentLabel,
      governanceAdminOnlyControlCount,
      governanceOperatorControlCount,
      governanceOwnerPendingCount,
      governanceRoleLabel,
      governanceStatusLabel,
      isClientViewerMode,
      launchControl?.overallReady,
      salesActivePublicOfferCount,
      salesAdArtifactPresent,
      salesCatalogClinicLabel,
      salesHydratedOfferCount,
      salesPublicSchedulerCount,
      salesStructuredAdStateReady,
      salesAdStateStatusLabel,
      schedulingAuthorityLabel,
      schedulingIntegrityStatusLabel,
      schedulingModule?.currentState,
      paymentCaptureFallbackCount,
      paymentCaptureFailureCount,
      paymentCaptureFailedCount,
      paymentCaptureOutcomeCount,
      paymentCaptureSuccessCount,
      paymentCaptureStartedCount,
      paymentExceptionStaleCount,
      paymentExceptionTotalCount,
      paymentFollowupLinkAvailableCount,
      paymentFollowupNotifyOkCount,
      paymentFollowupObservedCount,
      paymentHostedLinkActiveCount,
      paymentOperabilityStatusLabel,
      paymentRecoveryStatusLabel,
      paymentStaffRequiredCount,
      platformReadyCount,
      platformScaffoldOnlyCount,
      playbookActiveSummary?.ownerOverrideEligibleCount,
      playbookGovernanceStatusLabel,
      reminderAttendanceAutomationEnabled,
      reminderAttendanceOverdueCount,
      reminderAttendanceSharedPendingCount,
      reminderAttendanceStatusLabel,
      renewalAtRiskCount,
      renewalPitchPresent,
      renewalRetentionStatusLabel,
      revenueModuleBlockedCount,
      voicePaymentCaptureModule?.currentState,
    ]
  );
  const canUsePlaybookPreview =
    canViewInternalMetrics && !isClientViewerMode && hasOperatorToken;
  const playbookPreviewVoiceLines = Array.isArray(playbookPreviewResult?.voiceLines)
    ? playbookPreviewResult.voiceLines
    : [];

  const handleExportReportingSnapshot = (format: "json" | "md") => {
    if (!canViewInternalMetrics || isClientViewerMode) return;

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    if (format === "json") {
      downloadTextFile(
        `vetcan-reporting-snapshot-${timestamp}.json`,
        JSON.stringify(dashboardReportingSnapshot, null, 2),
        "application/json"
      );
      setReportingExportMessage("Owner snapshot export prepared from current dashboard truth.");
      return;
    }

    downloadTextFile(
      `vetcan-reporting-snapshot-${timestamp}.md`,
      buildDashboardReportingMarkdown(dashboardReportingSnapshot),
      "text/markdown"
    );
    setReportingExportMessage(
      "Operator summary export prepared from current dashboard truth."
    );
  };

  return (
    <div
      style={{
        minHeight: embedMode ? "auto" : "100vh",
        color: "#1b1b1b",
        background: embedMode
          ? "transparent"
          : "radial-gradient(circle at 10% 20%, rgba(255, 229, 210, 0.7), transparent 50%), radial-gradient(circle at 85% 10%, rgba(186, 225, 255, 0.6), transparent 45%), linear-gradient(180deg, #f7f4ef, #f1efe9 60%, #f9f6f0)",
        fontFamily: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
        padding: embedMode ? "8px" : "32px",
      }}
    >
      <main
        style={{
          maxWidth: embedMode ? "100%" : "1100px",
          margin: embedMode ? "0" : "0 auto",
          background: embedMode ? "#ffffff" : "rgba(255, 255, 255, 0.82)",
          borderRadius: embedMode ? "12px" : "18px",
          padding: embedMode ? "16px 18px" : "28px 32px",
          boxShadow: embedMode ? "none" : "0 18px 60px rgba(24, 28, 34, 0.12)",
          border: embedMode
            ? "1px solid rgba(28, 28, 28, 0.08)"
            : "1px solid rgba(28, 28, 28, 0.08)",
        }}
      >
        {!embedMode ? (
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <p
                style={{
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  fontSize: "12px",
                  margin: 0,
                }}
              >
                VetCan Ops Console
              </p>
              <h1 style={{ fontSize: "30px", margin: "8px 0 6px" }}>
                Unified Operations Dashboard
              </h1>
              <p style={{ margin: 0, color: "#5a524b" }}>
                Environment: <strong>{environmentLabel}</strong>
              </p>
              <p style={{ margin: "6px 0 0", color: "#5a524b", fontSize: "13px" }}>
                Dashboard Access:{" "}
                <strong>
                  {canViewInternalMetrics
                    ? isBypassMode
                      ? BYPASS_CLIENT_VIEW_LABEL
                      : isClientViewerMode
                      ? "Client Viewer (Sanitized)"
                      : "Unlocked"
                    : "Locked"}
                </strong>
              </p>
              {bypassSourceMessage && (
                <p style={{ margin: "6px 0 0", color: "#6a5f55", fontSize: "12px" }}>
                  {bypassSourceMessage}
                </p>
              )}
              {tokenSourceMessage && (
                <p style={{ margin: "6px 0 0", color: "#6a5f55", fontSize: "12px" }}>
                  {tokenSourceMessage}
                </p>
              )}
              <p style={{ margin: "6px 0 0", color: "#5a524b", fontSize: "13px" }}>
                Build: <strong>{buildStamp}</strong>
              </p>
              <p style={{ margin: "8px 0 0", color: "#5a524b", fontSize: "13px" }}>
                Dashboard is the live read-only view. Use Studio for draft changes
                and Preview for rehearsal, then use this page to confirm what is
                happening now.
              </p>
              <p style={{ margin: "8px 0 0", color: "#6a5f55", fontSize: "13px" }}>
                Launch control appears here as readiness guidance, not as a deployment button.
              </p>
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <a
                  href="/studio"
                  style={{
                    display: "inline-block",
                    padding: "8px 12px",
                    borderRadius: "9px",
                    border: "1px solid #c7beb4",
                    background: "#ffffff",
                    color: "#5a524b",
                    textDecoration: "none",
                    fontSize: "13px",
                  }}
                >
                  Open Studio
                </a>
                <a
                  href="/studio/preview?returnStep=preview"
                  style={{
                    display: "inline-block",
                    padding: "8px 12px",
                    borderRadius: "9px",
                    border: "1px solid #c7beb4",
                    background: "#ffffff",
                    color: "#5a524b",
                    textDecoration: "none",
                    fontSize: "13px",
                  }}
                >
                  Open Preview Center
                </a>
              </div>
              {isClientViewerMode && (
                <p style={{ margin: "8px 0 0", color: "#6a5f55", fontSize: "13px" }}>
                  {metrics?.note || status?.note || CLIENT_VIEWER_MESSAGE}
                </p>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ margin: 0, fontSize: "14px", color: "#5a524b" }}>
                Uptime
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "22px" }}>
                {uptimeLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                Metrics updated {metricsUpdatedAt || "Not updated yet"}
              </p>
            </div>
          </header>
        ) : (
          <div style={{ marginBottom: "6px", color: "#5a524b", fontSize: "13px" }}>
            Environment: <strong>{environmentLabel}</strong> · Uptime {uptimeLabel} ·
            Access{" "}
            {canViewInternalMetrics
              ? isBypassMode
                ? BYPASS_CLIENT_VIEW_LABEL
                : isClientViewerMode
                ? "Client Viewer (Sanitized)"
                : "Unlocked"
              : "Locked"}{" "}
            · Build {buildStamp}
            {bypassSourceMessage ? ` · ${bypassSourceMessage}` : ""}
            {tokenSourceMessage ? ` · ${tokenSourceMessage}` : ""}
          </div>
        )}

        {showOperatorAccessPanel && (
          <section
            style={{
              marginTop: "20px",
              padding: "12px",
              borderRadius: "10px",
              background: "#f9f4ee",
              border: "1px solid #e4dbd0",
            }}
          >
            <h2 style={{ fontSize: "18px", margin: "0 0 8px" }}>
              Dashboard Access
            </h2>
            <p style={{ margin: 0, color: "#5a524b", fontSize: "14px" }}>
              Paste access token to unlock dashboard metrics.
            </p>
            {operatorAccessError && (
              <p style={{ margin: "8px 0 0", color: "#a23e3e", fontSize: "13px" }}>
                {operatorAccessError}
              </p>
            )}
            <div
              style={{
                marginTop: "10px",
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <input
                aria-label="Access token"
                type={showOperatorToken ? "text" : "password"}
                value={operatorTokenInput}
                onChange={(event) => {
                  setOperatorTokenInput(event.target.value);
                  if (copyTokenState !== "idle") {
                    setCopyTokenState("idle");
                  }
                }}
                placeholder="Paste access token"
                autoComplete="off"
                style={{
                  flex: "1 1 300px",
                  minWidth: "220px",
                  borderRadius: "8px",
                  border: "1px solid #d4cabc",
                  padding: "8px 10px",
                  fontFamily: "monospace",
                }}
              />
              <button
                type="button"
                onClick={() => setShowOperatorToken((value) => !value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #c7beb4",
                  background: "#ffffff",
                  cursor: "pointer",
                }}
              >
                {showOperatorToken ? "Hide" : "Show"}
              </button>
              <button
                type="button"
                onClick={handleSaveOperatorToken}
                disabled={!normalizedOperatorTokenInput || operatorTokenSaving}
                style={{
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #2c5d43",
                  background: "#2c5d43",
                  color: "#ffffff",
                  cursor:
                    normalizedOperatorTokenInput && !operatorTokenSaving
                      ? "pointer"
                      : "not-allowed",
                  opacity:
                    normalizedOperatorTokenInput && !operatorTokenSaving
                      ? 1
                      : 0.6,
                }}
              >
                {operatorTokenSaving ? "Validating..." : "Save"}
              </button>
              <button
                type="button"
                onClick={handleCopyToken}
                disabled={!normalizedOperatorTokenInput}
                style={{
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #c7beb4",
                  background: "#ffffff",
                  cursor: normalizedOperatorTokenInput ? "pointer" : "not-allowed",
                  opacity: normalizedOperatorTokenInput ? 1 : 0.6,
                }}
              >
                {copyTokenState === "copied" ? "Copied" : "Copy token"}
              </button>
              <button
                type="button"
                onClick={handleClearOperatorToken}
                style={{
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #c7beb4",
                  background: "#ffffff",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            </div>
            {normalizedOperatorTokenInput && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  background: "#ffffff",
                  border: "1px solid #e4dbd0",
                  fontSize: "12px",
                  color: "#4f473f",
                  lineHeight: 1.6,
                }}
              >
                <p style={{ margin: 0, fontWeight: 600 }}>Token Info</p>
                <p style={{ margin: "4px 0 0" }}>
                  Scope: {tokenScopeDisplay}
                </p>
                <p style={{ margin: "2px 0 0" }}>
                  Type: {tokenTypeDisplay}
                </p>
                <p style={{ margin: "2px 0 0" }}>
                  Expires:{" "}
                  {typeof decodedTokenPayload?.exp === "number"
                    ? tokenExpiryDate
                      ? tokenExpiryDate.toLocaleString()
                      : "Invalid timestamp"
                    : "Unavailable"}
                </p>
                {typeof decodedTokenPayload?.exp === "number" && (
                  <p style={{ margin: "2px 0 0" }}>
                    Expired: {tokenExpired ? "Yes" : "No"}
                  </p>
                )}
                <p style={{ margin: "2px 0 0" }}>
                  Issued:{" "}
                  {tokenIssuedAtIso
                    ? new Date(tokenIssuedAtIso).toLocaleString()
                    : "Unavailable"}
                </p>
                <p style={{ margin: "2px 0 0" }}>
                  Subject:{" "}
                  {typeof decodedTokenPayload?.sub === "string"
                    ? decodedTokenPayload.sub
                    : "Unavailable"}
                </p>
                <p style={{ margin: "2px 0 0" }}>
                  Role:{" "}
                  {typeof decodedTokenPayload?.role === "string"
                    ? decodedTokenPayload.role
                    : "Unavailable"}
                </p>
                <p style={{ margin: "2px 0 0" }}>
                  Header typ:{" "}
                  {typeof decodedTokenHeader?.typ === "string"
                    ? decodedTokenHeader.typ
                    : "Unavailable"}
                </p>
              </div>
            )}
          </section>
        )}

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>Today's Work Queue</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p
                style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}
                title="Callbacks that still need someone to call the client back."
              >
                Pending callbacks
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                {pendingCount}
              </p>
            </article>
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p
                style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}
                title="Voice booking requests waiting on a final confirmed or cancelled outcome."
              >
                Pending voice bookings
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                {voiceBookingOutcomePending}
              </p>
            </article>
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p
                style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}
                title="Scheduling exceptions that still need manual confirmation."
              >
                Scheduling exceptions needing confirmation
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                {awaitingConfirmationCount}
              </p>
            </article>
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p
                style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}
                title="Alerts currently open and needing attention."
              >
                Active alerts
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                {activeAlertCount}
              </p>
            </article>
          </div>
          {canViewInternalMetrics && callbacksUpdatedAt && (
            <p style={{ marginTop: "10px", fontSize: "12px", color: "#7b726a" }}>
              Callbacks updated {callbacksUpdatedAt.toLocaleTimeString()}
            </p>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>What do I do next?</SectionTitle>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <a
              href="#active-alerts-live-feed"
              style={{
                display: "inline-block",
                padding: "8px 12px",
                borderRadius: "9px",
                border: "1px solid #c7beb4",
                background: "#ffffff",
                color: "#5a524b",
                textDecoration: "none",
                fontSize: "13px",
              }}
            >
              Review active alerts
            </a>
            <a
              href="#callback-requests"
              style={{
                display: "inline-block",
                padding: "8px 12px",
                borderRadius: "9px",
                border: "1px solid #c7beb4",
                background: "#ffffff",
                color: "#5a524b",
                textDecoration: "none",
                fontSize: "13px",
              }}
            >
              Work open requests
            </a>
            <a
              href="#voice-ops"
              style={{
                display: "inline-block",
                padding: "8px 12px",
                borderRadius: "9px",
                border: "1px solid #c7beb4",
                background: "#ffffff",
                color: "#5a524b",
                textDecoration: "none",
                fontSize: "13px",
              }}
            >
              Check voice ops
            </a>
            <a
              href="#callback-follow-ups"
              style={{
                display: "inline-block",
                padding: "8px 12px",
                borderRadius: "9px",
                border: "1px solid #c7beb4",
                background: "#ffffff",
                color: "#5a524b",
                textDecoration: "none",
                fontSize: "13px",
              }}
            >
              Review follow-ups
            </a>
          </div>
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Workflow Integrations</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : !integrations ? (
            <p style={{ color: "#7b726a" }}>Workflow integration status unavailable.</p>
          ) : (
            <>
              <p style={{ margin: "0 0 10px", fontSize: "12px", color: "#7b726a" }}>
                Current webhook and workflow wiring only. Future platform connectors are
                tracked separately.
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "12px",
                }}
              >
                {integrationCards.map((card) => (
                  <article
                    key={card.label}
                    style={{
                      padding: "14px 16px",
                      borderRadius: "14px",
                      background: "#f9f4ee",
                      border: "1px solid #e4dbd0",
                    }}
                  >
                    <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                      {card.label}
                    </p>
                    <p
                      style={{
                        margin: "8px 0 0",
                        fontSize: "18px",
                        color: card.configured ? "#2c5d43" : "#a23e3e",
                      }}
                    >
                      {card.configured ? "Configured" : "Missing"}
                    </p>
                    <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                      {card.guidance}
                    </p>
                  </article>
                ))}
              </div>
              <p style={{ marginTop: "10px", fontSize: "12px", color: "#7b726a" }}>
                Last checked {integrationsCheckedAtLabel || "just now"}.
              </p>
            </>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>External Visibility (Read-Only)</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              External visibility seams are shown for operator sessions.
            </p>
          ) : externalVisibilityLoading ? (
            <p style={{ color: "#7b726a" }}>Loading external visibility seams...</p>
          ) : externalVisibilityError ? (
            <p style={{ color: "#a23e3e" }}>{externalVisibilityError}</p>
          ) : externalVisibilityCards.length === 0 ? (
            <p style={{ color: "#7b726a" }}>
              External visibility seams are not available yet.
            </p>
          ) : (
            <>
              <p style={{ margin: "0 0 10px", fontSize: "12px", color: "#7b726a" }}>
                Read-only platform seams only. These cards show tenant-scoped
                connector manifests and advisory context without introducing CRM
                write-back, live calendar routing, or roster-driven automation.
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: "12px",
                }}
              >
                {externalVisibilityCards.map((card) => (
                  <article
                    key={card.key}
                    style={{
                      padding: "14px 16px",
                      borderRadius: "14px",
                      background: "#f9f4ee",
                      border: "1px solid #e4dbd0",
                    }}
                  >
                    <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                      {card.label}
                    </p>
                    <p
                      style={{
                        margin: "8px 0 0",
                        fontSize: "18px",
                        color: card.ready ? "#2c5d43" : "#7b726a",
                      }}
                    >
                      {card.ready ? "Read-only ready" : "Scaffold only"}
                    </p>
                    <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#5a524b" }}>
                      {card.summary}
                    </p>
                    <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                      Connector:{" "}
                      {card.connectorManifestSourceName ||
                        card.tenantScopedSourceName ||
                        "Not named"}
                    </p>
                    <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                      Manifest: {card.connectorManifestKey || "Not generated"}
                    </p>
                    <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                      Source: {card.sourceSystem || "Not defined"}
                    </p>
                    {card.freshnessWindowMinutes ? (
                      <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                        Freshness: {card.freshnessWindowMinutes}m advisory window
                      </p>
                    ) : null}
                    {card.connectorManifestOwnerApprovalNote ? (
                      <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                        Approval: {card.connectorManifestOwnerApprovalNote}
                      </p>
                    ) : null}
                    {card.bookingAuthority ? (
                      <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                        Booking authority: {card.bookingAuthority}
                      </p>
                    ) : null}
                    {card.callbackTimingCue ? (
                      <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                        Timing cue: {card.callbackTimingCue}
                      </p>
                    ) : null}
                    {card.calendarStaleStateNote ? (
                      <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                        Stale state: {card.calendarStaleStateNote}
                      </p>
                    ) : null}
                    {card.rosterRoleVisibility.length > 0 ? (
                      <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                        Roles: {card.rosterRoleVisibility.join(", ")}
                      </p>
                    ) : null}
                    {card.rosterChannelVisibility.length > 0 ? (
                      <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                        Channels: {card.rosterChannelVisibility.join(", ")}
                      </p>
                    ) : null}
                    {card.rosterFallbackNote ? (
                      <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                        Escalation context: {card.rosterFallbackNote}
                      </p>
                    ) : null}
                    <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                      {card.detail}
                    </p>
                    <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                      Observed: {formatTimestamp(card.lastObservedAt, "Not observed")}
                    </p>
                  </article>
                ))}
              </div>
              <p style={{ marginTop: "10px", fontSize: "12px", color: "#7b726a" }}>
                Last checked {externalVisibilityCheckedAtLabel || "just now"}.
              </p>
            </>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Launch Control</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Launch control is shown for operator sessions.
            </p>
          ) : launchControlLoading ? (
            <p style={{ color: "#7b726a" }}>Loading launch control...</p>
          ) : launchControlError ? (
            <p style={{ color: "#a23e3e" }}>{launchControlError}</p>
          ) : !launchControl ? (
            <p style={{ color: "#7b726a" }}>Launch control unavailable.</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Overall readiness
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: launchControl.overallReady ? "#2c5d43" : "#a23e3e",
                }}
              >
                {launchControl.overallReady ? "Ready" : "Blocked"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Blockers: {safeNumber(launchControl.blockerCount)}
              </p>
              {launchControlTopActions.length > 0 ? (
                <ul style={{ margin: "10px 0 0", paddingLeft: "18px", color: "#5a524b" }}>
                  {launchControlTopActions.map((action) => (
                    <li key={action} style={{ marginBottom: "4px", fontSize: "13px" }}>
                      {action}
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                  No immediate actions.
                </p>
              )}
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Week-1 Stabilization</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Week-1 stabilization is shown for operator sessions.
            </p>
          ) : week1StabilizationLoading ? (
            <p style={{ color: "#7b726a" }}>Loading week-1 stabilization...</p>
          ) : week1StabilizationError ? (
            <p style={{ color: "#a23e3e" }}>{week1StabilizationError}</p>
          ) : !week1Stabilization ? (
            <p style={{ color: "#7b726a" }}>Week-1 stabilization unavailable.</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Probe status
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: week1BlockerCount > 0 ? "#a23e3e" : "#2c5d43",
                }}
              >
                {week1ProbeStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Probes: {week1TopProbes.length} · Blockers: {week1BlockerCount}
              </p>
              {week1Stabilization.checkedAt ? (
                <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Checked {formatTimestamp(week1Stabilization.checkedAt, "n/a")}
                </p>
              ) : null}
              {week1TopProbes.length > 0 ? (
                <ul style={{ margin: "10px 0 0", paddingLeft: "18px", color: "#5a524b" }}>
                  {week1TopProbes.map((probe) => (
                    <li
                      key={`${probe.key}:${probe.cadence}`}
                      style={{ marginBottom: "4px", fontSize: "13px" }}
                    >
                      {probe.key}
                      {probe.cadence ? ` · ${probe.cadence}` : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
              {week1TopActions.length > 0 ? (
                <ul style={{ margin: "10px 0 0", paddingLeft: "18px", color: "#5a524b" }}>
                  {week1TopActions.map((action) => (
                    <li key={action} style={{ marginBottom: "4px", fontSize: "13px" }}>
                      {action}
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                  No immediate actions.
                </p>
              )}
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Reminder & Attendance</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Reminder and attendance discipline is shown for operator sessions.
            </p>
          ) : week1StabilizationLoading ? (
            <p style={{ color: "#7b726a" }}>Loading reminder and attendance lane...</p>
          ) : week1StabilizationError ? (
            <p style={{ color: "#a23e3e" }}>{week1StabilizationError}</p>
          ) : !week1Stabilization ? (
            <p style={{ color: "#7b726a" }}>Reminder and attendance lane unavailable.</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Owner-safe lifecycle lane
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: reminderAttendanceStatusColor,
                }}
              >
                {reminderAttendanceStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Tracked rules: Booking confirmed reminder, Same-day reminder, No-show
                follow-up.
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Shared lifecycle pending items: {reminderAttendanceSharedPendingCount} ·
                Overdue reminder/no-show items: {reminderAttendanceOverdueCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Lifecycle automation: {reminderAttendanceAutomationEnabled ? "enabled" : "disabled"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                At-risk lanes:{" "}
                {reminderAttendanceTopRiskTypes.length > 0
                  ? reminderAttendanceTopRiskTypes
                      .map(
                        (item) =>
                          `${formatLifecycleLaneLabel(item.lifecycleType)} (${item.count})`
                      )
                      .join(", ")
                  : "No overdue reminder or no-show lanes detected."}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Owner-safe action: {reminderAttendanceAction}
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Unpaid Follow-up & Payment Recovery</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Payment recovery is shown for operator sessions.
            </p>
          ) : !hasOperatorToken ? (
            <p style={{ color: "#7b726a" }}>
              Save an operator token to inspect payment recovery exceptions.
            </p>
          ) : paymentExceptionsLoading || moduleMatrixLoading ? (
            <p style={{ color: "#7b726a" }}>Loading payment recovery lane...</p>
          ) : paymentExceptionsError ? (
            <p style={{ color: "#a23e3e" }}>{paymentExceptionsError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Payment-safe recovery lane
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: paymentRecoveryStatusColor,
                }}
              >
                {paymentRecoveryStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Lifecycle rule: unpaid_followup
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Open payment exceptions: {paymentExceptionTotalCount} · stale:{" "}
                {paymentExceptionStaleCount} · staff-required: {paymentStaffRequiredCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Recovery categories:{" "}
                {[
                  "unpaid_followup_required",
                  "capture_failed",
                  "pending_link",
                  "link_sent",
                  "staff_required",
                ]
                  .map(
                    (key) =>
                      `${formatPaymentExceptionCategoryLabel(key)} (${paymentCategoryCounts[key] || 0})`
                  )
                  .join(", ")}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Hardening states: hosted-link active {paymentHostedLinkActiveCount} ·
                capture-started {paymentCaptureStartedCount} · capture-failed{" "}
                {paymentCaptureFailedCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Processor detail stays external. This lane shows operator follow-up
                states only.
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Module readiness: {formatReadinessStateLabel(paymentFollowupModule?.currentState)}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Owner-safe action:{" "}
                {paymentRecoveryAction}
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Renewal & Retention</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Renewal and retention is shown for operator sessions.
            </p>
          ) : moduleMatrixLoading || playbookActiveSummaryLoading || week1StabilizationLoading ? (
            <p style={{ color: "#7b726a" }}>Loading renewal and retention lane...</p>
          ) : moduleMatrixError ? (
            <p style={{ color: "#a23e3e" }}>{moduleMatrixError}</p>
          ) : playbookActiveSummaryError ? (
            <p style={{ color: "#a23e3e" }}>{playbookActiveSummaryError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Renewal-safe retention lane
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px", color: "#2c5d43" }}>
                {renewalRetentionStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Lifecycle rule: renewal_nudge
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Overdue renewal items: {renewalAtRiskCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Renewal-safe playbook proof:{" "}
                {renewalPitchPresent ? "renewal_pitch active" : "renewal_pitch missing"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Readiness: lifecycle {formatReadinessStateLabel(lifecycleAutomationModule?.currentState)} ·
                playbooks {formatReadinessStateLabel(playbooksModule?.currentState)}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Playbook summary: version {playbookActiveSummary?.version || "n/a"} · approved
                by {playbookActiveSummary?.approvedBy || "n/a"}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Owner-safe action:{" "}
                {renewalAtRiskCount > 0
                  ? "Review overdue renewal items against current owner-approved playbook copy without implying subscription billing automation."
                  : playbooksModule?.nextUpgradeAction ||
                    lifecycleAutomationModule?.nextUpgradeAction ||
                    "Renewal and retention proof is visible through current lifecycle and playbook signals."}
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Abandoned Booking Recovery</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Abandoned booking recovery is shown for operator sessions.
            </p>
          ) : revenueRecoveryLoading ? (
            <p style={{ color: "#7b726a" }}>Loading abandoned booking lane...</p>
          ) : revenueRecoveryError ? (
            <p style={{ color: "#a23e3e" }}>{revenueRecoveryError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Dedicated recovery packet
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px", color: abandonedRecoveryStatusLabel === "Visible" ? "#2c5d43" : "#b26a00" }}>
                {abandonedRecoveryStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Lifecycle rule: {abandonedRecoveryProof?.lifecycleRule || "abandoned_booking_followup"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Dedicated read model: {abandonedRecoveryProof?.dedicatedReadModel ? "ready" : "pending"} · source {abandonedRecoveryProof?.sourceRoute || "/api/internal/metrics/revenue-recovery"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Overdue abandoned-booking items: {abandonedRecoveryOverdueCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Booking exception pressure: awaiting confirmation {abandonedRecoveryAwaitingCount} ·
                needs staff {abandonedRecoveryNeedsStaffCount}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Owner-safe action: {abandonedRecoveryProof?.operatorAction || "Review overdue abandoned-booking follow-up and unresolved confirmation queue items; booking authority remains unchanged."}
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Owner-Tuned Playbooks & Overrides</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Playbook governance is shown for operator sessions.
            </p>
          ) : moduleMatrixLoading || playbookActiveSummaryLoading ? (
            <p style={{ color: "#7b726a" }}>Loading playbook governance lane...</p>
          ) : moduleMatrixError ? (
            <p style={{ color: "#a23e3e" }}>{moduleMatrixError}</p>
          ) : playbookActiveSummaryError ? (
            <p style={{ color: "#a23e3e" }}>{playbookActiveSummaryError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Override governance lane
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: playbookGovernanceStatusColor,
                }}
              >
                {playbookGovernanceStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Playbook module: {formatReadinessStateLabel(playbooksModule?.currentState)}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Owner-approved summary: version {playbookActiveSummary?.version || "n/a"} ·
                approved by {playbookActiveSummary?.approvedBy || "n/a"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Governed override keys ({playbookActiveSummary?.ownerOverrideEligibleCount || 0}):{" "}
                {ownerOverrideEligibleKeys.join(", ") || "None"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Commercial override keys visible: {commercialOverrideKeys.join(", ") || "None"}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Governance action:{" "}
                {playbooksModule?.nextUpgradeAction ||
                  "Keep commercial messaging bounded to approved playbook blocks only."}
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Revenue Scorecard</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Revenue scorecard is shown for operator sessions.
            </p>
          ) : moduleMatrixLoading ||
            playbookActiveSummaryLoading ||
            paymentExceptionsLoading ||
            week1StabilizationLoading ? (
            <p style={{ color: "#7b726a" }}>Loading revenue scorecard...</p>
          ) : moduleMatrixError ? (
            <p style={{ color: "#a23e3e" }}>{moduleMatrixError}</p>
          ) : playbookActiveSummaryError ? (
            <p style={{ color: "#a23e3e" }}>{playbookActiveSummaryError}</p>
          ) : paymentExceptionsError ? (
            <p style={{ color: "#a23e3e" }}>{paymentExceptionsError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Evidence-backed owner view
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px", color: "#2c5d43" }}>
                Visible
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                A27-ready lanes visible: {revenueVisibleLaneCount} · blocked modules:{" "}
                {revenueModuleBlockedCount} · owner pending: {revenueOwnerPendingCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Signals: reminders overdue {reminderAttendanceOverdueCount} · renewals overdue{" "}
                {renewalAtRiskCount} · abandoned booking overdue {abandonedBookingAtRiskCount} ·
                payment exceptions {paymentExceptionTotalCount} · governed override keys{" "}
                {playbookActiveSummary?.ownerOverrideEligibleCount || 0}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Module readiness: lifecycle {formatReadinessStateLabel(lifecycleAutomationModule?.currentState)} ·
                payment follow-up {formatReadinessStateLabel(paymentFollowupModule?.currentState)} ·
                playbooks {formatReadinessStateLabel(playbooksModule?.currentState)}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Reactivation is now tracked through a bounded dormant-customer cohort and separate rule family, but it remains outside this core rollup until owner-approved copy and outcome proof exist.
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Reporting & Operator Exports</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Export packaging is available for internal operator/admin sessions only.
            </p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Evidence-backed export packaging
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px", color: "#2c5d43" }}>
                Visible
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Exportable slices: reminder and attendance, payment recovery, renewal and
                retention, abandoned booking recovery, playbook governance, sales and ad-state
                clarity, scheduling integrity, payment operability proof, and enterprise
                readiness.
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                These exports reuse current dashboard truth only. No new analytics model, PHI,
                transcript, or raw payment data is introduced here.
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Access boundary: internal operator/admin only. Client-viewer stays
                read-only.
              </p>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px" }}>
                <button
                  type="button"
                  onClick={() => handleExportReportingSnapshot("json")}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #2c5d43",
                    background: "#2c5d43",
                    color: "#ffffff",
                    cursor: "pointer",
                  }}
                >
                  Export owner snapshot (JSON)
                </button>
                <button
                  type="button"
                  onClick={() => handleExportReportingSnapshot("md")}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #c7beb4",
                    background: "#ffffff",
                    cursor: "pointer",
                  }}
                >
                  Export operator summary (Markdown)
                </button>
              </div>
              {reportingExportMessage ? (
                <p style={{ margin: "10px 0 0", fontSize: "13px", color: "#5a524b" }}>
                  {reportingExportMessage}
                </p>
              ) : null}
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Sales & Ad-State Clarity</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Sales and ad-state clarity is shown for operator sessions.
            </p>
          ) : salesOfferClarityLoading ? (
            <p style={{ color: "#7b726a" }}>Loading sales and ad-state lane...</p>
          ) : salesOfferClarityError ? (
            <p style={{ color: "#a23e3e" }}>{salesOfferClarityError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Operator-facing sales surface
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: salesAdStateStatusColor,
                }}
              >
                {salesAdStateStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Proven active public offers: {salesActivePublicOfferCount} · public scheduler{" "}
                {salesPublicSchedulerCount} · hydrated type IDs {salesHydratedOfferCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Audience mix: new patient {salesByAudience.newPatient} · renewal{" "}
                {salesByAudience.renewal} · veteran {salesByAudience.veteran} · special{" "}
                {salesByAudience.special}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Payment mix: full {salesByPaymentMode.full} · deposit{" "}
                {salesByPaymentMode.deposit} · dynamic {salesByPaymentMode.dynamic}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Catalog source: {salesCatalogClinicLabel} · owner-confirmed{" "}
                {salesOfferClarity?.manifest?.ownerConfirmedUpdatedAt || "n/a"} · type IDs
                verified {salesOfferClarity?.manifest?.typeIdsVerifiedAt || "n/a"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Ad-state packaging: image artifact{" "}
                {salesAdArtifactPresent
                  ? salesAdArtifactEmpty
                    ? "present but empty"
                    : "checked in"
                  : "missing"}{" "}
                · structured ad-state {salesStructuredAdStateReady ? "ready" : "pending"}
                {salesStructuredAdStateReady
                  ? ` · active/private entries ${salesActivePrivateEntryCount} · private ${salesPrivateEntryCount}`
                  : ""}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                {salesOfferClarity?.adState?.note ||
                  "Offer truth is grounded; structured ad-state packaging is pending."}
              </p>
              <details style={{ marginTop: "10px" }}>
                <summary style={{ cursor: "pointer", color: "#5a524b" }}>
                  Show {salesOfferCards.length} proven public offers
                </summary>
                <ul style={{ margin: "10px 0 0", paddingLeft: "18px", color: "#5a524b" }}>
                  {salesOfferCards.map((offer) => (
                    <li key={offer.key} style={{ marginBottom: "6px" }}>
                      {offer.displayName} ({offer.publicTotalLabel || "n/a"}) ·{" "}
                      {offer.audience.replace(/_/g, " ")} · {offer.paymentMode} ·{" "}
                      {offer.hydrationStatus.replace(/_/g, " ")}
                    </li>
                  ))}
                </ul>
              </details>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Owner-safe action: {salesAdStateOperatorAction}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                Last checked {salesOfferCheckedAtLabel || "just now"}.
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Scheduling Integrity &amp; Acuity Truth</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Scheduling integrity is shown for operator sessions.
            </p>
          ) : salesOfferClarityLoading ||
            externalVisibilityLoading ||
            moduleMatrixLoading ||
            week1StabilizationLoading ? (
            <p style={{ color: "#7b726a" }}>Loading scheduling integrity lane...</p>
          ) : salesOfferClarityError ? (
            <p style={{ color: "#a23e3e" }}>{salesOfferClarityError}</p>
          ) : externalVisibilityError ? (
            <p style={{ color: "#a23e3e" }}>{externalVisibilityError}</p>
          ) : moduleMatrixError ? (
            <p style={{ color: "#a23e3e" }}>{moduleMatrixError}</p>
          ) : week1StabilizationError ? (
            <p style={{ color: "#a23e3e" }}>{week1StabilizationError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Route-by-route scheduling truth
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: schedulingIntegrityStatusColor,
                }}
              >
                {schedulingIntegrityStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Direct-booking proof: public scheduler {salesPublicSchedulerCount} · active
                public offers {salesActivePublicOfferCount} · hydrated type IDs{" "}
                {salesHydratedOfferCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Authority: {schedulingAuthorityLabel} for direct-booking paths only · readiness{" "}
                {formatReadinessStateLabel(schedulingModule?.currentState)}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Current packaged claim:{" "}
                {schedulingModule?.clientVisibleValue ||
                  "Acuity-backed direct-booking truth only; callback timing and confirmation lanes remain operator-bounded."}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Callback/advisory route:{" "}
                {calendarAdvisoryCard?.summary ||
                  "No advisory calendar route is packaged in this session."}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Confirmation recovery route: awaiting confirmation{" "}
                {bookingExceptionAwaitingConfirmationCount} · needs staff{" "}
                {bookingExceptionNeedsStaffCount}. These remain operator follow-up paths, not
                immediate live-slot authority.
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Webhook cutover stays prep-only and blocked. Service-map and hydration evidence
                alone do not activate live routing.
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Bounded action: {schedulingIntegrityAction}
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Payment Operability Proof</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Payment operability proof is shown for operator sessions.
            </p>
          ) : paymentExceptionsLoading ||
            paymentFollowupsLoading ||
            paymentCaptureOutcomesLoading ||
            moduleMatrixLoading ? (
            <p style={{ color: "#7b726a" }}>Loading payment operability proof...</p>
          ) : paymentExceptionsError ? (
            <p style={{ color: "#a23e3e" }}>{paymentExceptionsError}</p>
          ) : paymentFollowupsError ? (
            <p style={{ color: "#a23e3e" }}>{paymentFollowupsError}</p>
          ) : paymentCaptureOutcomesError ? (
            <p style={{ color: "#a23e3e" }}>{paymentCaptureOutcomesError}</p>
          ) : moduleMatrixError ? (
            <p style={{ color: "#a23e3e" }}>{moduleMatrixError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Safe internal proof lane
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: paymentOperabilityStatusColor,
                }}
              >
                {paymentOperabilityStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Follow-up proof: observed {paymentFollowupObservedCount} · link available{" "}
                {paymentFollowupLinkAvailableCount} · notify ok {paymentFollowupNotifyOkCount} ·
                SMS delivered {paymentFollowupSmsDeliveredCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Follow-up providers observed: {paymentFollowupProvidersLabel} · module readiness{" "}
                {formatReadinessStateLabel(paymentFollowupModule?.currentState)}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Capture proof: outcomes {paymentCaptureOutcomeCount} · succeeded{" "}
                {paymentCaptureSuccessCount} · failed {paymentCaptureFailureCount} · fallback{" "}
                {paymentCaptureFallbackCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Capture providers observed: {paymentCaptureProvidersLabel} · hosted-link fallback{" "}
                {paymentCaptureHostedLinkFallbackCount} · staff-required fallback{" "}
                {paymentCaptureStaffFallbackCount} · module readiness{" "}
                {formatReadinessStateLabel(voicePaymentCaptureModule?.currentState)}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Safe boundary: raw processor detail stays external. This lane proves safe
                follow-up and capture outcomes only; it is not a processor-ledger view.
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Bounded action: {paymentOperabilityAction}
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Reactivation Foundation</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Reactivation foundation is shown for operator sessions.
            </p>
          ) : revenueRecoveryLoading ? (
            <p style={{ color: "#7b726a" }}>Loading reactivation lane...</p>
          ) : revenueRecoveryError ? (
            <p style={{ color: "#a23e3e" }}>{revenueRecoveryError}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Bounded dormant-customer cohort
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "22px", color: reactivationStatusLabel === "Visible" ? "#2c5d43" : "#b26a00" }}>
                {reactivationStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Rule family: {reactivationProof?.cohortDefinition?.ruleFamily || "reactivation_reengagement"} · channel {reactivationProof?.cohortDefinition?.channel || "sms"}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Cohort definition: dormant {reactivationProof?.cohortDefinition?.dormantDays || 90} days · recent activity window {reactivationProof?.cohortDefinition?.recentActivityDays || 30} days
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Eligible dormant contacts: {reactivationEligibleCount} · sample phones {reactivationSamplePhonesLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Exclusions applied: open queue {reactivationExcludedOpenQueueCount} · recent activity {reactivationExcludedRecentActivityCount} · renewal {reactivationExcludedRenewalCount} · unpaid {reactivationExcludedUnpaidCount} · abandoned booking {reactivationExcludedAbandonedCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Proof readiness: dormant cohort {reactivationProof?.proof?.dormantCustomerCohortReady ? "ready" : "pending"} · separate rule family {reactivationProof?.proof?.separateRuleFamilyReady ? "ready" : "pending"} · owner-approved copy {reactivationProof?.proof?.ownerApprovedCopyReady ? "ready" : "pending"} · outcome proof pending
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Bounded action: {reactivationProof?.operatorAction || "Use the bounded dormant-customer cohort as owner-safe reactivation proof only. Keep reactivation separate from reminder, renewal, unpaid, and abandoned-booking lanes until owner-approved copy and outcome evidence exist."}
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Admin & Approval Governance</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Role-bounded governance lane
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: governanceStatusColor,
                }}
              >
                {governanceStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Current access role: {governanceRoleLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Owner approvals pending: {governanceOwnerPendingCount} · admin-only controls
                available: {governanceAdminOnlyControlCount} · operator controls available:{" "}
                {governanceOperatorControlCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Publish and approval posture remains internal-only. Metrics client-viewer access
                never grants operator actions, Studio approval, or live deployment authority.
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Enterprise Readiness Scorecard</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                Evidence-backed enterprise view
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "22px",
                  color: enterpriseReadinessStatusColor,
                }}
              >
                {enterpriseReadinessStatusLabel}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Platform seams: read-only ready {platformReadyCount} · scaffold-only{" "}
                {platformScaffoldOnlyCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Revenue and governance: blocked modules {revenueModuleBlockedCount} · owner
                pending {governanceOwnerPendingCount} · payment staff-required{" "}
                {paymentStaffRequiredCount}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Voice posture: {enterpriseVoicePosture}. Preview-only voice quality proof remains
                in Studio Preview and is not treated as live deployment authority here.
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a524b" }}>
                Compliance gate: record-aware enterprise prep remains scaffold-only and
                compliance-gated. No PHI expansion or unsupported enterprise claim is implied by
                this scorecard.
              </p>
            </article>
          )}
        </section>

        <section style={{ marginTop: "18px" }}>
          <SectionTitle>Playbook Preview</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>
              Playbook preview is available for operator sessions only.
            </p>
          ) : !hasOperatorToken ? (
            <p style={{ color: "#7b726a" }}>
              Save an operator token to run preview simulations.
            </p>
          ) : (
            <article
              style={{
                padding: "14px 16px",
                borderRadius: "14px",
                background: "#f9f4ee",
                border: "1px solid #e4dbd0",
              }}
            >
              <form onSubmit={handleRunPlaybookPreview}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "10px",
                  }}
                >
                  <label style={{ fontSize: "12px", color: "#5a524b" }}>
                    Channel
                    <select
                      aria-label="Playbook preview channel"
                      value={playbookPreviewChannel}
                      onChange={(event) =>
                        setPlaybookPreviewChannel(
                          event.target.value as "chat" | "voice" | "outbound_voice"
                        )
                      }
                      style={{
                        width: "100%",
                        marginTop: "4px",
                        borderRadius: "8px",
                        border: "1px solid #d4cabc",
                        padding: "8px 10px",
                        background: "#ffffff",
                      }}
                    >
                      <option value="chat">chat</option>
                      <option value="voice">voice</option>
                      <option value="outbound_voice">outbound_voice</option>
                    </select>
                  </label>
                  <label style={{ fontSize: "12px", color: "#5a524b" }}>
                    Topic hint (optional)
                    <input
                      aria-label="Playbook preview topic hint"
                      value={playbookPreviewTopicHint}
                      onChange={(event) => setPlaybookPreviewTopicHint(event.target.value)}
                      placeholder="hours"
                      style={{
                        width: "100%",
                        marginTop: "4px",
                        borderRadius: "8px",
                        border: "1px solid #d4cabc",
                        padding: "8px 10px",
                      }}
                    />
                  </label>
                  <label style={{ fontSize: "12px", color: "#5a524b" }}>
                    Intent hint (optional)
                    <input
                      aria-label="Playbook preview intent hint"
                      value={playbookPreviewIntentHint}
                      onChange={(event) => setPlaybookPreviewIntentHint(event.target.value)}
                      placeholder="operational_faq"
                      style={{
                        width: "100%",
                        marginTop: "4px",
                        borderRadius: "8px",
                        border: "1px solid #d4cabc",
                        padding: "8px 10px",
                      }}
                    />
                  </label>
                </div>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#5a524b",
                    display: "block",
                    marginTop: "10px",
                  }}
                >
                  Message
                  <textarea
                    aria-label="Playbook preview message"
                    value={playbookPreviewMessage}
                    onChange={(event) => setPlaybookPreviewMessage(event.target.value)}
                    placeholder="Type one safe preview message..."
                    rows={3}
                    style={{
                      width: "100%",
                      marginTop: "4px",
                      borderRadius: "8px",
                      border: "1px solid #d4cabc",
                      padding: "8px 10px",
                      resize: "vertical",
                    }}
                  />
                </label>
                <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
                  <button
                    type="submit"
                    disabled={playbookPreviewSubmitting || !canUsePlaybookPreview}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid #2c5d43",
                      background: "#2c5d43",
                      color: "#ffffff",
                      cursor:
                        playbookPreviewSubmitting || !canUsePlaybookPreview
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        playbookPreviewSubmitting || !canUsePlaybookPreview
                          ? 0.6
                          : 1,
                    }}
                  >
                    {playbookPreviewSubmitting ? "Running..." : "Run preview"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPlaybookPreviewMessage("");
                      setPlaybookPreviewTopicHint("");
                      setPlaybookPreviewIntentHint("");
                      setPlaybookPreviewResult(null);
                      setPlaybookPreviewError(null);
                    }}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid #c7beb4",
                      background: "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                </div>
              </form>
              {playbookPreviewError && (
                <p style={{ margin: "10px 0 0", color: "#a23e3e", fontSize: "13px" }}>
                  {playbookPreviewError}
                </p>
              )}
              {playbookPreviewResult && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "10px",
                    borderRadius: "8px",
                    border: "1px solid #e4dbd0",
                    background: "#ffffff",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Result
                  </p>
                  <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a524b" }}>
                    Matched: {playbookPreviewResult.matched ? "yes" : "no"}
                    {playbookPreviewResult.matched && playbookPreviewResult.key
                      ? ` · key: ${playbookPreviewResult.key}`
                      : ""}
                    {playbookPreviewResult.matched && playbookPreviewResult.intent
                      ? ` · intent: ${playbookPreviewResult.intent}`
                      : ""}
                  </p>
                  {playbookPreviewResult.matched && (
                    <>
                      <p
                        style={{
                          margin: "6px 0 0",
                          fontSize: "13px",
                          color: "#5a524b",
                        }}
                      >
                        Source: {playbookPreviewResult.source || "n/a"} · Prohibited:{" "}
                        {playbookPreviewResult.prohibited ? "yes" : "no"} · Escalate:{" "}
                        {playbookPreviewResult.escalateToStaff ? "yes" : "no"}
                        {playbookPreviewResult.version
                          ? ` · version: ${playbookPreviewResult.version}`
                          : ""}
                      </p>
                      {playbookPreviewResult.reply && (
                        <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#3f3a34" }}>
                          {playbookPreviewResult.reply}
                        </p>
                      )}
                      {playbookPreviewVoiceLines.length > 0 && (
                        <ul style={{ margin: "8px 0 0", paddingLeft: "18px" }}>
                          {playbookPreviewVoiceLines.slice(0, 3).map((line) => (
                            <li key={line} style={{ fontSize: "12px", color: "#5a524b" }}>
                              {line}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </article>
          )}
        </section>

        <section id="alert-health" style={{ marginTop: "28px" }}>
          <SectionTitle>Alert Health</SectionTitle>
          {!alertOverview ? (
            <p style={{ color: "#7b726a" }}>Alert overview unavailable.</p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                gap: "12px",
              }}
            >
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Active Alerts
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                  {alertOverview.totalActive}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Acked {acknowledgedActive} · Unacked{" "}
                  {alertOverview.unacknowledgedActive}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Sev: {alertOverview.activeBySeverity.info} info ·{" "}
                  {alertOverview.activeBySeverity.warning} warn ·{" "}
                  {alertOverview.activeBySeverity.critical} critical
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Resolved (24h)
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                  {alertOverview.resolvedWindow}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Today {alertOverview.resolvedToday}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Resolution Source
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                  {alertOverview.resolutionCounts.automation +
                    alertOverview.resolutionCounts.operator}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Automation {alertOverview.resolutionCounts.automation} · Operator{" "}
                  {alertOverview.resolutionCounts.operator}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  SLA Health
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                  {alertOverview.slaCounts.breached + alertOverview.slaCounts.within}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Breached {alertOverview.slaCounts.breached} · Within{" "}
                  {alertOverview.slaCounts.within}
                </p>
              </article>
            </div>
          )}
        </section>

        <details
          onToggle={(event) => {
            setAdvancedOpen((event.currentTarget as HTMLDetailsElement).open);
          }}
          style={{
            marginTop: "28px",
            padding: "12px",
            borderRadius: "10px",
            background: "#f9f4ee",
            border: "1px solid #e4dbd0",
          }}
        >
          <summary
            style={{
              cursor: "pointer",
              fontSize: "15px",
              fontWeight: 600,
              color: "#4f473f",
            }}
          >
            Advanced
          </summary>
          {!embedMode && (
            <section style={{ marginTop: "14px" }}>
              <SectionTitle>Embed this widget</SectionTitle>
              <code
                style={{
                  display: "block",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: "12px",
                  lineHeight: 1.5,
                  background: "#ffffff",
                  border: "1px solid #d9d2c7",
                  borderRadius: "8px",
                  padding: "10px",
                }}
              >
                {embedSnippet}
              </code>
            </section>
          )}

          {canManageClinicSettings && (
            <section style={{ marginTop: "20px" }}>
              <SectionTitle>Clinic Settings</SectionTitle>
              <p style={{ margin: "0 0 8px", color: "#5a524b", fontSize: "13px" }}>
                Update the clinic details shown on the public profile page.
              </p>
              {clinicProfileTenantKey && (
                <p style={{ margin: "0 0 10px", color: "#6a5f55", fontSize: "12px" }}>
                  Tenant: <strong>{clinicProfileTenantKey}</strong>
                </p>
              )}
              {clinicProfileLoading ? (
                <p style={{ color: "#7b726a" }}>Loading clinic settings…</p>
              ) : (
                <form
                  onSubmit={(event) => {
                    void handleSaveClinicProfile(event);
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "10px",
                    }}
                  >
                    {CLINIC_PROFILE_FIELDS.map((field) => (
                      <label
                        key={field.key}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          fontSize: "12px",
                          color: "#6a5f55",
                        }}
                      >
                        <span>{field.label}</span>
                        <input
                          type="text"
                          value={clinicProfileForm[field.key]}
                          onChange={(event) =>
                            handleClinicProfileFieldChange(field.key, event.target.value)
                          }
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #d4cabc",
                            padding: "7px 9px",
                            background: "#ffffff",
                            color: "#1b1b1b",
                            fontSize: "13px",
                          }}
                        />
                      </label>
                    ))}
                  </div>
                  {clinicProfileError && (
                    <p style={{ margin: "10px 0 0", color: "#a23e3e", fontSize: "13px" }}>
                      {clinicProfileError}
                    </p>
                  )}
                  {clinicProfileMessage && (
                    <p style={{ margin: "10px 0 0", color: "#2c5d43", fontSize: "13px" }}>
                      {clinicProfileMessage}
                    </p>
                  )}
                  <div
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="submit"
                      disabled={clinicProfileSaving}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid #2c5d43",
                        background: "#2c5d43",
                        color: "#ffffff",
                        cursor: clinicProfileSaving ? "not-allowed" : "pointer",
                        opacity: clinicProfileSaving ? 0.7 : 1,
                      }}
                    >
                      {clinicProfileSaving ? "Saving..." : "Save clinic settings"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void loadClinicProfile()}
                      disabled={clinicProfileSaving}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid #c7beb4",
                        background: "#ffffff",
                        color: "#4f473f",
                        cursor: clinicProfileSaving ? "not-allowed" : "pointer",
                        opacity: clinicProfileSaving ? 0.7 : 1,
                      }}
                    >
                      Reload
                    </button>
                  </div>
                </form>
              )}
            </section>
          )}

          <section style={{ marginTop: "20px" }}>
          <SectionTitle>Escalation Ops Health</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : escalationsLoading ? (
            <p style={{ color: "#7b726a" }}>Loading escalation health…</p>
          ) : escalationsError ? (
            <p style={{ color: "#a23e3e" }}>{escalationsError}</p>
          ) : !escalationsStatus?.breaker ? (
            <p style={{ color: "#7b726a" }}>Escalation status unavailable.</p>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "12px",
                }}
              >
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Breaker State
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                    {fallbackString(escalationsStatus.breaker.state, "unknown")}
                  </p>
                  {typeof escalationsStatus.breaker.remainingOpenMs === "number" && (
                    <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                      remainingOpenMs: {escalationsStatus.breaker.remainingOpenMs}
                    </p>
                  )}
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Backoff State
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                    {fallbackString(escalationsStatus.backoff?.state, "NONE")}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    remainingMs: {escalationsStatus.backoff?.remainingMs ?? "—"}
                  </p>
                  {escalationsStatus.backoff?.reason ? (
                    <p
                      style={{ margin: "2px 0 0", fontSize: "12px", color: "#7b726a" }}
                      title={escalationsStatus.backoff.reason}
                    >
                      reason: {truncateText(escalationsStatus.backoff.reason, 48)}
                    </p>
                  ) : null}
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Delivery Counters
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    attempted: {escalationsStatus.counters?.attempted ?? 0}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    delivered: {escalationsStatus.counters?.delivered ?? 0}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    failed: {escalationsStatus.counters?.failed ?? 0}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    skippedBreaker: {escalationsStatus.counters?.skippedBreaker ?? 0}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    skippedBackoff: {escalationsStatus.counters?.skippedBackoff ?? 0}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    skippedNonePending:{" "}
                    {escalationsStatus.counters?.skippedNonePending ?? 0}
                  </p>
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Config
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    Retry: baseMs:{" "}
                    {escalationsStatus.config?.retry?.baseMs ?? "—"} / maxMs:{" "}
                    {escalationsStatus.config?.retry?.maxMs ?? "—"} / jitterMs:{" "}
                    {escalationsStatus.config?.retry?.jitterMs ?? "—"}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    Breaker: failureThreshold:{" "}
                    {escalationsStatus.config?.breaker?.failureThreshold ?? "—"} /
                    openMs: {escalationsStatus.config?.breaker?.openMs ?? "—"}
                  </p>
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Backlog
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    Pending backlog: {escalationsStatus.backlog?.pendingCount ?? 0}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    Failed backlog: {escalationsStatus.backlog?.failedCount ?? 0}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    Oldest pending:{" "}
                    {typeof escalationsStatus.backlog?.oldestPendingAgeSeconds ===
                    "number"
                      ? `${escalationsStatus.backlog.oldestPendingAgeSeconds}s`
                      : "—"}
                    {escalationsStatus.backlog?.oldestPendingCorrelationId ? (
                      <span
                        title={escalationsStatus.backlog.oldestPendingCorrelationId}
                      >
                        {" "}
                        ·{" "}
                        {truncateText(
                          escalationsStatus.backlog.oldestPendingCorrelationId,
                          24
                        )}
                      </span>
                    ) : null}
                  </p>
                </article>
                {(canResetEscalationBreaker || canResetEscalationBackoff) && (
                  <article
                    style={{
                      padding: "14px 16px",
                      borderRadius: "14px",
                      background: "#f9f4ee",
                      border: "1px solid #e4dbd0",
                    }}
                  >
                    <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                      Admin Actions
                    </p>
                    {canResetEscalationBreaker ? (
                      <button
                        type="button"
                        onClick={() => void handleResetEscalationBreaker()}
                        disabled={escalationsResetting}
                        style={{
                          marginTop: "8px",
                          padding: "8px 10px",
                          borderRadius: "8px",
                          border: "1px solid #2c5d43",
                          background: "#2c5d43",
                          color: "#ffffff",
                          cursor: escalationsResetting ? "not-allowed" : "pointer",
                        }}
                      >
                        {escalationsResetting ? "Resetting..." : "Reset breaker"}
                      </button>
                    ) : null}
                    {canResetEscalationBackoff ? (
                      <button
                        type="button"
                        onClick={() => void handleResetEscalationBackoff()}
                        disabled={escalationsBackoffResetting}
                        style={{
                          marginTop: "8px",
                          marginLeft: canResetEscalationBreaker ? "8px" : "0",
                          padding: "8px 10px",
                          borderRadius: "8px",
                          border: "1px solid #2c5d43",
                          background: "#2c5d43",
                          color: "#ffffff",
                          cursor: escalationsBackoffResetting
                            ? "not-allowed"
                            : "pointer",
                        }}
                      >
                        {escalationsBackoffResetting
                          ? "Resetting..."
                          : "Reset backoff"}
                      </button>
                    ) : null}
                  </article>
                )}
              </div>

              <div
                style={{
                  marginTop: "14px",
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <h3 style={{ fontSize: "16px", margin: "0 0 10px" }}>
                  Eligibility Probe
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                  Eligible now:{" "}
                  {`${escalationEligibilityProbe?.eligibility?.pendingEligibleNow ?? 0} / ${
                    escalationEligibilityProbe?.eligibility?.pendingTotal ?? 0
                  }`}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                  In backoff:{" "}
                  {escalationEligibilityProbe?.eligibility?.pendingInBackoff ?? 0}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                  Next eligible in:{" "}
                  {typeof escalationEligibilityProbe?.eligibility?.nextEligibleInMs ===
                  "number"
                    ? `${escalationEligibilityProbe.eligibility.nextEligibleInMs}ms`
                    : "—"}
                </p>
              </div>

              <div style={{ marginTop: "14px" }}>
                <h3 style={{ fontSize: "16px", margin: "0 0 10px" }}>
                  Recent Deliveries
                </h3>
                {escalationDeliveriesLoading ? (
                  <p style={{ color: "#7b726a" }}>Loading recent deliveries…</p>
                ) : escalationDeliveriesError ? (
                  <p style={{ color: "#a23e3e" }}>{escalationDeliveriesError}</p>
                ) : escalationDeliveries.length === 0 ? (
                  <p style={{ color: "#7b726a" }}>No recent delivery attempts.</p>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                          <th style={tableHeaderStyle}>Activity</th>
                          <th style={tableHeaderStyle}>Status</th>
                          <th style={tableHeaderStyle}>CorrelationId</th>
                          <th style={tableHeaderStyle}>EventName</th>
                          <th style={tableHeaderStyle}>LastError</th>
                          <th style={tableHeaderStyle}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {escalationDeliveries.map((delivery) => (
                          <tr key={delivery.deliveryId}>
                            <td style={tableCellStyle}>
                              {formatTimestamp(delivery.activityAt, "Unknown")}
                            </td>
                            <td style={tableCellStyle}>
                              {`${delivery.status} (${delivery.attemptCount ?? 0})`}
                            </td>
                            <td
                              style={tableCellStyle}
                              title={delivery.correlationId || undefined}
                            >
                              {delivery.correlationId
                                ? truncateText(delivery.correlationId, 24)
                                : "—"}
                            </td>
                            <td style={tableCellStyle}>{delivery.eventName || "—"}</td>
                            <td
                              style={tableCellStyle}
                              title={delivery.lastError || undefined}
                            >
                              {delivery.lastError
                                ? truncateText(delivery.lastError, 60)
                                : "—"}
                            </td>
                            <td style={tableCellStyle}>
                              {delivery.correlationId ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    void handleOpenEscalationCorrelation(
                                      delivery.correlationId
                                    )
                                  }
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "8px",
                                    border: "1px solid #c7beb4",
                                    background: "#ffffff",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                  }}
                                >
                                  Details
                                </button>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {canViewInternalMetrics && !isClientViewerMode && (
          <section style={{ marginTop: "28px" }}>
            <SectionTitle>Outbound Scheduling (SMS)</SectionTitle>
            {canManageSchedulingExceptions && (
              <div style={{ marginBottom: "10px" }}>
                {outboundVoiceSweepHealthLoading ? (
                  <span style={{ fontSize: "12px", color: "#7b726a" }}>
                    Sweep health: loading…
                  </span>
                ) : outboundVoiceSweepHealthError ? (
                  <span style={{ fontSize: "12px", color: "#a23e3e" }}>
                    Sweep health: {outboundVoiceSweepHealthError}
                  </span>
                ) : (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "999px",
                      border: "1px solid #d9d2c7",
                      background:
                        outboundVoiceSweepHealthStatus === "error"
                          ? "#fde9e6"
                          : outboundVoiceSweepHealthStatus === "warn"
                          ? "#fff3d9"
                          : "#e7f4ea",
                      color:
                        outboundVoiceSweepHealthStatus === "error"
                          ? "#8f2c2c"
                          : outboundVoiceSweepHealthStatus === "warn"
                          ? "#8a5a00"
                          : "#2e5f3e",
                    }}
                  >
                    {`Sweep health: ${outboundVoiceSweepHealthStatus.toUpperCase()} · ${outboundVoiceSweepHealthReason}`}
                  </span>
                )}
              </div>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              <label htmlFor="outbound-scheduling-window" style={{ fontSize: "13px" }}>
                Snapshot
              </label>
              <select
                id="outbound-scheduling-window"
                aria-label="Outbound scheduling window"
                value={String(outboundSchedulingSnapshotDays)}
                onChange={(event) => {
                  const nextValue = Number(event.target.value);
                  if (nextValue === 7 || nextValue === 14 || nextValue === 30) {
                    setOutboundSchedulingSnapshotDays(nextValue);
                  }
                }}
                style={{
                  borderRadius: "8px",
                  border: "1px solid #d4cabc",
                  padding: "6px 8px",
                  background: "#ffffff",
                }}
              >
                {outboundSchedulingSnapshotOptions.map((days) => (
                  <option key={days} value={days}>
                    {days} days
                  </option>
                ))}
              </select>
            </div>
            {outboundSchedulingLoading ? (
              <p style={{ color: "#7b726a" }}>Loading outbound scheduling metrics…</p>
            ) : outboundSchedulingError ? (
              <p style={{ color: "#a23e3e" }}>{outboundSchedulingError}</p>
            ) : (
              <>
                <div
                  style={{
                    margin: "0 0 12px",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    border: "1px solid #e4dbd0",
                    background: "#f9f4ee",
                  }}
                >
                  {outboundVoiceSweepRunsLoading ? (
                    <p style={{ margin: 0, color: "#7b726a", fontSize: "13px" }}>
                      Loading outbound voice sweep runs…
                    </p>
                  ) : outboundVoiceSweepRunsError ? (
                    <p style={{ margin: 0, color: "#a23e3e", fontSize: "13px" }}>
                      {outboundVoiceSweepRunsError}
                    </p>
                  ) : (
                    <p style={{ margin: 0, fontSize: "13px", color: "#4f473f" }}>
                      {`Outbound voice sweep: last run at ${
                        latestOutboundVoiceSweepRun?.createdAt
                          ? formatTimestamp(latestOutboundVoiceSweepRun.createdAt, "Unknown")
                          : "Never"
                      } · attempted ${
                        safeNumber(latestOutboundVoiceSweepRun?.counters?.attempted)
                      } / succeeded ${safeNumber(
                        latestOutboundVoiceSweepRun?.counters?.succeeded
                      )} / failed ${safeNumber(
                        latestOutboundVoiceSweepRun?.counters?.failed
                      )} / skipped ${safeNumber(
                        latestOutboundVoiceSweepRun?.counters?.skipped
                      )} · durationMs ${safeNumber(
                        latestOutboundVoiceSweepRun?.perf?.durationMs
                      )}`}
                    </p>
                  )}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "12px",
                  }}
                >
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Links Sent
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                    {outboundSchedulingLinksSent}
                  </p>
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Bookings Confirmed
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                    {outboundSchedulingBookingsConfirmed}
                  </p>
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Deflection Rate
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                    {outboundSchedulingDeflectionRate}
                  </p>
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Staff Saves (Minutes)
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                    {outboundSchedulingEstimatedMinutes}
                  </p>
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                    gridColumn: "1 / -1",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Time to Resolution
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    avg: {outboundSchedulingAvgResolution}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    p50: {outboundSchedulingP50Resolution}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    p90: {outboundSchedulingP90Resolution}
                  </p>
                </article>
                <article
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                    gridColumn: "1 / -1",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                    Voice booking requests
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    Total: {voiceBookingRequestsTotal}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    After-hours share: {voiceBookingAfterHoursPercent} (
                    {voiceBookingAfterHoursCount})
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#4f473f" }}>
                    Outcomes: pending {voiceBookingOutcomePending} / confirmed{" "}
                    {voiceBookingOutcomeConfirmed} / cancelled{" "}
                    {voiceBookingOutcomeCancelled}
                  </p>
                </article>
                </div>
              </>
            )}
          </section>
        )}

        {canManageSchedulingExceptions && (
          <section style={{ marginTop: "28px" }}>
            <SectionTitle>Scheduling exceptions</SectionTitle>
            <div
              style={{
                margin: "0 0 12px",
                padding: "10px 12px",
                borderRadius: "10px",
                border: "1px solid #e4dbd0",
                background: "#f9f4ee",
              }}
            >
              {schedulingSweepRunsLoading ? (
                <p style={{ margin: 0, color: "#7b726a", fontSize: "13px" }}>
                  Loading sweep runs…
                </p>
              ) : schedulingSweepRunsError ? (
                <p style={{ margin: 0, color: "#a23e3e", fontSize: "13px" }}>
                  {schedulingSweepRunsError}
                </p>
              ) : (
                <>
                  <p style={{ margin: "0 0 6px", fontSize: "13px", color: "#4f473f" }}>
                    {`Resend bulk sweep: last run at ${
                      latestResendBulkSweepRun?.createdAt
                        ? formatTimestamp(latestResendBulkSweepRun.createdAt, "Unknown")
                        : "Never"
                    } · attempted ${
                      safeNumber(latestResendBulkSweepRun?.counters?.attempted)
                    } / sent ${safeNumber(
                      latestResendBulkSweepRun?.counters?.sent
                    )} / failed ${safeNumber(
                      latestResendBulkSweepRun?.counters?.failed
                    )} / skippedCooldown ${safeNumber(
                      latestResendBulkSweepRun?.counters?.skippedCooldown
                    )}${
                      latestResendBulkSweepRun
                        ? latestResendBulkSweepRun.idempotent
                          ? " · idempotent"
                          : " · non-idempotent"
                        : ""
                    }`}
                  </p>
                  <p style={{ margin: 0, fontSize: "13px", color: "#4f473f" }}>
                    {`Force staff sweep: last run at ${
                      latestForceStaffSweepRun?.createdAt
                        ? formatTimestamp(latestForceStaffSweepRun.createdAt, "Unknown")
                        : "Never"
                    } · attempted ${
                      safeNumber(latestForceStaffSweepRun?.counters?.attempted)
                    } / updated ${safeNumber(
                      latestForceStaffSweepRun?.counters?.updated
                    )} / skipped ${safeNumber(
                      latestForceStaffSweepRun?.counters?.skipped
                    )}${
                      latestForceStaffSweepRun
                        ? latestForceStaffSweepRun.idempotent
                          ? " · idempotent"
                          : " · non-idempotent"
                        : ""
                    }`}
                  </p>
                </>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                alignItems: "center",
                margin: "0 0 12px",
              }}
            >
              <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "12px", color: "#7b726a" }}>Age bucket</span>
                <select
                  value={schedulingExceptionsBucketFilter}
                  onChange={(event) =>
                    setSchedulingExceptionsBucketFilter(
                      event.target.value as SchedulingExceptionsBucketFilter
                    )
                  }
                  style={{
                    borderRadius: "8px",
                    border: "1px solid #d9d2c7",
                    padding: "6px 8px",
                    background: "#fff",
                  }}
                >
                  {SCHEDULING_EXCEPTION_BUCKET_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "12px", color: "#7b726a" }}>
                  Bulk threshold (minutes)
                </span>
                <input
                  type="number"
                  min={0}
                  value={String(schedulingExceptionsMinAgeMinutes)}
                  onChange={(event) => {
                    const next = Number.parseInt(event.target.value || "0", 10);
                    setSchedulingExceptionsMinAgeMinutes(
                      Number.isFinite(next) && next >= 0 ? next : 0
                    );
                  }}
                  style={{
                    width: "120px",
                    borderRadius: "8px",
                    border: "1px solid #d9d2c7",
                    padding: "6px 8px",
                    background: "#fff",
                  }}
                />
              </label>
              <button
                type="button"
                onClick={() => void handleSchedulingExceptionsBulkAction("resend-bulk")}
                disabled={Boolean(schedulingExceptionsBulkAction)}
                style={{
                  marginTop: "18px",
                  padding: "7px 10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d2c7",
                  background: "#fff7f0",
                  cursor: schedulingExceptionsBulkAction ? "not-allowed" : "pointer",
                  fontSize: "12px",
                }}
              >
                {schedulingExceptionsBulkAction === "resend-bulk"
                  ? "Sending..."
                  : "Resend all > X mins"}
              </button>
              <button
                type="button"
                onClick={() =>
                  void handleSchedulingExceptionsBulkAction("force-staff-bulk")
                }
                disabled={Boolean(schedulingExceptionsBulkAction)}
                style={{
                  marginTop: "18px",
                  padding: "7px 10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d2c7",
                  background: "#fff",
                  cursor: schedulingExceptionsBulkAction ? "not-allowed" : "pointer",
                  fontSize: "12px",
                }}
              >
                {schedulingExceptionsBulkAction === "force-staff-bulk"
                  ? "Saving..."
                  : "Force staff for all > X mins"}
              </button>
              <button
                type="button"
                onClick={handleExportSchedulingExceptionsCsv}
                disabled={schedulingExceptionsExporting}
                style={{
                  marginTop: "18px",
                  padding: "7px 10px",
                  borderRadius: "8px",
                  border: "1px solid #d9d2c7",
                  background: "#ffffff",
                  cursor: schedulingExceptionsExporting ? "not-allowed" : "pointer",
                  fontSize: "12px",
                }}
              >
                {schedulingExceptionsExporting ? "Exporting..." : "Export CSV"}
              </button>
            </div>
            {schedulingExceptionsToast && (
              <p
                style={{
                  margin: "0 0 10px",
                  color:
                    schedulingExceptionsToast.type === "success"
                      ? "#2c5d43"
                      : "#a23e3e",
                  fontSize: "13px",
                }}
              >
                {schedulingExceptionsToast.message}
              </p>
            )}
            {schedulingExceptionsLoading ? (
              <p style={{ color: "#7b726a" }}>Loading scheduling exceptions…</p>
            ) : schedulingExceptionsError ? (
              <p style={{ color: "#a23e3e" }}>{schedulingExceptionsError}</p>
            ) : schedulingExceptions.length === 0 ? (
              <p style={{ color: "#7b726a" }}>
                No scheduling exceptions needing manual confirmation.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                      <th style={tableHeaderStyle}>Created</th>
                      <th style={tableHeaderStyle}>Status</th>
                      <th style={tableHeaderStyle}>Age</th>
                      <th style={tableHeaderStyle}>Summary</th>
                      <th style={tableHeaderStyle}>Phone Last4</th>
                      <th style={tableHeaderStyle}>Source</th>
                      <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedulingExceptions.map((item) => {
                      const resendActionKey = `${item.id}:resend-link`;
                      const staffActionKey = `${item.id}:mark-needs-staff`;
                      return (
                        <tr key={item.id}>
                          <td style={tableCellStyle}>
                            {formatTimestamp(item.createdAt, "Unknown")}
                          </td>
                          <td style={tableCellStyle}>
                            {formatCallbackQueueStatus(item.status)}
                          </td>
                          <td style={tableCellStyle}>
                            {typeof item.ageMinutes === "number"
                              ? `${formatSchedulingAgeBucketLabel(item.ageBucket)} (${item.ageMinutes}m)`
                              : "—"}
                          </td>
                          <td style={tableCellStyle}>{item.summary || "—"}</td>
                          <td style={tableCellStyle}>{item.phoneLast4 || "—"}</td>
                          <td style={tableCellStyle}>{item.source || "sms"}</td>
                          <td style={tableCellStyle}>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <button
                                type="button"
                                onClick={() =>
                                  void handleSchedulingExceptionAction(
                                    item.id,
                                    "resend-link"
                                  )
                                }
                                disabled={schedulingExceptionsActionKey === resendActionKey}
                                style={{
                                  padding: "5px 8px",
                                  borderRadius: "8px",
                                  border: "1px solid #d9d2c7",
                                  background: "#fff7f0",
                                  cursor:
                                    schedulingExceptionsActionKey === resendActionKey
                                      ? "not-allowed"
                                      : "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                {schedulingExceptionsActionKey === resendActionKey
                                  ? "Sending..."
                                  : "Resend link"}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  void handleSchedulingExceptionAction(
                                    item.id,
                                    "mark-needs-staff"
                                  )
                                }
                                disabled={schedulingExceptionsActionKey === staffActionKey}
                                style={{
                                  padding: "5px 8px",
                                  borderRadius: "8px",
                                  border: "1px solid #d9d2c7",
                                  background: "#ffffff",
                                  cursor:
                                    schedulingExceptionsActionKey === staffActionKey
                                      ? "not-allowed"
                                      : "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                {schedulingExceptionsActionKey === staffActionKey
                                  ? "Saving..."
                                  : "Staff follow-up"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
        </details>

        <section id="active-alerts-live-feed" style={{ marginTop: "28px" }}>
          <SectionTitle>Active Alerts (Live Feed)</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : (
            <>
              {alertsError && <p style={{ color: "#a23e3e" }}>{alertsError}</p>}
              {alertRows.length === 0 ? (
                <p style={{ color: "#7b726a" }}>No active alerts. System is stable.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                        <th style={tableHeaderStyle}>ID</th>
                        <th style={tableHeaderStyle}>Alert</th>
                        <th style={tableHeaderStyle}>Event</th>
                        <th style={tableHeaderStyle}>Count</th>
                        <th style={tableHeaderStyle}>Threshold</th>
                        <th style={tableHeaderStyle}>Triggered</th>
                        <th style={tableHeaderStyle}>Acknowledged</th>
                        <th style={tableHeaderStyle}>Delivery</th>
                        <th style={tableHeaderStyle}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alertRows.map(alert => (
                        <tr key={alert.id}>
                          <td style={tableCellStyle}>{alert.id}</td>
                          <td style={tableCellStyle}>{alert.alertType}</td>
                          <td style={tableCellStyle}>{alert.eventName}</td>
                          <td style={tableCellStyle}>{alert.count}</td>
                          <td style={tableCellStyle}>{alert.threshold}</td>
                          <td style={tableCellStyle}>
                            {new Date(alert.firstTriggeredAt).toLocaleString()}
                          </td>
                          <td style={tableCellStyle}>
                            {alert.acknowledgedAt
                              ? `Acknowledged by ${alert.acknowledgedBy || "Unknown operator"} at ${new Date(alert.acknowledgedAt).toLocaleString()}`
                              : "Not acknowledged"}
                          </td>
                          <td style={tableCellStyle}>
                            {(() => {
                              const deliveryStatus = String(
                                alert.delivery?.status || "none"
                              ).toLowerCase();
                              if (deliveryStatus === "none") return "—";
                              const attemptCount = alert.delivery?.attemptCount;
                              const deliveryLabel =
                                typeof attemptCount === "number"
                                  ? `${deliveryStatus} (${attemptCount})`
                                  : deliveryStatus;
                              const deliveryTitle =
                                (deliveryStatus === "failed" ||
                                  deliveryStatus === "canceled") &&
                                alert.delivery?.lastError
                                  ? alert.delivery.lastError
                                  : undefined;
                              const retryActionKey = `${alert.id}:delivery_retry`;
                              const canRetryDelivery =
                                deliveryStatus === "failed" && !alert.acknowledgedAt;
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <span title={deliveryTitle}>{deliveryLabel}</span>
                                  {canRetryDelivery && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        void handleAlertDeliveryRetry(alert.id)
                                      }
                                      disabled={alertActionKey === retryActionKey}
                                      style={{
                                        fontSize: "11px",
                                        borderRadius: "8px",
                                        border: "1px solid #d9d2c7",
                                        background: "#fff7f0",
                                        padding: "3px 8px",
                                        cursor:
                                          alertActionKey === retryActionKey
                                            ? "not-allowed"
                                            : "pointer",
                                      }}
                                    >
                                      {alertActionKey === retryActionKey
                                        ? "Retrying..."
                                        : "Retry"}
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td style={tableCellStyle}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: "999px",
                                  fontSize: "12px",
                                  background: "#f0ebe4",
                                }}
                              >
                                {getAlertStatus(
                                  alert.alertType,
                                  alert.eventName,
                                  alert.acknowledgedAt
                                )}
                              </span>
                              {getResolvedLabel(
                                alert.alertType,
                                alert.eventName,
                                alert.acknowledgedAt
                              ) && (
                                <span style={{ fontSize: "12px", color: "#7b726a" }}>
                                  {getResolvedLabel(
                                    alert.alertType,
                                    alert.eventName,
                                    alert.acknowledgedAt
                                  )}
                                </span>
                              )}
                              <span style={{ fontSize: "12px", color: "#7b726a" }}>
                                {alert.externalIncident?.referenceId ? (
                                  alert.externalIncident.url ? (
                                    <a
                                      href={alert.externalIncident.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ color: "#5a524b" }}
                                    >
                                      External: {alert.externalIncident.referenceId}
                                    </a>
                                  ) : (
                                    `External: ${alert.externalIncident.referenceId}`
                                  )
                                ) : (
                                  "No external incident link"
                                )}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        <section id="callback-requests" style={{ marginTop: "28px" }}>
          <SectionTitle>Callback Requests</SectionTitle>
          {!isClientViewerMode ? (
            <p style={callbackQueueHintStyle}>
              Use Confirmed only for manual confirmation cases that were not booked directly.
            </p>
          ) : null}
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : callbackRequestQueueLoading ? (
            <p style={{ color: "#7b726a" }}>Loading callback requests queue…</p>
          ) : callbackRequestRows.length === 0 ? (
            <p style={{ color: "#7b726a" }}>No callback requests in queue.</p>
          ) : (
            <>
              {callbackRequestActionMessage ? (
                <p style={{ color: "#2c5d43", marginBottom: "10px" }}>
                  {callbackRequestActionMessage}
                </p>
              ) : null}
              {callbackRequestActionError ? (
                <p style={{ color: "#a23e3e", marginBottom: "10px" }}>
                  {callbackRequestActionError}
                </p>
              ) : null}
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "max-content",
                    minWidth: "100%",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                      <th style={callbackQueueHeaderStyle}>Time</th>
                      <th style={callbackQueueHeaderStyle}>Name</th>
                      <th style={callbackQueueHeaderStyle}>Phone</th>
                      <th style={callbackQueueHeaderStyle}>Best Time</th>
                      <th style={callbackQueueHeaderStyle}>Status</th>
                      {!isClientViewerMode && <th style={callbackQueueHeaderStyle}>Actions</th>}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>Source</th>
                      )}
                      {!isClientViewerMode && <th style={callbackQueueHeaderStyle}>Type</th>}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>AI Outcome</th>
                      )}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>Staff Follow-up</th>
                      )}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>Summary</th>
                      )}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>Last Attempt</th>
                      )}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>Attempts</th>
                      )}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>Call SID</th>
                      )}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>Handled At</th>
                      )}
                      {!isClientViewerMode && (
                        <th style={callbackQueueHeaderStyle}>Handled By</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {callbackRequestRows.map((item, idx) => {
                    const rowId = item.id || "";
                    const rowStatus = item.status || "pending";
                    const normalizedRowStatus = normalizeCallbackQueueStatus(rowStatus);
                    const statusActions = getQueueStatusActions(
                      rowStatus,
                      item.requestType || null
                    );
                    const showMarkCompleted = normalizedRowStatus === "needs_staff";
                    const showCallNow =
                      normalizedRowStatus === "pending" ||
                      normalizedRowStatus === "needs_staff";
                    const callNowMessage = rowId ? callbackCallNowFeedback[rowId] : "";
                    const callNowActionKey = `${rowId}:call_now`;
                    const markCompletedActionKey = `${rowId}:mark_completed`;
                    const callNowPending = callbackRequestActionKey === callNowActionKey;
                    const markCompletedPending =
                      callbackRequestActionKey === markCompletedActionKey;

                    return (
                      <tr key={`${item.id || "item"}-${item.createdAt}-${idx}`}>
                        <td style={tableCellStyle}>
                          {formatTimestamp(item.createdAt, "Unknown time")}
                        </td>
                        <td style={tableCellStyle}>{fallbackString(item.name, "—")}</td>
                        <td style={tableCellStyle}>
                          {formatPhoneLast4(item.phone || null)}
                        </td>
                        <td style={tableCellStyle}>
                          {fallbackString(item.bestTime, "—")}
                        </td>
                        <td style={tableCellStyle}>
                          {formatCallbackQueueStatus(rowStatus)}
                        </td>
                        {!isClientViewerMode && (
                          <td style={{ ...tableCellStyle, minWidth: "240px" }}>
                            {(!showMarkCompleted &&
                              !showCallNow &&
                              statusActions.length === 0) ||
                            !rowId ? (
                              "—"
                            ) : (
                              <div style={callbackActionGroupStyle}>
                                {showCallNow && (
                                  <button
                                    type="button"
                                    onClick={() => void handleCallNow(rowId)}
                                    disabled={callNowPending}
                                    style={{
                                      ...callbackCallNowButtonStyle,
                                      cursor: callNowPending ? "not-allowed" : "pointer",
                                      opacity: callNowPending ? 0.65 : 1,
                                    }}
                                  >
                                    Call Now
                                  </button>
                                )}
                                {showMarkCompleted && (
                                  <button
                                    type="button"
                                    onClick={() => void handleMarkCallbackCompleted(rowId)}
                                    disabled={markCompletedPending}
                                    style={{
                                      ...callbackCompleteButtonStyle,
                                      cursor: markCompletedPending ? "not-allowed" : "pointer",
                                      opacity: markCompletedPending ? 0.65 : 1,
                                    }}
                                  >
                                    Mark Completed
                                  </button>
                                )}
                                {statusActions.map((action) => {
                                  const actionKey = `${rowId}:${action.key}`;
                                  const actionPending = callbackRequestActionKey === actionKey;
                                  return (
                                    <button
                                      key={action.key}
                                      type="button"
                                      onClick={() =>
                                        void applyCallbackRequestAction(rowId, action.status, {
                                          actionKey,
                                          successMessage: action.successMessage,
                                        })
                                      }
                                      disabled={actionPending}
                                      style={{
                                        ...callbackActionButtonStyle,
                                        cursor: actionPending ? "not-allowed" : "pointer",
                                        opacity: actionPending ? 0.65 : 1,
                                      }}
                                    >
                                      {action.label}
                                    </button>
                                  );
                                })}
                                {callNowMessage ? (
                                  <span style={callbackActionFeedbackStyle}>
                                    {callNowMessage}
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {fallbackString(item.source, "—")}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {fallbackString(item.requestType, "—")}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {fallbackString(item.aiOutcome, item.aiHandled ? "handled" : "—")}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {item.staffFollowupRequired ? "required" : "—"}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>{fallbackString(item.summary, "—")}</td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {formatTimestamp(item.lastAttemptAt, "—")}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {typeof item.outboundAttemptCount === "number"
                              ? String(item.outboundAttemptCount)
                              : "0"}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {fallbackString(item.outboundLastCallSid, "—")}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {formatTimestamp(item.handledAt, "—")}
                          </td>
                        )}
                        {!isClientViewerMode && (
                          <td style={tableCellStyle}>
                            {fallbackString(item.handledBy, "—")}
                          </td>
                        )}
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <section id="voice-ops" style={{ marginTop: "28px" }}>
          <SectionTitle>Voice Ops</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : voiceOpsLoading ? (
            <p style={{ color: "#7b726a" }}>Loading voice operations…</p>
          ) : voiceOpsError ? (
            <p style={{ color: "#a23e3e" }}>{voiceOpsError}</p>
          ) : voiceOpsRows.length === 0 ? (
            <p style={{ color: "#7b726a" }}>No recent voice calls.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                    <th style={tableHeaderStyle}>Started</th>
                    <th style={tableHeaderStyle}>Last Event</th>
                    <th style={tableHeaderStyle}>Status</th>
                    <th style={tableHeaderStyle}>Milestones</th>
                    <th style={tableHeaderStyle}>CallSid</th>
                  </tr>
                </thead>
                <tbody>
                  {voiceOpsRows.map((row, idx) => (
                    <tr
                      key={`${row.correlationId || "voice"}-${idx}`}
                      onClick={() => void handleOpenVoiceTimeline(row.callSid)}
                      style={{ cursor: row.callSid ? "pointer" : "default" }}
                    >
                      <td style={tableCellStyle}>
                        {formatTimestamp(row.startedAt, "Unknown")}
                      </td>
                      <td style={tableCellStyle}>
                        {formatTimestamp(row.lastEventAt, "Unknown")}
                      </td>
                      <td style={tableCellStyle}>{formatVoiceStatus(row.status)}</td>
                      <td style={tableCellStyle}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {VOICE_MILESTONE_LABELS.filter(
                            (milestone) => row.milestones?.[milestone.key]
                          ).map((milestone) => (
                            <span
                              key={milestone.key}
                              style={{
                                fontSize: "11px",
                                borderRadius: "999px",
                                border: "1px solid #c7beb4",
                                padding: "2px 8px",
                                background: "#ffffff",
                              }}
                            >
                              {milestone.label}
                            </span>
                          ))}
                          {VOICE_MILESTONE_LABELS.every(
                            (milestone) => !row.milestones?.[milestone.key]
                          ) && "—"}
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <code>{row.callSid || "unknown"}</code>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleCopyCallSid(row.callSid);
                            }}
                            style={{
                              padding: "4px 8px",
                              borderRadius: "8px",
                              border: "1px solid #c7beb4",
                              background: "#ffffff",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section id="callback-follow-ups" style={{ marginTop: "28px" }}>
          <SectionTitle>Callback Follow-ups</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                {[
                  { key: "all", label: "All" },
                  { key: "pending", label: "Pending" },
                  { key: "ai", label: "AI Handled" },
                  { key: "staff", label: "Needs Staff" },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setFilterMode(tab.key as FilterMode)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "999px",
                      border: "1px solid #c7beb4",
                      background: filterMode === tab.key ? "#1b1b1b" : "#ffffff",
                      color: filterMode === tab.key ? "#ffffff" : "#5a524b",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Search by name or phone"
                value={query}
                onChange={e => setQuery(e.target.value)}
                style={{
                  marginBottom: "14px",
                  width: "100%",
                  maxWidth: "320px",
                  borderRadius: "10px",
                  border: "1px solid #d9d2c7",
                  padding: "8px 12px",
                }}
              />
              {callbacksError && (
                <p style={{ margin: "0 0 10px", color: "#7b726a", fontSize: "12px" }}>
                  {callbacksError}
                </p>
              )}
              {callbacksError && callbacksNonJsonResponse && (
                <p style={{ margin: "0 0 10px", color: "#7b726a", fontSize: "12px" }}>
                  API routing issue: /api/callbacks returned HTML (expected JSON).
                </p>
              )}
              {callbacksLoading ? (
                <p style={{ color: "#7b726a" }}>Loading callbacks…</p>
              ) : filteredCallbacks.length === 0 ? (
                <p style={{ color: "#7b726a" }}>No callback requests yet.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                        <th style={tableHeaderStyle}>Name</th>
                        <th style={tableHeaderStyle}>Phone</th>
                        <th style={tableHeaderStyle}>Status</th>
                        <th style={tableHeaderStyle}>AI</th>
                        <th style={tableHeaderStyle}>Staff</th>
                        <th style={tableHeaderStyle}>AI Outcome</th>
                        <th style={tableHeaderStyle}>Summary</th>
                        <th style={tableHeaderStyle}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCallbacks.map(cb => (
                        <tr
                          key={cb.id}
                          style={{
                            background: cb.id === recentlyUpdatedId ? "#fff7d6" : "transparent",
                          }}
                        >
                          <td style={tableCellStyle}>{cb.name}</td>
                          <td style={tableCellStyle}>{cb.phone}</td>
                          <td style={tableCellStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span>{cb.status}</span>
                            </div>
                          </td>
                          <td style={tableCellStyle}>
                            {cb.aiHandled ? "AI" : "Not handled"}
                          </td>
                          <td style={tableCellStyle}>
                            {cb.staffFollowupRequired ? "Needs staff" : "None"}
                          </td>
                          <td style={tableCellStyle}>
                            {cb.aiOutcome ?? (cb.aiHandled ? "Handled" : "—")}
                          </td>
                          <td style={tableCellStyle}>{cb.summary ?? "No summary"}</td>
                          <td style={tableCellStyle}>
                            {new Date(cb.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>Alert Engine Snapshot</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : (
            <>
              {metricsLoading && <p style={{ color: "#7b726a" }}>Loading metrics...</p>}
              {metricsError && <p style={{ color: "#a23e3e" }}>{metricsError}</p>}
              {!metricsLoading && !metricsError && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                    <th style={tableHeaderStyle}>Alert</th>
                    <th style={tableHeaderStyle}>Event</th>
                    <th style={tableHeaderStyle}>Count</th>
                    <th style={tableHeaderStyle}>Threshold</th>
                    <th style={tableHeaderStyle}>Window</th>
                    <th style={tableHeaderStyle}>Triggered</th>
                    <th style={tableHeaderStyle}>Last ACKed</th>
                    <th style={tableHeaderStyle}>Status</th>
                    <th style={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {metricsActiveAlerts.length === 0 && (
                    <tr>
                      <td
                        style={{ padding: "14px 10px", color: "#7b726a" }}
                        colSpan={9}
                      >
                        No active alerts at this time.
                      </td>
                    </tr>
                  )}
                  {metricsActiveAlerts.map((alert) => {
                    const matchedAlert = alertRows.find(
                      (row) =>
                        row.alertType === alert.alertType &&
                        row.eventName === alert.eventName
                    );
                    const alertActionId = alert.id || matchedAlert?.id;
                    const actionBaseKey = alertActionId || `${alert.alertType}:${alert.eventName}`;
                    const resolveNote = alertActionId
                      ? alertResolveNotes[alertActionId] || ""
                      : "";

                    return (
                    <tr key={`${alert.alertType}-${alert.eventName}`}>
                      <td style={tableCellStyle}>{alert.alertType}</td>
                      <td style={tableCellStyle}>{alert.eventName}</td>
                      <td style={tableCellStyle}>{alert.count}</td>
                      <td style={tableCellStyle}>{alert.threshold}</td>
                      <td style={tableCellStyle}>{alert.windowSeconds}s</td>
                      <td style={tableCellStyle}>
                        {new Date(alert.triggeredAt).toLocaleString()}
                      </td>
                      <td style={tableCellStyle}>
                        {alert.acknowledgedAt
                          ? `Acknowledged by ${alert.acknowledgedBy || "Unknown operator"} at ${new Date(alert.acknowledgedAt).toLocaleString()}`
                          : "Not acknowledged"}
                      </td>
                        <td style={tableCellStyle}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: "999px",
                                fontSize: "12px",
                                background: "#f0ebe4",
                              }}
                            >
                              {getAlertStatus(
                                alert.alertType,
                                alert.eventName,
                                alert.acknowledgedAt
                              )}
                            </span>
                            {getResolvedLabel(
                              alert.alertType,
                              alert.eventName,
                              alert.acknowledgedAt
                            ) && (
                              <span style={{ fontSize: "12px", color: "#7b726a" }}>
                                {getResolvedLabel(
                                  alert.alertType,
                                  alert.eventName,
                                  alert.acknowledgedAt
                                )}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={tableCellStyle}>
                          {!alertActionId ? (
                            "—"
                          ) : alert.acknowledgedAt ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <input
                                type="text"
                                aria-label={`Resolve note ${alert.alertType} ${alert.eventName}`}
                                placeholder="Optional note"
                                value={resolveNote}
                                onChange={(event) => {
                                  setAlertResolveNotes((prev) => ({
                                    ...prev,
                                    [alertActionId]: event.target.value,
                                  }));
                                }}
                                style={{
                                  borderRadius: "8px",
                                  border: "1px solid #d4cabc",
                                  padding: "6px 8px",
                                  minWidth: "180px",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  void handleAlertAction({
                                    alertId: alertActionId,
                                    action: "resolve",
                                  })
                                }
                                disabled={alertActionKey === `${actionBaseKey}:resolve`}
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: "8px",
                                  border: "1px solid #2c5d43",
                                  background: "#2c5d43",
                                  color: "#ffffff",
                                  cursor: "pointer",
                                }}
                              >
                                Resolve
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                void handleAlertAction({
                                  alertId: alertActionId,
                                  action: "acknowledge",
                                })
                              }
                              disabled={alertActionKey === `${actionBaseKey}:acknowledge`}
                              style={{
                                padding: "6px 10px",
                                borderRadius: "8px",
                                border: "1px solid #c7beb4",
                                background: "#ffffff",
                                cursor: "pointer",
                              }}
                            >
                              Acknowledge
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              )}
            </>
          )}
        </section>

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>System Status</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : !status ? (
            <p style={{ color: "#7b726a" }}>Status unavailable.</p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Alert Engine
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "18px" }}>
                  {status.alertEngineInitialized ? "Initialized" : "Disabled"}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Active Alerts
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "18px" }}>
                  {status.activeAlertCount}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Event Forwarder
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "18px" }}>
                  {status.eventForwarderEnabled ? "Enabled" : "Disabled"}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Voice Activation
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "18px" }}>
                  {typeof status.voiceEnabled === "boolean"
                    ? status.voiceEnabled
                      ? "Enabled"
                      : "Disabled"
                    : "Unknown"}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Source: {status.voiceActivationSource ?? "unknown"}
                </p>
              </article>
            </div>
          )}
        </section>

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>Recent Alert Activity</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : alertEvents.length === 0 ? (
            <p style={{ color: "#7b726a" }}>No recent alert activity.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                    <th style={tableHeaderStyle}>Event</th>
                    <th style={tableHeaderStyle}>Timestamp</th>
                    <th style={tableHeaderStyle}>Environment</th>
                    <th style={tableHeaderStyle}>Correlation</th>
                  </tr>
                </thead>
                <tbody>
                  {alertEvents.map((event, idx) => (
                    <tr key={`${event.type}-${event.createdAt}-${idx}`}>
                      <td style={tableCellStyle}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <span>{event.type}</span>
                          {event.type === "alert_resolved" && (
                            <span style={{ fontSize: "12px", color: "#7b726a" }}>
                              Resolved at {new Date(event.createdAt).toLocaleString()} —{" "}
                              {acknowledgedAlertKeys.has(
                                `${event.payload?.alertType || ""}:${event.payload?.eventName || ""}`
                              )
                                ? "Resolved after acknowledgment"
                                : "Auto-resolved by system"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        {new Date(event.createdAt).toLocaleString()}
                      </td>
                      <td style={tableCellStyle}>
                        {event.payload?.environment || "local"}
                      </td>
                      <td style={tableCellStyle}>
                        {event.payload?.correlationId || "Not available"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>Voice Escalations (Recent)</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : voiceEscalations.length === 0 ? (
            <p style={{ color: "#7b726a" }}>No recent voice escalations.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                    <th style={tableHeaderStyle}>Time</th>
                    <th style={tableHeaderStyle}>Intent</th>
                    <th style={tableHeaderStyle}>Reason</th>
                    <th style={tableHeaderStyle}>Call SID</th>
                    <th style={tableHeaderStyle}>Correlation</th>
                    <th style={tableHeaderStyle}>Environment</th>
                  </tr>
                </thead>
                <tbody>
                  {voiceEscalations.map((event, idx) => (
                    <tr key={`${event.type}-${event.createdAt}-${idx}`}>
                      <td style={tableCellStyle}>
                        {formatTimestamp(event.createdAt, "Unknown time")}
                      </td>
                      <td style={tableCellStyle}>
                        {event.payload?.intent || "unknown"}
                      </td>
                      <td style={tableCellStyle}>
                        {event.payload?.reason || "unspecified"}
                      </td>
                      <td style={tableCellStyle}>
                        {event.payload?.callSid || "—"}
                      </td>
                      <td style={tableCellStyle}>
                        {event.payload?.correlationId || "—"}
                      </td>
                      <td style={tableCellStyle}>
                        {event.payload?.environment || "local"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>Alert Acknowledgement Timeline</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : ackTimeline.length === 0 ? (
            <p style={{ color: "#7b726a" }}>No alert acknowledgements yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                    <th style={tableHeaderStyle}>Alert</th>
                    <th style={tableHeaderStyle}>Operator</th>
                    <th style={tableHeaderStyle}>Acknowledged At</th>
                  </tr>
                </thead>
                <tbody>
                  {ackTimeline.map((entry, idx) => (
                    <tr key={`${entry.eventName}-${entry.createdAt}-${idx}`}>
                      <td style={tableCellStyle}>
                        {entry.payload?.alertType || "Alert"}
                      </td>
                      <td style={tableCellStyle}>
                        {entry.payload?.operatorName
                          ? `${entry.payload.operatorName}${
                              entry.payload.role ? ` (${entry.payload.role})` : ""
                            }`
                          : "Operator unknown"}
                      </td>
                      <td style={tableCellStyle}>
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>Operator + AI Audit Timeline</SectionTitle>
          {!canViewInternalMetrics ? (
            <p style={{ color: "#7b726a" }}>{INTERNAL_METRICS_MESSAGE}</p>
          ) : isClientViewerMode ? (
            <p style={{ color: "#7b726a" }}>{CLIENT_VIEWER_MESSAGE}</p>
          ) : (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "12px" }}>
                <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", color: "#7b726a" }}>
                  Source
                  <select
                    value={auditSourceFilter}
                    onChange={(event) => setAuditSourceFilter(event.target.value as "all" | "operator" | "ai")}
                    style={{
                      padding: "6px 8px",
                      borderRadius: "8px",
                      border: "1px solid #d4cabc",
                      background: "#ffffff",
                      fontSize: "12px",
                    }}
                  >
                    <option value="all">All sources</option>
                    <option value="operator">Operator</option>
                    <option value="ai">AI</option>
                  </select>
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", color: "#7b726a" }}>
                  Action
                  <select
                    value={auditActionFilter}
                    onChange={(event) => setAuditActionFilter(event.target.value)}
                    style={{
                      padding: "6px 8px",
                      borderRadius: "8px",
                      border: "1px solid #d4cabc",
                      background: "#ffffff",
                      fontSize: "12px",
                    }}
                  >
                    <option value="all">All actions</option>
                    {auditActions.map((action) => (
                      <option key={action} value={action}>
                        {action}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", color: "#7b726a" }}>
                  Time window
                  <select
                    value={auditWindow}
                    onChange={(event) => setAuditWindow(event.target.value as "24h" | "7d" | "all")}
                    style={{
                      padding: "6px 8px",
                      borderRadius: "8px",
                      border: "1px solid #d4cabc",
                      background: "#ffffff",
                      fontSize: "12px",
                    }}
                  >
                    <option value="24h">Last 24h</option>
                    <option value="7d">Last 7d</option>
                    <option value="all">All time</option>
                  </select>
                </label>
              </div>
              {operatorAuditLoading && (
                <p style={{ color: "#7b726a" }}>Loading audit activity…</p>
              )}
              {!operatorAuditLoading && operatorAuditError && (
                <p style={{ color: "#a23e3e" }}>{operatorAuditError}</p>
              )}
              {!operatorAuditLoading && !operatorAuditError && filteredAudit.length === 0 && (
                <p style={{ color: "#7b726a" }}>No audit activity in this window.</p>
              )}
              {!operatorAuditLoading && !operatorAuditError && filteredAudit.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                        <th style={tableHeaderStyle}>Source</th>
                        <th style={tableHeaderStyle}>Action</th>
                        <th style={tableHeaderStyle}>Actor</th>
                        <th style={tableHeaderStyle}>Role</th>
                        <th style={tableHeaderStyle}>Time</th>
                        <th style={tableHeaderStyle}>Entity</th>
                        <th style={tableHeaderStyle}>Environment</th>
                        <th style={tableHeaderStyle}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAudit.map((entry, idx) => (
                        <tr key={`${entry.entityId}-${entry.createdAt}-${idx}`}>
                          <td style={tableCellStyle}>
                            <span
                              style={{
                                fontSize: "11px",
                                padding: "2px 8px",
                                borderRadius: "999px",
                                background: entry.source === "ai" ? "#e8f0ff" : "#e6efe9",
                                color: entry.source === "ai" ? "#2242a6" : "#1f3d2a",
                                textTransform: "uppercase",
                              }}
                            >
                              {entry.source}
                            </span>
                          </td>
                          <td style={tableCellStyle}>{entry.action || "Unknown action"}</td>
                          <td style={tableCellStyle}>{entry.actor || "Unknown"}</td>
                          <td style={tableCellStyle}>{entry.role || "unknown"}</td>
                          <td style={tableCellStyle}>
                            {formatTimestamp(entry.createdAt, "Unknown time")}
                          </td>
                          <td style={tableCellStyle}>
                            <span
                              style={{
                                fontSize: "11px",
                                padding: "2px 6px",
                                borderRadius: "999px",
                                background: "#f2ede6",
                                color: "#6a5f55",
                                marginRight: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              {entry.entityType || "unknown"}
                            </span>
                            {entry.entityId || "Unknown"}
                          </td>
                          <td style={tableCellStyle}>{entry.environment || "unknown"}</td>
                          <td style={tableCellStyle}>{entry.detail || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>SLA Metrics</SectionTitle>
          {!sla ? (
            <p style={{ color: "#7b726a" }}>SLA data unavailable.</p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "12px",
              }}
            >
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Callback SLA Avg
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                  {sla.callbacks.averageSeconds}s
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Breaches (&gt;10m): {sla.callbacks.breachCount}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Alert Ack SLA Avg
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                  {sla.alerts.acknowledge.averageSeconds}s
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Breaches (&gt;10m): {sla.alerts.acknowledge.breachCount}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Callback Buckets
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "12px" }}>
                  ≤2m {sla.callbacks.buckets.le2m} · ≤5m {sla.callbacks.buckets.le5m}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px" }}>
                  ≤10m {sla.callbacks.buckets.le10m} · &gt;10m {sla.callbacks.buckets.gt10m}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Alert Ack Buckets
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "12px" }}>
                  ≤2m {sla.alerts.acknowledge.buckets.le2m} · ≤5m{" "}
                  {sla.alerts.acknowledge.buckets.le5m}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px" }}>
                  ≤10m {sla.alerts.acknowledge.buckets.le10m} · &gt;10m{" "}
                  {sla.alerts.acknowledge.buckets.gt10m}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Alert Resolve SLA Avg
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                  {sla.alerts.resolve.averageSeconds}s
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  Breaches (&gt;10m): {sla.alerts.resolve.breachCount}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Alert Resolve Buckets
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "12px" }}>
                  ≤2m {sla.alerts.resolve.buckets.le2m} · ≤5m{" "}
                  {sla.alerts.resolve.buckets.le5m}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px" }}>
                  ≤10m {sla.alerts.resolve.buckets.le10m} · &gt;10m{" "}
                  {sla.alerts.resolve.buckets.gt10m}
                </p>
              </article>
              <article
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: "#f9f4ee",
                  border: "1px solid #e4dbd0",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                  Fallback Usage
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "22px" }}>
                  {sla.alerts.fallbackUsage.withFallback}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                  of {sla.alerts.fallbackUsage.total} alerts
                </p>
              </article>
            </div>
          )}
        </section>

        <section style={{ marginTop: "28px" }}>
          <SectionTitle>Event Counts</SectionTitle>
          {!metricsLoading && !metricsError && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              {eventEntries.length === 0 && (
                <p style={{ color: "#7b726a", margin: 0 }}>
                  No events recorded yet.
                </p>
              )}
              {eventEntries.map(([eventName, count]) => (
                <article
                  key={eventName}
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "#f9f4ee",
                    border: "1px solid #e4dbd0",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#7b726a",
                    }}
                  >
                    {eventName}
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "22px" }}>{count}</p>
                </article>
              ))}
            </div>
          )}
        </section>

        {voiceTimelineCallSid && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Voice Timeline ${voiceTimelineCallSid}`}
            onClick={handleCloseVoiceTimeline}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(17, 17, 17, 0.45)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              zIndex: 1000,
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "min(980px, 100%)",
                maxHeight: "85vh",
                overflowY: "auto",
                background: "#ffffff",
                borderRadius: "14px",
                border: "1px solid #d9d2c7",
                padding: "16px",
                boxShadow: "0 20px 48px rgba(0, 0, 0, 0.24)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "10px",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "18px" }}>
                  Voice Timeline: {voiceTimelineCallSid}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseVoiceTimeline}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: "1px solid #c7beb4",
                    background: "#ffffff",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
              {voiceTimelineLoading ? (
                <p style={{ color: "#7b726a" }}>Loading voice timeline…</p>
              ) : voiceTimelineError ? (
                <p style={{ color: "#a23e3e" }}>{voiceTimelineError}</p>
              ) : voiceTimelineEvents.length === 0 ? (
                <p style={{ color: "#7b726a" }}>No timeline events for this call.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                        <th style={tableHeaderStyle}>Time</th>
                        <th style={tableHeaderStyle}>Event</th>
                        <th style={tableHeaderStyle}>Severity</th>
                        <th style={tableHeaderStyle}>Summary</th>
                        <th style={tableHeaderStyle}>Meta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {voiceTimelineEvents.map((event, idx) => (
                        <tr key={`${event.at || "event"}-${event.eventName || idx}-${idx}`}>
                          <td style={tableCellStyle}>
                            {formatTimestamp(event.at, "Unknown")}
                          </td>
                          <td style={tableCellStyle}>
                            {fallbackString(event.eventName, "unknown_event")}
                          </td>
                          <td style={tableCellStyle}>
                            {fallbackString(event.severity, "info")}
                          </td>
                          <td style={tableCellStyle}>
                            {truncateText(
                              fallbackString(event.summary, "No summary available."),
                              160
                            )}
                          </td>
                          <td style={tableCellStyle}>
                            <details>
                              <summary style={{ cursor: "pointer" }}>View meta</summary>
                              <pre
                                style={{
                                  marginTop: "8px",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  fontSize: "11px",
                                  lineHeight: 1.45,
                                  background: "#faf7f2",
                                  border: "1px solid #e4dbd0",
                                  borderRadius: "8px",
                                  padding: "8px",
                                }}
                              >
                                {JSON.stringify(event.meta || {}, null, 2)}
                              </pre>
                            </details>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {escalationCorrelationId && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Escalation Correlation ${escalationCorrelationId}`}
            onClick={handleCloseEscalationCorrelation}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(17, 17, 17, 0.45)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              zIndex: 1000,
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "min(980px, 100%)",
                maxHeight: "85vh",
                overflowY: "auto",
                background: "#ffffff",
                borderRadius: "14px",
                border: "1px solid #d9d2c7",
                padding: "16px",
                boxShadow: "0 20px 48px rgba(0, 0, 0, 0.24)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "10px",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "18px" }}>
                  Escalation Correlation: {escalationCorrelationId}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseEscalationCorrelation}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: "1px solid #c7beb4",
                    background: "#ffffff",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
              {escalationCorrelationLoading ? (
                <p style={{ color: "#7b726a" }}>Loading correlation details…</p>
              ) : escalationCorrelationError ? (
                <p style={{ color: "#a23e3e" }}>{escalationCorrelationError}</p>
              ) : (
                <>
                  <div
                    style={{
                      marginBottom: "12px",
                      padding: "12px",
                      borderRadius: "12px",
                      background: "#f9f4ee",
                      border: "1px solid #e4dbd0",
                    }}
                  >
                    <h4 style={{ margin: "0 0 8px", fontSize: "14px" }}>
                      External Incident Linkback
                    </h4>
                    {escalationCorrelationExternalIncident?.referenceId ? (
                      <>
                        <p style={{ margin: 0, fontSize: "12px", color: "#5a524b" }}>
                          Reference: {escalationCorrelationExternalIncident.referenceId}
                        </p>
                        <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                          Source: {escalationCorrelationExternalIncident.sourceSystem || "Unknown"}
                        </p>
                        <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                          Attached:{" "}
                          {formatTimestamp(
                            escalationCorrelationExternalIncident.attachedAt,
                            "Unknown"
                          )}
                          {escalationCorrelationExternalIncident.attachedBy
                            ? ` by ${escalationCorrelationExternalIncident.attachedBy}`
                            : ""}
                        </p>
                        <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#7b726a" }}>
                          Provenance:{" "}
                          {fallbackString(
                            escalationCorrelationExternalIncident.provenance,
                            "operational_event_payload"
                          )}
                        </p>
                        {escalationCorrelationExternalIncident.url ? (
                          <p style={{ margin: "6px 0 0", fontSize: "12px" }}>
                            <a
                              href={escalationCorrelationExternalIncident.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: "#5a524b" }}
                            >
                              Open external incident
                            </a>
                          </p>
                        ) : null}
                      </>
                    ) : (
                      <p style={{ margin: 0, fontSize: "12px", color: "#7b726a" }}>
                        No external incident linked. Internal alert evidence remains the
                        source of truth.
                      </p>
                    )}
                  </div>

                  {escalationCorrelationDeliveries.length === 0 ? (
                    <p style={{ color: "#7b726a" }}>
                      No escalation deliveries for this correlation.
                    </p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                            <th style={tableHeaderStyle}>Activity</th>
                            <th style={tableHeaderStyle}>Status</th>
                            <th style={tableHeaderStyle}>EventName</th>
                            <th style={tableHeaderStyle}>LastError</th>
                            {canRetryEscalationDeliveries && (
                              <th style={tableHeaderStyle}>Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {escalationCorrelationDeliveries.map((delivery, idx) => (
                            <tr
                              key={`${delivery.deliveryId || "delivery"}-${delivery.activityAt || idx}`}
                            >
                              <td style={tableCellStyle}>
                                {formatTimestamp(delivery.activityAt, "Unknown")}
                              </td>
                              <td style={tableCellStyle}>
                                {`${fallbackString(
                                  delivery.status,
                                  "unknown"
                                )} (${delivery.attemptCount ?? 0})`}
                              </td>
                              <td style={tableCellStyle}>
                                {fallbackString(delivery.event?.eventName, "—")}
                              </td>
                              <td
                                style={tableCellStyle}
                                title={delivery.lastError || undefined}
                              >
                                {delivery.lastError
                                  ? truncateText(delivery.lastError, 80)
                                  : "—"}
                              </td>
                              {canRetryEscalationDeliveries && (
                                <td style={tableCellStyle}>
                                  {delivery.status === "failed" && delivery.deliveryId ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        void handleRetryEscalationDelivery(
                                          delivery.deliveryId
                                        )
                                      }
                                      disabled={
                                        escalationCorrelationRetryingId ===
                                        delivery.deliveryId
                                      }
                                      style={{
                                        padding: "4px 8px",
                                        borderRadius: "8px",
                                        border: "1px solid #c7beb4",
                                        background: "#ffffff",
                                        cursor:
                                          escalationCorrelationRetryingId ===
                                          delivery.deliveryId
                                            ? "not-allowed"
                                            : "pointer",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {escalationCorrelationRetryingId === delivery.deliveryId
                                        ? "Retrying…"
                                        : "Retry"}
                                    </button>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
              {escalationCorrelationEvents.length > 0 && (
                <div style={{ marginTop: "12px", overflowX: "auto" }}>
                  <h4 style={{ margin: "0 0 8px", fontSize: "14px" }}>Events</h4>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", background: "#f0ebe4" }}>
                        <th style={tableHeaderStyle}>Time</th>
                        <th style={tableHeaderStyle}>Event</th>
                        <th style={tableHeaderStyle}>Environment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {escalationCorrelationEvents.map((event, idx) => (
                        <tr key={`${event.createdAt || "event"}-${event.eventName || idx}`}>
                          <td style={tableCellStyle}>
                            {formatTimestamp(event.createdAt, "Unknown")}
                          </td>
                          <td style={tableCellStyle}>
                            {fallbackString(event.eventName, "unknown_event")}
                          </td>
                          <td style={tableCellStyle}>
                            {fallbackString(event.environment, "unknown")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
